import { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as api from '../lib/api';
import type { Song, Cue, CueState, StageObject, StageObjectCategory, MaterialProps, LedProps } from '../lib/api';
import { DEFAULT_HIDDEN_LABEL, defaultHiddenToast, defaultHiddenToggleText, defaultVisible, isDefaultHidden, visibilityNote } from '../lib/defaultHidden';
import { useAuth } from '../lib/auth';
import { useMemberWatermark } from '../lib/watermark/useMemberWatermark';
import { memberWatermarkOn } from '../lib/watermark/policy';
import StageScene from '../components/StageScene';
import MechanismTab from '../components/mechanisms/MechanismTab';
import SongTimingBar from '../components/adjust/SongTimingBar';
import { endTimePreview } from '../lib/mechanisms/timePreview';
import { useMediaQuery } from '../lib/useMediaQuery';
import { useCueOps } from '../components/adjust/useCueOps';
import { OpsUndoStack } from '../lib/adjust/undoStack';
import { songLengthInfo } from '../lib/mechanisms/songLength';
import { startLabel } from '../lib/mechanisms/songStart';
// 音樂是這首歌的時間（沒影片、有音樂）時，播放頭跟著音樂走（其他情況跟 MechanismTimeline 一樣）
import MechanismTimeline from '../components/mechanisms/MechanismTimelineClocked';
import MechanismObjectHint, { MechanismHintProvider, MechanismLockNote, useMechanismMounts } from '../components/mechanisms/MechanismObjectHint';
import { mountedNames, stageLockMessage } from '../lib/stageLock';
import { useEditorMechanisms } from '../lib/mechanisms/useMechanisms';
import { useEditorTimelineEdit } from '../lib/mechanisms/useEditorTimelineEdit';
import { setCuePositionsBulk, withMechanismSnapshot } from '../lib/mechanisms/mechanismApi';
import { applyTimingItems, syncDraftTimings } from '../lib/mechanisms/libTimings';
import type { LibTimingSave, LibTimingStep } from '../lib/mechanisms/useMechanisms';
import TimingScopeMenu, { type TimingScope } from '../components/TimingScopeMenu';
import { valuesOf } from '../lib/mechanisms/track';
import UploadModelDialog from '../components/UploadModelDialog';
import ModelVersionsDialog from '../components/ModelVersionsDialog';
import AssetPickerDialog from '../components/AssetPickerDialog';
import ActivityDrawer from '../components/ActivityDrawer';
import DriveSettingsDialog from '../components/DriveSettingsDialog';
import CueStoryboard from '../components/CueStoryboard';
import SaveIndicator from '../components/SaveIndicator';
import CueDiffDialog from '../components/CueDiffDialog';
import ImportCuesDialog from '../components/ImportCuesDialog';
import ShareLinksDialog from '../components/ShareLinksDialog';
import ProjectMembersDialog from '../components/ProjectMembersDialog';
import CommentSearchDialog from '../components/CommentSearchDialog';
import NotificationBell from '../components/NotificationBell';
import OverflowMenu, { type OverflowItem } from '../components/OverflowMenu';
import { useViewAsMenu, VIEW_AS_MENU_LABEL } from '../components/ViewAsMenu';
import DecisionDialog from '../components/DecisionDialog';
import { decisionNotifyHint, newVersionLabel } from '../lib/reviewHistory';
import GateStageTag from '../components/GateStageTag';
import { newerVersionTitle, reviewVideoIdFor, statusBlockedReason } from '../lib/editorGate';
import { pushRecentProject } from '../components/CommandPalette';
import { useProjectViewSettings } from '../lib/projectViewSettings';
import { useStageProps } from '../lib/useStageProps';
import { canEditStageProps } from '../lib/stageProps';
import { useCameraBookmarks } from '../lib/useCameraBookmarks';
import { isStageShowActive } from '../lib/stageShow';
import { canEditCameraBookmarks } from '../lib/cameraBookmarks';
import { usePersonalLook } from '../lib/stageLook/personalLook';
import { cueTransitionSeconds, crossfadeLabel, NEW_CUE_CROSSFADE_SECONDS, parseCrossfadeInput, parseVideoTimeInput } from '../lib/cueTiming';
import { EditHistory, historyScope } from '../lib/editHistory';
import { deleteSongMessage, sharedLibraryCueCount } from '../lib/cueBadge';
import DeletedSongsEntry from '../components/DeletedSongs';
import { deletedSongToast } from '../lib/songTrashApi';
import { RESET_ALL_MENU_LABEL, RESET_ALL_NOTHING, resetAllDialog, resetAllHistoryScopes, runResetAll, summarizeOverrides } from '../lib/cueResetAll';
import { applyCueStatePatch, cueStateRestorePlan, overridePatch, type CueStateField, type CueStatePatch } from '../lib/cueStateUndo';
import { formatFieldNumber, metersToScene, parseNumberInput, sameAsShown, sceneToMeters, stepNumberInput } from '../lib/numberField';
import { toast } from '../lib/toast';
import * as cueLib from '../lib/cueLibraryApi';
import { applyDraftToLook, applyDraftToStates, applyLookToDraft, draftHasOps, suggestLabel, type LookDraft } from '../lib/lookDraft';
import { useLookDraft } from '../lib/useLookDraft';
import LookDraftBar from '../components/LookDraftBar';
import CueLibraryTab from '../components/CueLibraryTab';
import { deleteBlockedText, sortLibraryCues, stepLibraryCue } from '../lib/cueLibraryList';
import SaveAsCueDialog from '../components/SaveAsCueDialog';
import AddSongCueDialog from '../components/AddSongCueDialog';
import SongCueSlots from '../components/SongCueSlots';
import CueDragHandle, { useCueRowDrag } from '../components/CueDragHandle';
import { canDragOnto, dragHintText, dragReorder, fmtSlotTime, ledSwitchFollows, sortedSlotIds, stepTarget } from '../lib/songSlots';
import UpdateCueDialog, { usageSongNames } from '../components/UpdateCueDialog';
import { useLiveRefresh } from '../lib/liveRefresh';
import { isTextEntryTarget, isTypingTarget } from '../lib/keyTarget';
import { useEscapeClose } from '../lib/useEscapeClose';
import { chooseDialog, confirmDialog, promptDialog } from '../components/dialogs';
import { friendlyError, staleStatusMessage } from '../lib/songReview';
import type { ProjectMember, ProjectRole } from '../lib/mockData';
import { SONG_STATUS_LABEL } from '../lib/songReview';
import SongCodeField from '../components/SongCodeField';
import AddVideoDialog from '../components/AddVideoDialog';
import LedTemplatesDialog from '../components/LedTemplatesDialog';
import LedUvPanel from '../components/LedUvPanel';
import { getUvLayoutEdit } from '../lib/ledUvApi';
import { editIsApplied, parseEditSnapshot } from '../lib/ledUv/editState';
import type { UvPreview } from '../lib/ledUv/previewUv';
import { useLedTemplates } from '../lib/useLedTemplates';
import { useLedPatternCanvas, type LedPatternSource } from '../lib/useLedPatternCanvas';
import { setSongTemplate, workScaleTemplatesOf } from '../lib/ledTemplateApi';
import type { WorkScale } from '../lib/workScale';
import { chooseLedTemplate, ledTemplateChip, liveSourceHint, songTemplatePlan } from '../lib/ledLiveTemplate';
import LedTemplateChip, { LedTemplateHint } from '../components/LedTemplateChip';
import LedTimelineDialog, { SongTimelineSummary } from '../components/LedTimelineDialog';
import { cueTimesOf, useSongTimeline } from '../lib/useSongTimeline';
import { parseSwitches, type TimelineSwitch } from '../lib/ledTemplateTimeline';
import SongVideosDialog from '../components/SongVideosDialog';
import { isJobActive, jobStatusText, useVideoJobDone, useVideoJobs, useWarnBeforeUnloadWhileUploading, type VideoJob } from '../lib/videoJobs';
import { songTitle } from '../lib/songCode';
import { songCueCounts } from '../lib/editorSongs';
import Icon from '../components/Icon';
import PhoneEditNote from '../components/PhoneEditNote';
import { STAGE_CATEGORY } from '../components/stageCategories';
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowRightLeft, ArrowUp, Bookmark, BookmarkPlus, Camera, Check, ChevronDown, Clapperboard, Clock, Cloud, Columns2,
  Copy, Eye, EyeOff, Film, Flag, HistoryIcon, Import, LayoutGrid, LayoutPanelLeft, Library, Lightbulb, Link, Lock, LockOpen, MousePointerClick, Music,
  FolderCog, Palette, Pencil, Plus, Redo2, RotateCcw, Ruler, Search, Shapes, Sprout, Trash2, Undo2, Upload, Users, Wrench, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import './ProjectEditor.css';
import { usePanelWidth } from '../lib/usePanelWidth';
import PanelEdge from '../components/PanelEdge';
import { useSongSwitchHold } from '../lib/songSwitchHold';
import { reviewToggle, type ProjectStatusValue } from '../lib/projectStatus';
import SongMusicField from '../components/SongMusicField';
import MusicBatchDialog from '../components/MusicBatchDialog';
import MusicBar from '../components/MusicBar';
import { useSongMusic } from '../lib/useSongMusic';
import { useMusicHotkeys, useMusicPreviewFeed, useMusicTimelineClock } from '../lib/musicClock';
import { useFitLevel } from '../lib/useFitLevel';
import { EditorMusicContext, type EditorMusicValue } from '../lib/editorMusicContext';
import { MUSIC_TEXT, canEditMusic, canSetCueTimeFromMusic, cueTimeChanged, cueTimeFromMusic, formatMusicClock, musicSummaryOf, setNowBlocker, setNowToast } from '../lib/songMusic';

type RightTab = 'cues' | 'state' | 'mechanisms' | 'proposals' | 'objects' | 'library';
type LoadStatus = 'loading' | 'ready' | 'error';
/**
 * 改物件狀態：直接給要改的欄位，或給「從現在的樣子算出要改什麼」
 * （排隊輪到時才算 → 連續改 X、Y 不會互相蓋掉還沒存完的值）
 */
type StatePatchInput = CueStatePatch | ((current: CueState['effective']) => CueStatePatch);
type SongStatus = Song['status'];
type StatusFilter = SongStatus | 'all';

const CATEGORY_INFO = STAGE_CATEGORY;

const STATUS_INFO: Record<SongStatus, { label: string; mod: string }> = {
  todo:          { label: SONG_STATUS_LABEL.todo,          mod: 'todo' },
  in_review:     { label: SONG_STATUS_LABEL.in_review,     mod: 'review' },
  approved:      { label: SONG_STATUS_LABEL.approved,      mod: 'approved' },
  needs_changes: { label: SONG_STATUS_LABEL.needs_changes, mod: 'changes' },
};
/** 審片流程的順序：待製作 → 審查中 → 需修改 → 已通過 */
const STATUS_ORDER: SongStatus[] = ['todo', 'in_review', 'needs_changes', 'approved'];

const EMPTY_DRAFT_MECHS: Record<string, never> = {};

export default function ProjectEditor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Songs
  const [songs, setSongs] = useState<Song[]>([]);
  const [songsLoading, setSongsLoading] = useState(true);
  // 「最近刪除的歌」：剛刪了一首就加 1，入口重新讀一次
  const [trashKey, setTrashKey] = useState(0);
  const [selectedSongId, setSelectedSongId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // 「我改過的」filter — 用 activity log 算出當前用戶有動過的 song id
  const [mineOnly, setMineOnly] = useState(false);
  const [mineSongIds, setMineSongIds] = useState<Set<string>>(new Set());
  // 專案成員（「負責動畫師」選單只列這個專案裡角色是動畫師的人）
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  // 我在這個專案的角色（全站製作一律是製作；專案的製作也是）：決定哪些編輯功能看得到。後台才是真正的權限
  const [myProjectRole, setMyProjectRole] = useState<ProjectRole | null>(null);
  // 有人用邀請連結進來之後要看得到 → 一進來讀一次、每分鐘和切回分頁時再讀、關掉邀請視窗時也讀
  const refreshMembers = useCallback(async () => {
    if (!projectId) return;
    try {
      const { members: next, myRole } = await api.getProjectTeam(projectId);
      setProjectMembers(prev => (sameMembers(prev, next) ? prev : next));
      setMyProjectRole(myRole);
    } catch { /* 讀不到就照舊（只顯示「還沒指派」） */ }
  }, [projectId]);
  useEffect(() => { setProjectMembers([]); setMyProjectRole(null); void refreshMembers(); }, [refreshMembers]);
  useLiveRefresh(refreshMembers, { enabled: !!projectId });
  const animatorMembers = useMemo(() => projectMembers.filter(m => m.role === 'animator'), [projectMembers]);
  // 「用其他身分看」：導演畫面 / 動畫師畫面（每個動畫師一個）/ 客戶畫面（分享連結），開新分頁、只能看
  const viewAsMenu = useViewAsMenu({ projectId: projectId || '', animators: animatorMembers, onCreateShare: () => setShareOpen(true) });
  // 指派的人已經不是這個專案的成員（例如複製專案後，成員不會帶過去）→ 拿全部用戶的名字，選單上才寫得出是誰
  const [userNames, setUserNames] = useState<Map<string, string> | null>(null);
  const needUserNames = useMemo(
    () => songs.some(s => s.animatorUserId && !projectMembers.some(m => m.id === s.animatorUserId)),
    [songs, projectMembers],
  );
  useEffect(() => {
    // 全站的用戶清單只有全站製作讀得到；專案的製作拿不到就寫「不在專案裡的人」
    if (!needUserNames || userNames || user?.role !== 'admin') return;
    let cancelled = false;
    api.listUsers()
      .then(list => { if (!cancelled) setUserNames(new Map(list.map(u => [u.id, u.name]))); })
      .catch(() => { /* 讀不到就寫「已刪除的帳號」 */ });
    return () => { cancelled = true; };
  }, [needUserNames, userNames, user?.role]);

  // Cues
  const [cues, setCues] = useState<Cue[]>([]);
  const [cuesLoading, setCuesLoading] = useState(false);
  // cues 是哪一首歌的（剛換歌、新的還沒讀到時，cues / selectedCueId 還是上一首的）
  const [cuesSongId, setCuesSongId] = useState<string | null>(null);
  const [selectedCueId, setSelectedCueId] = useState<string | null>(null);
  // Cue 庫分頁點的 cue 這首歌沒有用到時：只在 3D 預覽它（不改任何歌）。null = 看這首歌選的 cue
  const [libPreviewId, setLibPreviewId] = useState<string | null>(null);
  const sceneCueId = libPreviewId ? 'lib:' + libPreviewId : selectedCueId;

  // Stage objects (project-level)
  const [stageObjects, setStageObjectsState] = useState<StageObject[]>([]);
  // 第一次讀到之前是 loading：不要先跳「這個專案還沒有物件」
  const [stageObjectsStatus, setStageObjectsStatus] = useState<LoadStatus>('loading');
  // ref 跟 state 一起更新：排隊中的修改要拿最新的值，不能等畫面重畫
  const stageObjectsRef = useRef<StageObject[]>([]);
  const commitStageObjects = useCallback((list: StageObject[]) => {
    stageObjectsRef.current = list;
    setStageObjectsState(list);
  }, []);

  // 3D 模型檔案（R2）
  const [modelUrl, setModelUrl] = useState<string | null>(null);

  // Cue object states (per selected cue)
  const [cueStatesBase, setCueStatesState] = useState<CueState[]>([]);
  // 這份 cueStates 是哪個 cue 的：換 cue 後新的還沒讀到前，不能拿上一個 cue 的來編輯
  const [cueStatesFor, setCueStatesFor] = useState<string | null>(null);
  const [statesError, setStatesError] = useState(false);
  const cueStatesRef = useRef<CueState[]>([]);
  const cueStatesForRef = useRef<string | null>(null);
  const commitCueStates = useCallback((cueId: string | null, list: CueState[]) => {
    cueStatesRef.current = list;
    cueStatesForRef.current = cueId;
    setCueStatesState(list);
    setCueStatesFor(cueId);
  }, []);
  const statesLoading = !!sceneCueId && cueStatesFor !== sceneCueId && !statesError;

  // Selected objects (multi-select supported — viewport 點選預設單選，下方清單可勾多個)
  const [selectedObjectIds, setSelectedObjectIds] = useState<string[]>([]);
  // 單選兼容：給 viewport 點選用（清空之前選的）
  const selectSingle = useCallback((id: string | null) => {
    setSelectedObjectIds(id ? [id] : []);
  }, []);
  // 加減選（checkbox / Shift+click 用）
  const toggleSelected = useCallback((id: string) => {
    setSelectedObjectIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const isGlobalAdmin = user?.role === 'admin';
  // 在這個專案能不能編輯內容（全站製作，或這個專案的製作）；Drive 來源 / 封存 / 複製只有全站製作（isGlobalAdmin）
  const editorRole: ProjectRole | null = isGlobalAdmin ? 'admin' : myProjectRole;
  // 測距校準倍率 + 現場設定 + 材質 + 重置視角 + LED 畫布大小 — 存在專案上，所有人共用；
  // 能編輯這個專案內容的人（全站製作、這個專案的製作）能改（後台 updateProject 的 touchesViewSettings 也是這樣擋）
  const view = useProjectViewSettings(projectId, editorRole === 'admin');
  // 「用其他身分看」和直接打開導演頁 / 工作站：全站製作，或這個專案的製作（只限這個專案）
  const canViewAs = editorRole === 'admin';
  // 比例尺的樂手和器材（全專案共用；製作、導演可以擺）
  const band = useStageProps(projectId, canEditStageProps(editorRole));
  // 視角書籤（專案共用）
  const camBm = useCameraBookmarks(projectId, canEditCameraBookmarks(editorRole));
  // 現場設定：專案預設 + 自己調過的（存在這台電腦）
  const personalLook = usePersonalLook(projectId, view.lookSettings);

  const [rightTab, setRightTab] = useState<RightTab>('cues');
  const rightTabRef = useRef(rightTab);
  rightTabRef.current = rightTab;
  const [uploadOpen, setUploadOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [driveOpen, setDriveOpen] = useState(false);
  const [storyboardOpen, setStoryboardOpen] = useState(false);
  useEscapeClose(storyboardOpen, () => setStoryboardOpen(false));
  const [shareOpen, setShareOpen] = useState(false);
  // LED 畫布版型（數字表單；每首歌選用哪個）
  // 左右兩欄可以拖邊緣調寬（名字很長時看得到全名），記在這台電腦
  const songsPanel = usePanelWidth('sp_editor_songs_w', { side: 'left', min: 220, max: 560, enabledQuery: '(min-width: 901px)' });
  const sidePanel = usePanelWidth('sp_editor_side_w', { side: 'right', min: 300, max: 800, enabledQuery: '(min-width: 901px)' });
  const ledTpls = useLedTemplates(projectId);
  const [ledTplOpen, setLedTplOpen] = useState(false);
  // 畫布大小改了（3D 工具列「設定畫布大小…」或版型面板裡填的）、打開版型面板 → 重讀版型清單（等存檔送出再讀）
  const ledCanvasKey = view.ledCanvas ? `${view.ledCanvas.width}x${view.ledCanvas.height}` : '';
  const reloadLedTpls = ledTpls.reload;
  useEffect(() => {
    if (!ledCanvasKey && !ledTplOpen) return;
    const t = window.setTimeout(() => { void reloadLedTpls(); }, 800);
    return () => window.clearTimeout(t);
  }, [ledCanvasKey, ledTplOpen, reloadLedTpls]);
  // 編輯版型時 3D 的 LED 顯示的測試圖（面板關掉 = null，LED 照原本的）
  const [tplPreview, setTplPreview] = useState<LedPatternSource | null>(null);
  const tplPattern = useLedPatternCanvas(tplPreview);
  const customLedTemplates = useMemo(() => (ledTpls.list?.templates ?? []).filter(t => t.kind === 'custom'), [ledTpls.list]);
  const ledObjectInfos = useMemo(
    () => stageObjects.filter(o => o.category === 'led_panel').map(o => ({ id: o.id, name: o.displayName || o.meshName, order: o.order })),
    [stageObjects],
  );
  // 製作尺寸：版型（原尺寸）一起檢查；改完比例重讀版型清單（輸出用的座標跟著變）
  const workScaleTemplates = useMemo(
    () => workScaleTemplatesOf(ledTpls.rawList, new Map(stageObjects.map(o => [o.id, o.displayName || o.meshName]))),
    [ledTpls.rawList, stageObjects],
  );
  const changeWorkScale = useCallback(async (v: WorkScale | null) => {
    const err = await view.setWorkScale(v);
    if (!err) void reloadLedTpls();
    return err;
  }, [view.setWorkScale, reloadLedTpls]); // eslint-disable-line react-hooks/exhaustive-deps
  // 自動貼 UV（和版型面板都是貼在頁面下方，一次只開一個）
  const [uvOpen, setUvOpen] = useState(false);
  /** 自動貼 UV 怎麼打開的（'adjust' = 「調整排版」，直接到排版那一步）；n 每按一次加一 */
  const [uvEntry, setUvEntry] = useState<{ mode: 'full' | 'adjust'; n: number }>({ mode: 'full', n: 0 });
  /** 目前的模型版本是自動貼 UV 套用出來的（有「調整排版」可以按） */
  const [uvApplied, setUvApplied] = useState(false);
  useEffect(() => {
    if (!projectId || editorRole !== 'admin' || !modelUrl) { setUvApplied(false); return; }
    let alive = true;
    void (async () => {
      try {
        const saved = await getUvLayoutEdit(projectId);
        // 有「套用過」記號，或後台說這個版本是套用出來的（舊的套用沒有記號）
        if (alive) setUvApplied(editIsApplied(saved ? parseEditSnapshot(saved.edit) : null) || saved?.applied === true);
      } catch { if (alive) setUvApplied(false); }
    })();
    return () => { alive = false; };
  }, [projectId, editorRole, modelUrl]);
  /** 自動貼 UV 的 3D 預覽（面板算好的新 UV）；null = 3D 畫面照模型現在的 UV */
  const [uvPreview, setUvPreview] = useState<UvPreview | null>(null);
  const uvObjects = useMemo(
    () => stageObjects.filter(o => o.category === 'led_panel').map(o => ({ id: o.id, meshName: o.meshName, name: o.displayName || o.meshName, order: o.order })),
    [stageObjects],
  );
  // 從專案卡片的「客戶畫面」→「建立分享連結」過來 → 一進來就打開分享連結視窗
  const location = useLocation();
  useEffect(() => {
    if (!(location.state as { openShare?: boolean } | null)?.openShare) return;
    setShareOpen(true);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state]);  // eslint-disable-line react-hooks/exhaustive-deps
  // 邀請連結（幫這個專案加導演 / 動畫師）
  const [membersOpen, setMembersOpen] = useState(false);
  const [commentSearchOpen, setCommentSearchOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [importCuesOpen, setImportCuesOpen] = useState(false);
  // 狀態選單選「需修改」→ 先寫原因
  const [decisionSongId, setDecisionSongId] = useState<string | null>(null);
  const [projectMeta, setProjectMeta] = useState<api.ProjectFull | null>(null);
  const [projectMetaFailed, setProjectMetaFailed] = useState(false);
  const refreshProjectMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const p = await api.getProjectFull(projectId);
      setProjectMeta(p);
      setProjectMetaFailed(!p); // 404 會回 null，也算載不到
    } catch (e) {
      console.warn('load project meta failed', e);
      setProjectMetaFailed(true);
    }
  }, [projectId]);
  useEffect(() => { refreshProjectMeta(); }, [refreshProjectMeta]);
  // 專案有打開「成員看片也加浮水印」→ 3D 畫面印上自己的名字和時間（lib/watermark）
  useMemberWatermark(memberWatermarkOn(projectMeta?.watermark_members));

  // ── 復原 / 重做：每個 cue 各一份（lib/editHistory.ts），換 cue 不會復原到別的 cue ──
  const historyRef = useRef(new EditHistory());
  const [, setHistoryVersion] = useState(0);
  const bumpHistory = useCallback(() => setHistoryVersion(v => v + 1), []);
  // 機關時間軸的「時間預覽」（拉時間尺 / 播放）：開始改東西、換 cue、換歌都先結束，3D 回到該 cue 的姿勢（lib/mechanisms/timePreview.ts）
  const [timePreviewStop, setTimePreviewStop] = useState(0);
  // 窄版（直排，中間這欄只有約 240px 高）：時間軸只留細條，放不下展開的
  const narrowLayout = useMediaQuery('(max-width: 900px)');
  const endPreviewRef = useRef<() => void>(() => {});
  const endTimePreviewNow = useCallback(() => endPreviewRef.current(), []);
  // 機關（右邊「機關」分頁）：每個 cue 的機關位置、定義、速度檢查；改了也記在同一份復原
  // Cue 庫專案：所有改畫面的動作先放進「還沒存的改動」（草稿），按另存 / 更新才寫進後台（lib/lookDraft.ts）
  const libMode = !!projectMeta?.cueLibrary;
  const libId = libMode ? (libPreviewId ?? cues.find(c => c.id === selectedCueId)?.libraryCueId ?? null) : null;
  const lookDraft = useLookDraft({ projectId, libId, enabled: libMode });
  const lookDraftRef = useRef(lookDraft);
  lookDraftRef.current = lookDraft;
  const libModeRef = useRef(libMode);
  libModeRef.current = libMode;
  const libIdRef = useRef(libId);
  libIdRef.current = libId;
  // 這個專案 Cue 庫的所有 cue（編號、名稱、幾首歌在用）；另存 / 更新的對話框
  const [libCues, setLibCues] = useState<cueLib.LibraryCue[]>([]);
  const libCuesRef = useRef(libCues);
  libCuesRef.current = libCues;
  const [saveAsOpen, setSaveAsOpen] = useState<{ after?: () => void } | null>(null);
  const [updateOpen, setUpdateOpen] = useState<{ usage: cueLib.LibraryCueUsage; after?: () => void } | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  // 覆蓋 cue 進行中：按下去馬上有提示，存好之前不能換 cue（不然會再跳一次「要另存嗎」）
  const lookSavingRef = useRef(false);
  const saveToastRef = useRef<string | null>(null);
  const [libLoading, setLibLoading] = useState(true);
  // 畫面上看到的物件狀態 = 後台給的 + 還沒存的改動
  const cueStates = useMemo(() => applyDraftToStates(cueStatesBase, lookDraft.draft), [cueStatesBase, lookDraft.draft]);
  const libDraftForMech = useMemo(() => (libMode && libId
    ? { mechanisms: lookDraft.draft?.mechanisms ?? EMPTY_DRAFT_MECHS, set: lookDraft.setMechanisms }
    : undefined), [libMode, libId, lookDraft.draft?.mechanisms, lookDraft.setMechanisms]);
  const previewBaseMech = lookDraft.base?.mechanisms;
  const previewLook = useMemo(
    () => (libMode && libPreviewId ? { mechanisms: previewBaseMech ?? [] } : null),
    [libMode, libPreviewId, previewBaseMech],
  );
  // Cue 庫分頁的小分頁：清單 / 機關（機關 = 這個 Cue 庫 cue 自己的機關，不用先回到某首歌）
  const [libSub, setLibSub] = useState<'list' | 'mech'>('list');
  const libShown = libCues.find(c => c.id === libId) ?? null;
  const libraryMechOn = rightTab === 'library' && libMode && libSub === 'mech' && !!libShown;
  // Cue 庫專案：機關時間放開就存進 Cue 庫的 cue（別首歌也用 → 問一次）；做事的函式在下面的「機關時間放開就存」那一段
  const libTimingSaveRef = useRef<(req: LibTimingSave) => Promise<LibTimingStep | null>>(async () => null);
  const libTimings = useMemo(() => (libMode ? { save: (req: LibTimingSave) => libTimingSaveRef.current(req) } : undefined), [libMode]);
  const [scopeAsk, setScopeAsk] = useState<{ songCount: number; resolve: (s: TimingScope | null) => void } | null>(null);
  const mech = useEditorMechanisms({
    projectId, songId: selectedSongId, cues, selectedCueId, canEdit: true, scaleCorrection: view.scaleCorrection,
    libTimings,
    previewLook,
    libraryView: libraryMechOn && libShown ? { name: libShown.name, crossfadeSeconds: libShown.crossfadeSeconds } : null,
    onEditStart: endTimePreviewNow,
    history: { push: (scope, step) => historyRef.current.push(scope, step), bump: bumpHistory },
    libDraft: libDraftForMech,
  });
  const mechRef = useRef(mech);
  mechRef.current = mech;
  const currentHistoryScope = historyScope(sceneCueId);
  const draftUndoOn = libMode && !!libId;
  const historyScopeRef = useRef(currentHistoryScope);
  historyScopeRef.current = currentHistoryScope;
  const baseCounts = historyRef.current.counts(currentHistoryScope);
  // Cue 庫專案：還沒存的改動可以復原 / 重做，退完才輪到已經存了的步驟
  const historyCounts = draftUndoOn
    ? { undo: baseCounts.undo + lookDraft.undoCount, redo: baseCounts.redo + lookDraft.redoCount }
    : baseCounts;

  // 改物件位置 / 顯示、復原、重做都排隊一個一個做：每一步都拿得到前一步存好的值（復原才記得對），
  // 全部做完才重新讀一次（多選拖曳時物件不會一個一個跳回舊位置再跳過來）
  const editQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingEditsRef = useRef(0);
  const refreshNeededRef = useRef({ states: false, objects: false });
  const refreshCueStatesRef = useRef<() => Promise<void>>(async () => {});
  const refreshStageObjectsRef = useRef<() => Promise<void>>(async () => {});
  const runEdit = useCallback(<T,>(job: () => Promise<T>, refresh: 'states' | 'objects'): Promise<T> => {
    endTimePreviewNow();
    pendingEditsRef.current++;
    const run = editQueueRef.current.then(async () => {
      try {
        return await job();
      } finally {
        refreshNeededRef.current[refresh] = true;
        pendingEditsRef.current--;
        if (pendingEditsRef.current === 0) {
          const need = refreshNeededRef.current;
          refreshNeededRef.current = { states: false, objects: false };
          if (need.objects) await refreshStageObjectsRef.current();
          if (need.states) await refreshCueStatesRef.current();
        }
      }
    });
    editQueueRef.current = run.then(() => undefined, () => undefined);
    return run;
  }, [endTimePreviewNow]);
  const runHistory = useCallback((kind: 'undo' | 'redo') => {
    const ld = lookDraftRef.current;
    if (ld && libModeRef.current && libIdRef.current) {
      if (kind === 'undo' && ld.undoCount > 0) { ld.undo(); return; }
      if (kind === 'redo' && ld.redoCount > 0) { ld.redo(); return; }
    }
    const scope = historyScopeRef.current;
    const history = historyRef.current;
    if (history.counts(scope)[kind] === 0) return;
    runEdit(async () => {
      // 排隊輪到時已經換到別的 cue → 不做（只復原 / 重做現在選的 cue）
      if (historyScopeRef.current !== scope) return;
      await history[kind](scope);
    }, scope === historyScope(null) ? 'objects' : 'states')
      .catch(e => { console.warn(kind + ' failed', e); toast.error((kind === 'undo' ? '復原' : '重做') + '失敗：' + msg(e)); })
      .finally(bumpHistory);
  }, [runEdit, bumpHistory]);

  // 把最新值放進 ref，給 keyboard listener 用（避免 useEffect 因 derived value 變化重綁）
  const guardRef = useRef<(action: () => void) => void>(a => a());
  const selectLibRef = useRef<(id: string) => void>(() => {});
  const navRef = useRef({ masterCues: [] as Cue[], visibleSongs: [] as Song[], selectedCueId: null as string | null, selectedSongId: null as string | null });
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // 全螢幕預演中：復原 / 重做、換歌、換 cue 都關掉（這一頁沒有影片要控制）
      if (isStageShowActive()) return;
      // Cmd/Ctrl+Z = 復原；Cmd/Ctrl+Shift+Z 或 Ctrl+Y = 重做
      // 在會打字的欄位裡交給瀏覽器自己的復原；勾選框、機關滑桿上照樣復原（lib/keyTarget.ts）
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        const key = e.key.toLowerCase();
        const isUndo = key === 'z' && !e.shiftKey;
        const isRedo = (key === 'z' && e.shiftKey) || (key === 'y' && !e.shiftKey);
        if (isUndo || isRedo) {
          if (isTextEntryTarget(e.target)) return;
          e.preventDefault();
          runHistory(isUndo ? 'undo' : 'redo');
          return;
        }
      }

      // j/k = 切歌；Shift+J/K = 切 cue
      if (isTypingTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k !== 'j' && k !== 'k') return;
      e.preventDefault();
      // 「Cue 庫」分頁開著：J / K 照編號上下切換 Cue 庫的 cue
      if (rightTabRef.current === 'library' && libModeRef.current) {
        const next = stepLibraryCue(sortLibraryCues(libCuesRef.current), libIdRef.current, k === 'j' ? 1 : -1);
        if (next && next !== libIdRef.current) selectLibRef.current(next);
        return;
      }
      const { masterCues: cs, visibleSongs: ss, selectedCueId: cid, selectedSongId: sid } = navRef.current;
      if (e.shiftKey) {
        if (cs.length === 0) return;
        const curIdx = cs.findIndex(c => c.id === cid);
        const nextIdx = k === 'j'
          ? (curIdx < 0 ? 0 : Math.min(curIdx + 1, cs.length - 1))
          : (curIdx <= 0 ? 0 : curIdx - 1);
        // 換 cue 不換分頁：用戶常常只是想一個一個 cue 看舞台長怎樣（要改再自己點「物件狀態」）
        guardRef.current(() => { setLibPreviewId(null); setSelectedCueId(cs[nextIdx].id); });
      } else {
        if (ss.length === 0) return;
        const curIdx = ss.findIndex(s => s.id === sid);
        const nextIdx = k === 'j'
          ? (curIdx < 0 ? 0 : Math.min(curIdx + 1, ss.length - 1))
          : (curIdx <= 0 ? 0 : curIdx - 1);
        guardRef.current(() => setSelectedSongId(ss[nextIdx].id));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [runHistory]);

  // ── Loaders ──
  const refreshSongs = useCallback(async () => {
    if (!projectId) return;
    setSongsLoading(true);
    try {
      const list = await api.listSongs(projectId);
      setSongs(list);
      setSelectedSongId(prev => prev || (list[0]?.id ?? null));
    } catch (e) {
      toast.error('歌單讀不到：' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSongsLoading(false);
    }
  }, [projectId]);

  const refreshCues = useCallback(async () => {
    if (!projectId || !selectedSongId) { setCues([]); setCuesSongId(null); setSelectedCueId(null); return; }
    const songId = selectedSongId;
    setCuesLoading(true);
    try {
      const list = await api.listCues(projectId, songId);
      setCues(list);
      setCuesSongId(songId);
      setSelectedCueId(prev => list.find(c => c.id === prev) ? prev : (list[0]?.id ?? null));
      // 歌單上的「N 個 cue」跟著改（新增 / 刪除 / 沿用 / 匯入 cue 之後不用重新整理）
      const counts = songCueCounts(list);
      setSongs(ss => ss.map(s => (s.id === songId && (s.cueCount !== counts.cueCount || s.proposalCount !== counts.proposalCount) ? { ...s, ...counts } : s)));
    } finally {
      setCuesLoading(false);
    }
  }, [projectId, selectedSongId]);

  const refreshStageObjects = useCallback(async () => {
    if (!projectId) return;
    try {
      const list = await api.listStageObjects(projectId);
      commitStageObjects(list);
      setStageObjectsStatus('ready');
    } catch (e) {
      console.warn('load stage objects failed', e);
      // 已經讀到過就留著舊的；第一次就失敗 → 顯示載入失敗（不是「還沒有物件」）
      setStageObjectsStatus(s => (s === 'ready' ? s : 'error'));
    }
  }, [projectId, commitStageObjects]);

  const refreshModel = useCallback(async () => {
    if (!projectId) return;
    try {
      const info = await api.getModelInfo(projectId);
      setModelUrl(info ? api.modelDownloadUrl(info.key) : null);
    } catch (e) {
      console.warn('load model info failed', e);
      setModelUrl(null);
    }
  }, [projectId]);

  // 每次讀取編號：讀的途中換了 cue / 又有更新的讀取 → 舊的結果丟掉（不然會把別的 cue 的狀態放到這個 cue）
  const statesRequestRef = useRef(0);
  const refreshCueStates = useCallback(async () => {
    const req = ++statesRequestRef.current;
    // Cue 庫分頁預覽：讀這個 Cue 庫的 cue 自己的畫面
    if (libPreviewId) {
      if (!projectId) return;
      const key = 'lib:' + libPreviewId;
      if (cueStatesForRef.current !== key) setStatesError(false);
      try {
        const list = await cueLib.getLibraryStates(projectId, libPreviewId);
        if (req !== statesRequestRef.current) return;
        commitCueStates(key, list);
        setStatesError(false);
      } catch (e) {
        console.warn('load library states failed', e);
        if (req === statesRequestRef.current && cueStatesForRef.current !== key) setStatesError(true);
      }
      return;
    }
    // 剛換歌：cue 清單還是上一首的（選的 cue 也是上一首的）→ 等這首歌的 cue 讀到再讀，不然會拿錯的組合去問（404）
    if (!projectId || !selectedSongId || !selectedCueId || cuesSongId !== selectedSongId) { commitCueStates(null, []); setStatesError(false); return; }
    const cueId = selectedCueId;
    if (cueStatesForRef.current !== cueId) setStatesError(false);
    try {
      const list = await api.listCueStates(projectId, selectedSongId, cueId);
      if (req !== statesRequestRef.current) return;
      commitCueStates(cueId, list);
      setStatesError(false);
    } catch (e) {
      console.warn('load cue states failed', e);
      if (req === statesRequestRef.current && cueStatesForRef.current !== cueId) setStatesError(true);
    }
  }, [projectId, selectedSongId, selectedCueId, cuesSongId, commitCueStates, libPreviewId]);
  refreshCueStatesRef.current = refreshCueStates;
  refreshStageObjectsRef.current = refreshStageObjects;

  useEffect(() => { setStageObjectsStatus('loading'); }, [projectId]);
  useEffect(() => { refreshSongs(); refreshStageObjects(); refreshModel(); }, [refreshSongs, refreshStageObjects, refreshModel]);

  // 進入專案時推進 recent（Cmd+K palette 用）— 等專案 metadata 拉到才推（取真實名稱）
  // 簡化：用 projectId + 從 listProjects 找 name；如果還沒拉到就用 projectId
  useEffect(() => {
    if (!projectId) return;
    // 拉 project name（不增 round-trip — 用 listProjects 一個 lazy fetch）
    api.listProjects().then(list => {
      const p = list.find(x => x.id === projectId);
      if (p) pushRecentProject(p.id, p.name);
    }).catch(() => { /* swallow */ });
  }, [projectId]);
  useEffect(() => { refreshCues(); }, [refreshCues]);
  useEffect(() => { refreshCueStates(); }, [refreshCueStates]);

  // ── Derived ──
  const masterCues = useMemo(() => cues.filter(c => c.status === 'master'), [cues]);
  const proposalCues = useMemo(() => cues.filter(c => c.status === 'proposal'), [cues]);
  const selectedCue = useMemo(() => cues.find(c => c.id === selectedCueId) || null, [cues, selectedCueId]);
  useEffect(() => { setLibPreviewId(null); }, [selectedSongId]);
  const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId) || null, [songs, selectedSongId]);

  // AE 即時照這首歌選的 LED 版型放到 LED 上（lib/ledLiveTemplate.ts）；版型面板開著的測試圖蓋過一切
  const songTplPlan = useMemo(() => songTemplatePlan(ledTpls.list, selectedSong?.templateId), [ledTpls.list, selectedSong?.templateId]);
  // 中途換版型（時間線）：開頭 = 歌選的版型，換版型的點 = 歌的 templateSwitches；Cue 的時間用畫面上最新的
  const songTimelineState = useSongTimeline(ledTpls.list, selectedSong?.templateId, selectedSong?.templateSwitches, cuesSongId === selectedSongId ? cues : null);
  const [ledSegPick, setLedSegPick] = useState<number | null>(null);        // AE 即時沒有播放時間：手動選看哪一段（只有這個畫面，不存）
  useEffect(() => { setLedSegPick(null); }, [selectedSongId]);
  // 3D 工具列「⋯」的對位圖 / AE 合成腳本 / 位置表：這首歌用了版型就照版型下載（有中途換版型就整條時間線一起）
  const ledSongTpl = useMemo(
    () => (songTplPlan && ledTpls.list ? { plan: songTplPlan, manuals: ledTpls.list.screens, timeline: songTimelineState.timeline, songName: selectedSong?.name } : null),
    [songTplPlan, ledTpls.list, songTimelineState.timeline, selectedSong?.name],
  );
  const [ledFullView, setLedFullView] = useState(false);          // 「暫時用整張畫布看」：只有這個畫面，不存
  useEffect(() => { setLedFullView(false); }, [selectedSongId]);
  const [aeFrameSize, setAeFrameSize] = useState<{ width: number; height: number } | null>(null);
  const ledChoice = useMemo(
    () => chooseLedTemplate({ source: 'ndi', previewPlan: tplPreview?.plan ?? null, songPlan: songTplPlan, songTimeline: songTimelineState.timeline, fullView: ledFullView }),
    [tplPreview?.plan, songTplPlan, songTimelineState.timeline, ledFullView],
  );
  // AE 即時沒有播放時間：中途換版型時，看「手動選的那一段」，沒選就看選到的 Cue 的影片時間（沒有 = 開頭）
  const ledHoldSec = useMemo(() => {
    const tl = ledChoice.timeline;
    if (!tl) return 0;
    if (ledSegPick !== null && tl.segments[ledSegPick]) return tl.segments[ledSegPick].fromSec;
    return selectedCue?.videoTimeSec ?? 0;
  }, [ledChoice.timeline, ledSegPick, selectedCue?.videoTimeSec]);
  const ledChip = useMemo(
    () => ledTemplateChip(ledChoice, { source: 'ndi', songPlan: songTplPlan, list: ledTpls.list, timeSec: ledHoldSec, clocked: false, pickedSegment: ledSegPick }),
    [ledChoice, songTplPlan, ledTpls.list, ledHoldSec, ledSegPick],
  );
  const ledHint = useMemo(
    () => liveSourceHint({ choice: ledChoice, frame: aeFrameSize, source: 'ndi', fullCanvas: ledTpls.list?.fullCanvas ?? null, templates: customLedTemplates }),
    [ledChoice, aeFrameSize, ledTpls.list?.fullCanvas, customLedTemplates],
  );

  const visibleSongs = useMemo(() => {
    let list = statusFilter === 'all' ? songs : songs.filter(s => s.status === statusFilter);
    if (mineOnly && mineSongIds.size > 0) list = list.filter(s => mineSongIds.has(s.id));
    return list;
  }, [songs, statusFilter, mineOnly, mineSongIds]);

  // 開啟 mineOnly 時拉 activity log 算出當前用戶有動過的 song
  useEffect(() => {
    if (!mineOnly || !projectId || !user) return;
    let cancelled = false;
    api.listActivity(projectId, 200).then(list => {
      if (cancelled) return;
      const ids = new Set<string>();
      for (const a of list) {
        if (a.userId !== user.id) continue;
        // payload 可能含 songId / song_id；target_type 是 song / cue / cue_state
        const payload = a.payload as Record<string, unknown>;
        if (a.targetType === 'song' && a.targetId) ids.add(a.targetId);
        if (typeof payload?.songId === 'string') ids.add(payload.songId);
        if (typeof payload?.song_id === 'string') ids.add(payload.song_id);
      }
      setMineSongIds(ids);
    }).catch(() => { /* 略 */ });
    return () => { cancelled = true; };
  }, [mineOnly, projectId, user?.id]);
  // 同步 navRef 給 keyboard listener
  navRef.current = { masterCues, visibleSongs, selectedCueId, selectedSongId };
  const statusCounts = useMemo(() => {
    const c: Record<SongStatus, number> = { todo: 0, in_review: 0, approved: 0, needs_changes: 0 };
    for (const s of songs) c[s.status]++;
    return c;
  }, [songs]);

  // 當 filter 把當前選中的歌篩掉時，跳到第一個可見的歌
  useEffect(() => {
    if (!selectedSongId) return;
    if (visibleSongs.some(s => s.id === selectedSongId)) return;
    setSelectedSongId(visibleSongs[0]?.id ?? null);
  }, [visibleSongs, selectedSongId]);

  // viewport 用的 states：有 cue 用 cueStates，沒 cue 用 stageObjects 的 default 假裝成 state
  const viewportStates: CueState[] = useMemo(() => {
    if (sceneCueId && cueStates.length > 0) return cueStates;
    return stageObjects.map(o => ({
      objectId: o.id,
      meshName: o.meshName,
      displayName: o.displayName,
      category: o.category,
      order: o.order,
      locked: o.locked,
      default: { position: o.defaultPosition, rotation: o.defaultRotation, scale: o.defaultScale },
      defaultHidden: isDefaultHidden(o),
      override: null,
      effective: { position: o.defaultPosition, rotation: o.defaultRotation, scale: o.defaultScale, visible: defaultVisible(o) },
    }));
  }, [sceneCueId, cueStates, stageObjects]);

  // 自動貼 UV 的 3D 預覽：選了「不放進畫布」的物件，套用以後類型會變成「其他」→ 預覽時 3D 畫面先照一般物件顯示（沒有測試圖、沒有影片）。只換給 3D 畫面看的那份，右邊的物件清單不動
  const previewExcluded = uvOpen && uvPreview && uvPreview.excluded && uvPreview.excluded.length > 0 ? uvPreview.excluded : null;
  const sceneStageObjects: StageObject[] = useMemo(
    () => (previewExcluded ? stageObjects.map(o => (previewExcluded.includes(o.id) && o.category === 'led_panel' ? { ...o, category: 'other' as const } : o)) : stageObjects),
    [stageObjects, previewExcluded],
  );
  const sceneStates: CueState[] = useMemo(
    () => (previewExcluded ? viewportStates.map(s => (previewExcluded.includes(s.objectId) && s.category === 'led_panel' ? { ...s, category: 'other' as const } : s)) : viewportStates),
    [viewportStates, previewExcluded],
  );
  // 換歌：新歌的 cue / 物件狀態 / 機關位置都讀到之前畫面停在上一首，讀到了直接跳到這首歌的 cue（不從預設位置走過來）
  const songSceneReady = !selectedSongId || (cuesSongId === selectedSongId && !statesLoading && mech.positionsSongId === selectedSongId);
  const songSwitch = useSongSwitchHold(selectedSongId, songSceneReady, {
    states: sceneStates,
    cueKey: selectedCueId,
    crossfadeSeconds: cueTransitionSeconds(selectedCue),
    mechanismTargets: mech.sceneProps.mechanismTargets,
    mechanismTimings: mech.sceneProps.mechanismTimings,
  });
  const stageScene = songSwitch.scene;

  // ── 歌曲影片（＋加影片）：上傳中切到別首歌也會繼續；好了歌單上的「影片 V3」跟著更新 ──
  const [addVideoSongId, setAddVideoSongId] = useState<string | null>(null);
  const addVideoSong = addVideoSongId ? songs.find(s => s.id === addVideoSongId) ?? null : null;
  // 「影片版本」視窗（歌單上點「影片 V3」）：看每一版、刪除、到導演頁播放
  const [versionsSongId, setVersionsSongId] = useState<string | null>(null);
  // 批次上傳音樂視窗（歌單標題旁的「⋯」；只有製作看得到）
  const [musicBatchOpen, setMusicBatchOpen] = useState(false);
  const versionsSong = versionsSongId ? songs.find(s => s.id === versionsSongId) ?? null : null;
  // 從系統狀態的「重複的檔案」→「打開影片版本」過來 → 一進來就打開那首歌的影片版本
  useEffect(() => {
    const sid = (location.state as { openVersionsSongId?: string } | null)?.openVersionsSongId;
    if (!sid) return;
    setVersionsSongId(sid);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state]);  // eslint-disable-line react-hooks/exhaustive-deps
  // 刪了一版 / 交好了：安靜地重新讀歌單（「影片 V3」跟著變；不閃載入中）
  const reloadSongsQuietly = useCallback(() => {
    if (!projectId) return;
    api.listSongs(projectId).then(setSongs).catch(() => { /* 下次重新整理就有 */ });
  }, [projectId]);
  const videoJobs = useVideoJobs(projectId ?? null);
  const videoJobsBySong = useMemo(() => {
    const m = new Map<string, VideoJob[]>();
    for (const j of videoJobs) m.set(j.songId, [...(m.get(j.songId) ?? []), j]);
    return m;
  }, [videoJobs]);
  useWarnBeforeUnloadWhileUploading();
  const onVideoJobDone = useCallback((job: VideoJob) => {
    if (!projectId || job.projectId !== projectId) return;
    toast.success(`${job.songLabel}：${job.result?.label || '新的一版'} 好了`);
    reloadSongsQuietly();
  }, [projectId, reloadSongsQuietly]);
  useVideoJobDone(onVideoJobDone);

  // ── Song actions ──
  async function handleAddSong() {
    if (!projectId) return;
    const name = (await promptDialog({ title: '新增歌曲', label: '歌名', placeholder: '例：主題曲', hint: '歌曲代號（S01、S02…）會自動編好，之後在歌單上點代號可以改', confirmLabel: '新增', maxLength: 100 }))?.trim();
    if (!name) return;
    try { await api.createSong(projectId, name); await refreshSongs(); }
    catch (e) { toast.error('歌曲沒有新增：' + msg(e)); }
  }
  async function handleRenameSong(songId: string, currentName: string) {
    if (!projectId) return;
    const name = (await promptDialog({ title: '改歌名', label: '歌名', defaultValue: currentName, confirmLabel: '改名', maxLength: 100 }))?.trim();
    if (!name || name === currentName) return;
    try { await api.updateSong(projectId, songId, { name }); await refreshSongs(); }
    catch (e) { toast.error('改名失敗：' + msg(e)); }
  }
  async function handleDeleteSong(songId: string, name: string) {
    if (!projectId) return;
    if (!(await confirmDialog({ title: `刪除歌曲「${name}」？`, message: deleteSongMessage(libMode), confirmLabel: '刪除', destructive: true }))) return;
    try {
      await api.deleteSong(projectId, songId);
      if (selectedSongId === songId) setSelectedSongId(null);
      await refreshSongs();
      setTrashKey(k => k + 1);
      toast.success(deletedSongToast(name), 6000);
    } catch (e) { toast.error('刪除失敗：' + msg(e)); }
  }
  // 從「最近刪除的歌」救回來了：歌單馬上更新（回到原本的位置）；原本一首歌都沒有的話順便選第一首
  function handleSongRestored() {
    if (!projectId) return;
    api.listSongs(projectId)
      .then(list => { setSongs(list); setSelectedSongId(prev => prev || (list[0]?.id ?? null)); })
      .catch(() => { void refreshSongs(); });
  }
  /**
   * 管理頁的狀態選單：帶上畫面上以為的目前狀態（expectedStatus）→ 導演 / 動畫師剛改過的話後台回 409，
   * 不會蓋掉別人的決定；這時重新讀歌單、用白話說現在是什麼狀態（跟動畫師頁、導演頁一樣）
   */
  async function handleUpdateSongStatus(songId: string, status: SongStatus, reason?: string): Promise<boolean> {
    if (!projectId) return false;
    const song = songs.find(s => s.id === songId);
    const expected = song?.status;
    if (!song || !expected || expected === status) return false;
    if (statusBlockedReason(song, status)) return false;
    // 需修改 → 先開對話框（原因可以空白）；通過 / 需修改都記在導演看的那一版（放行版）
    if (status === 'needs_changes' && reason === undefined) {
      setDecisionSongId(songId);
      return false;
    }
    const review = (status === 'needs_changes' || status === 'approved')
      ? { reason: reason ?? '', reviewVideoId: reviewVideoIdFor(song) }
      : {};
    const setStatus = (to: SongStatus) => setSongs(list => list.map(s => s.id === songId ? { ...s, status: to } : s));
    setStatus(status);  // 先改畫面
    try {
      await api.updateSong(projectId, songId, { status, expectedStatus: expected, ...review });
      // 審片紀錄、「有新版」要用新的
      api.listSongs(projectId).then(setSongs).catch(() => { /* 下次重新整理再拿 */ });
      return true;
    } catch (e) {
      if (api.errorStatus(e) === 409) {
        const stale = staleStatusMessage((e as api.ApiError).data?.currentStatus, expected);
        try {
          setSongs(await api.listSongs(projectId));
        } catch {
          setStatus(expected);
        }
        toast.warn(stale ?? friendlyError(e));
      } else {
        setStatus(expected);
        toast.error('狀態沒有改成：' + friendlyError(e));
      }
      return false;
    }
  }
  // 指派負責的動畫師（動畫師工作站「我的」清單就是看這個）
  async function handleAssignAnimator(songId: string, userId: string | null) {
    if (!projectId) return;
    const before = songs.find(s => s.id === songId)?.animatorUserId ?? null;
    if (before === userId) return;
    setSongs(list => list.map(s => s.id === songId ? { ...s, animatorUserId: userId } : s));
    try {
      await api.updateSong(projectId, songId, { animator_user_id: userId });
    } catch (e) {
      setSongs(list => list.map(s => s.id === songId ? { ...s, animatorUserId: before } : s));
      toast.error('指派沒有存到：' + friendlyError(e));
    }
  }
  // 中途換版型存好了：歌單上這首歌的開頭版型和換版型的點換成後台回的，再重讀版型清單（幾首歌在用）
  function handleTimelineSaved(songId: string, out: { templateId: string | null; templateSwitches: TimelineSwitch[] }) {
    setSongs(list => list.map(s => s.id === songId ? { ...s, templateId: out.templateId, templateSwitches: out.templateSwitches } : s));
    void ledTpls.reload();
  }
  // 每首歌選的 LED 版型（null = 整張畫布）
  async function handleSongTemplate(songId: string, templateId: string | null) {
    if (!projectId) return;
    const before = songs.find(s => s.id === songId)?.templateId ?? null;
    if (before === templateId) return;
    setSongs(list => list.map(s => s.id === songId ? { ...s, templateId } : s));
    try {
      await setSongTemplate(projectId, songId, templateId);
      void ledTpls.reload();
    } catch (e) {
      setSongs(list => list.map(s => s.id === songId ? { ...s, templateId: before } : s));
      toast.error('版型沒有存到：' + friendlyError(e));
    }
  }
  async function moveSong(songId: string, direction: -1 | 1) {
    if (!projectId) return;
    const idx = songs.findIndex(s => s.id === songId);
    const newIdx = idx + direction;
    if (idx < 0 || newIdx < 0 || newIdx >= songs.length) return;
    const reordered = [...songs];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setSongs(reordered);
    try { await api.reorderSongs(projectId, reordered.map(s => s.id)); }
    catch (e) { toast.error('順序沒有存到：' + msg(e)); await refreshSongs(); }
  }

  // ── Cue templates / palette ──
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  // Cue 庫專案：常用擺位先套進草稿。'apply' = 只套進草稿；'new' = 套進草稿後直接開「另存成新的 cue」
  const [templatePickerMode, setTemplatePickerMode] = useState<'apply' | 'new'>('apply');
  const [saveAsName, setSaveAsName] = useState<string | null>(null);
  // 「中途換版型」對話框開在哪一首歌上
  const [timelineSongId, setTimelineSongId] = useState<string | null>(null);
  const timelineSong = timelineSongId ? songs.find(x => x.id === timelineSongId) ?? null : null;

  async function handleSaveCueAsTemplate(cueId: string, cueName: string) {
    if (!projectId) return;
    const name = (await promptDialog({
      title: '存成「常用擺位」',
      message: '把這個 cue 裡每個物件的位置存起來，之後新增 cue 時可以直接套用。',
      label: '名稱',
      defaultValue: cueName,
      confirmLabel: '下一步',
      maxLength: 100,
    }))?.trim();
    if (!name) return;
    const choice = await chooseDialog({
      title: `「${name}」要給哪些專案用？`,
      choices: [
        { value: 'project', label: '只給這個專案' },
        { value: 'global', label: '所有專案都能用', primary: true },
      ],
    });
    if (!choice) return;
    const scope = choice === 'global';
    try {
      await api.createCueTemplate(projectId, { name, description: '', fromCueId: cueId, global: scope });
      toast.success(`已存成${scope ? '所有專案都能用' : '這個專案'}的常用擺位「${name}」`);
    } catch (e) {
      toast.error('常用擺位沒有存到：' + msg(e));
    }
  }

  /** Cue 庫專案：把常用擺位套進目前這個 cue 的草稿（還沒存的改動），不新增 cue */
  async function handleApplyTemplateToDraft(templateId: string) {
    const ld = lookDraftRef.current;
    if (!projectId || !libId || !ld) { toast.info('先選一個 cue，常用擺位會套在它還沒存的改動裡'); return; }
    try {
      const [tpl, base] = await Promise.all([api.getCueTemplate(projectId, templateId), currentBaseLook()]);
      if (!base) { toast.error('這個 cue 的畫面讀不到，請重新整理'); return; }
      ld.replaceDraft(applyLookToDraft(libId, base.look, tpl.look));
      setTemplatePickerOpen(false);
      const skipped = tpl.skipped.objects + tpl.skipped.mechanisms;
      toast.success(`已套用常用擺位「${tpl.name}」，還沒存${skipped > 0 ? `（這個專案沒有的 ${skipped} 個物件或機關已略過）` : ''}：可以另存成新的 cue 或更新，不要就按放棄`);
      if (templatePickerMode === 'new') {
        setSaveAsName(tpl.name);
        setSaveAsOpen({});
      }
    } catch (e) { toast.error('常用擺位沒有套用：' + msg(e)); }
  }

  function openTemplatePicker(mode: 'apply' | 'new') {
    if (!libId) { toast.info('先選一個 cue，常用擺位會套在它還沒存的改動裡'); return; }
    setTemplatePickerMode(mode);
    setTemplatePickerOpen(true);
  }

  async function handleApplyTemplate(templateId: string) {
    if (!projectId || !selectedSongId) return;
    const defaultName = `Cue ${cues.length + 1}`;
    const name = (await promptDialog({ title: '用常用擺位新增 cue', label: 'cue 名稱', defaultValue: defaultName, confirmLabel: '新增', maxLength: 100 }))?.trim();
    if (!name) return;
    try {
      // 模板有自己的淡入淡出秒數會蓋過這個預設
      await api.createCue(projectId, selectedSongId, { name, fromTemplateId: templateId, crossfadeSeconds: NEW_CUE_CROSSFADE_SECONDS });
      await refreshCues();
      setRightTab('cues');
      setTemplatePickerOpen(false);
    } catch (e) { toast.error('常用擺位沒有套用：' + msg(e)); }
  }

  // ── Cue actions ──
  async function handleAddBlankCue() {
    if (!projectId || !selectedSongId) return;
    const defaultName = `Cue ${cues.length + 1}`;
    const name = (await promptDialog({ title: '新增空白 cue', message: '所有物件都在預設位置。', label: 'cue 名稱', defaultValue: defaultName, confirmLabel: '新增', maxLength: 100 }))?.trim();
    if (!name) return;
    try { await api.createCue(projectId, selectedSongId, { name, crossfadeSeconds: NEW_CUE_CROSSFADE_SECONDS }); await refreshCues(); setRightTab('cues'); }
    catch (e) { toast.error('cue 沒有新增：' + msg(e)); }
  }
  async function handleCloneCue(sourceCueId?: string) {
    if (!projectId || !selectedSongId) return;
    const src = sourceCueId
      ? cues.find(c => c.id === sourceCueId)
      : (selectedCue);
    if (!src) { toast.warn('還沒有選 cue，請先在清單裡選一個要沿用的 cue'); return; }
    const defaultName = `${src.name} (副本)`;
    const name = (await promptDialog({ title: `沿用「${src.name}」新增 cue`, label: '新 cue 名稱', defaultValue: defaultName, confirmLabel: '新增', maxLength: 100 }))?.trim();
    if (!name) return;
    try {
      // 後台複製 cue 時不會帶淡入淡出秒數 → 自己帶原本 cue 的
      await api.createCue(projectId, selectedSongId, { name, cloneFrom: src.id, crossfadeSeconds: src.crossfadeSeconds });
      await refreshCues();
      setRightTab('cues');
    } catch (e) { toast.error('沒有沿用成功：' + msg(e)); }
  }
  async function handleSnapshotCue() {
    if (!projectId || !selectedSongId) return;
    if (viewportStates.length === 0) { toast.warn('舞台上還沒有物件，沒有東西可以記下來'); return; }
    const defaultName = selectedCue ? `${selectedCue.name}（目前狀態）` : `Cue ${cues.length + 1}`;
    const name = (await promptDialog({ title: '用目前的 3D 畫面新增 cue', message: '每個物件現在的位置都會記進新的 cue。', label: 'cue 名稱', defaultValue: defaultName, confirmLabel: '新增', maxLength: 100 }))?.trim();
    if (!name) return;
    try {
      const snapshotStates = viewportStates.map(s => ({
        objectId: s.objectId,
        position: s.effective.position,
        rotation: s.effective.rotation,
      }));
      await api.createCue(projectId, selectedSongId, withMechanismSnapshot({ name, snapshotStates, crossfadeSeconds: NEW_CUE_CROSSFADE_SECONDS }, mech.currentValues));
      await refreshCues();
      setRightTab('cues');
    } catch (e) { toast.error('cue 沒有新增：' + msg(e)); }
  }
  async function handleDuplicateCueQuick(cueId: string) {
    if (!projectId || !selectedSongId) return;
    const src = cues.find(c => c.id === cueId);
    if (!src) return;
    try {
      await api.createCue(projectId, selectedSongId, { name: `${src.name} (副本)`, cloneFrom: cueId, crossfadeSeconds: src.crossfadeSeconds });
      await refreshCues();
    } catch (e) { toast.error('複製失敗：' + msg(e)); }
  }
  async function handleResetCue(cueId: string, name: string) {
    if (!projectId || !selectedSongId) return;
    if (libMode && lookDraft.hasChanges) { toast.info('這個 cue 還有沒存的改動，請先另存、更新或放棄'); return; }
    if (!(await confirmDialog({ title: `重置 cue「${name}」？`, message: '這個 cue 自己設的物件位置和機關位置都會清掉，改回沿用上一個 cue。', confirmLabel: '重置', destructive: true }))) return;
    try {
      await api.resetCue(projectId, selectedSongId, cueId);
      await refreshCueStates();
      await mech.refreshPositions();
    } catch (e) { toast.error('重置失敗：' + msg(e)); }
  }
  // 「所有 cue 的物件回到預設位置」（Cue 分頁的「⋯」）：先數給製作看會清掉多少，確認以後一次清掉，記成復原紀錄的一步（lib/cueResetAll.ts）
  const resetAllBusyRef = useRef(false);
  async function handleResetAllCueObjects() {
    if (libMode && lookDraft.hasChanges) { toast.info('這個 cue 還有沒存的改動，請先另存、更新或放棄'); return; }
    if (!projectId || !selectedSongId || resetAllBusyRef.current) return;
    if (cuesSongId !== selectedSongId) { toast.warn('這首歌的 cue 還在載入，等一下再試。'); return; }
    const pid = projectId, songId = selectedSongId;
    resetAllBusyRef.current = true;
    try {
      let info: Awaited<ReturnType<typeof api.listSongCueOverrides>>;
      try { info = await api.listSongCueOverrides(pid, songId); }
      catch (e) { toast.error('讀不到這首歌的 cue 設定：' + msg(e)); return; }
      const summary = summarizeOverrides(info.cueCount, info.states);
      if (summary.overrides === 0) { toast.info(RESET_ALL_NOTHING); return; }
      const shared = libMode ? sharedLibraryCueCount(songId, cues, libCuesRef.current) : 0;
      if (!(await confirmDialog(resetAllDialog(summary, shared)))) return;
      const scopes = resetAllHistoryScopes(historyScopeRef.current, cues.map(c => c.id));
      const removed = await runEdit(() => runResetAll({
        clear: () => api.clearSongCueOverrides(pid, songId),
        restore: (states) => api.restoreSongCueOverrides(pid, songId, states),
        history: historyRef.current,
        scopes,
      }), 'states');
      bumpHistory();
      toast.success(removed > 0 ? `已清掉 ${removed} 筆物件設定，這首歌所有 cue 的物件都回到預設位置。按 Ctrl+Z 可以一次還原。` : RESET_ALL_NOTHING);
    } catch (e) {
      toast.error('沒有清掉：' + msg(e));
    } finally {
      resetAllBusyRef.current = false;
    }
  }
  async function handleRenameCue(cueId: string, currentName: string) {
    if (!projectId || !selectedSongId) return;
    const name = (await promptDialog({ title: '改 cue 名稱', label: 'cue 名稱', defaultValue: currentName, confirmLabel: '改名', maxLength: 100 }))?.trim();
    if (!name || name === currentName) return;
    try {
      await api.updateCue(projectId, selectedSongId, cueId, { name });
      await refreshCues();
    } catch (e) { toast.error('改名失敗：' + msg(e)); }
  }
  async function handleDeleteCue(cueId: string, name: string) {
    if (!projectId || !selectedSongId) return;
    if (libMode && cueId === selectedCueId && lookDraft.hasChanges) { toast.info('這個 cue 還有沒存的改動，請先另存、更新或放棄'); return; }
    if (!(await confirmDialog({ title: `刪除 cue「${name}」？`, message: '刪了就救不回來。', confirmLabel: '刪除', destructive: true }))) return;
    try {
      await api.deleteCue(projectId, selectedSongId, cueId);
      historyRef.current.clear(historyScope(cueId));
      if (selectedCueId === cueId) setSelectedCueId(null);
      await refreshCues();
    } catch (e) { toast.error('刪除失敗：' + msg(e)); }
  }
  /** 拖拉改順序（有時間的 cue 時間重新分配，順序永遠照時間）。一次拖拉 = 一個復原步驟 */
  async function dragMoveCue(fromId: string, toId: string) {
    if (!projectId || !selectedSongId) return;
    const pid = projectId, sid = selectedSongId;
    const r = dragReorder(masterCues, fromId, toId);
    if (!r) return;
    const beforeList = [...masterCues].sort((a, b) => a.order - b.order);
    const before = { ids: beforeList.map(c => c.id), times: Object.fromEntries(beforeList.map(c => [c.id, c.videoTimeSec])) as Record<string, number | null> };
    const scope = historyScope(selectedCueId);
    try {
      await api.rearrangeCues(pid, sid, r.ids);
      historyRef.current.push(scope, {
        undo: async () => { await api.rearrangeCues(pid, sid, before.ids, before.times); await refreshCues(); },
        redo: async () => { await api.rearrangeCues(pid, sid, r.ids, r.times); await refreshCues(); },
      });
      bumpHistory();
      await refreshCues();
    } catch (e) { toast.error('順序沒有存到：' + msg(e)); await refreshCues(); }
  }
  /** 鍵盤上下鍵：跟最近一個同一種（有時間 / 沒時間）的 cue 換 */
  async function stepCue(cueId: string, direction: -1 | 1) {
    const to = stepTarget(masterCues, cueId, direction);
    if (to) await dragMoveCue(cueId, to);
  }
  async function moveCue(cueId: string, direction: -1 | 1) {
    if (!projectId || !selectedSongId) return;
    // 隔壁就是同一種（都有時間 / 都沒時間）：走拖拉那條（可復原、時間跟著重新分配）；不一樣才用舊的單純對調
    const nb = masterCues[masterCues.findIndex(c => c.id === cueId) + direction];
    if (nb && stepTarget(masterCues, cueId, direction) === nb.id) { await dragMoveCue(cueId, nb.id); return; }
    const list = masterCues;
    const idx = list.findIndex(c => c.id === cueId);
    const newIdx = idx + direction;
    if (idx < 0 || newIdx < 0 || newIdx >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await api.reorderCues(projectId, selectedSongId, reordered.map(c => c.id));
      await refreshCues();
    } catch (e) { toast.error('順序沒有存到：' + msg(e)); }
  }
  async function handleUpdateCueMeta(patch: Partial<Pick<Cue, 'name' | 'crossfadeSeconds' | 'videoTimeSec'>>) {
    if (!projectId || !selectedSongId || !selectedCueId) return;
    try {
      await api.updateCue(projectId, selectedSongId, selectedCueId, patch);
      await refreshCues();
    } catch (e) { toast.error('cue 沒有存到：' + msg(e)); }
  }

  // ── 音樂（沒有影片的歌，用音樂當歌的時間）──
  // 製作可以上傳 / 換 / 移除；有音樂、沒影片時，3D 下面有一條播放列，機關時間軸的播放頭和 3D 的機關跟著音樂走；
  // 邊聽邊按：把選中的 cue 的「對應影片時間」設成音樂現在的時間（取到 0.1 秒，可以復原）。規則在 lib/songMusic.ts
  const songMusic = useSongMusic({ projectId, song: selectedSong, hasVideo: !!selectedSong?.latestVideoId, videosLoaded: true });
  const musicClock = useMusicTimelineClock(songMusic.player, songMusic.musicMode);
  // 歌曲長度（有影片 / 音樂用它們的，沒有才用自己設的）和起始位置的存檔（跟導演頁同一份；編輯頁沒有 Ctrl+Z 的清單，退回只在導演頁）
  const songLengthNow = songLengthInfo({
    musicSec: songMusic.musicMode ? (songMusic.player.duration > 0 ? songMusic.player.duration : selectedSong?.music?.durationSec ?? null) : null,
    customSec: selectedSong?.lengthSec ?? null,
    hasVideo: !!selectedSong?.latestVideoId,
    hasMusic: songMusic.musicMode,
  });
  const timingOpsStack = useMemo(() => new OpsUndoStack(), []);
  const reloadSongTiming = useCallback(async () => { await Promise.all([refreshSongs(), mech.refreshPositions()]); }, [refreshSongs, mech.refreshPositions]);
  const timingOps = useCueOps({ projectId: projectId || '', songId: selectedSongId || '', ops: timingOpsStack, reload: reloadSongTiming, guard: (fn) => fn });
  // 機關時間軸直接拖（所有分頁都一樣）：色塊、key、cue 編號、轉場條、歌曲長度；放開就存、Ctrl+Z 可以退（lib/mechanisms/useEditorTimelineEdit.ts）
  const reloadTimelineEdit = useCallback(async () => { await Promise.all([refreshCues(), refreshSongs(), mech.refreshPositions()]); }, [refreshCues, refreshSongs, mech.refreshPositions]);
  const timelineEdit = useEditorTimelineEdit({
    enabled: editorRole === 'admin', projectId, songId: selectedSongId, libMode, cues, selectedCueId, previewing: !!libPreviewId,
    mech, length: songLengthNow, customLengthSec: selectedSong?.lengthSec ?? null,
    history: historyRef.current, bumpHistory, reload: reloadTimelineEdit, onGestureStart: endTimePreviewNow,
  });
  const { stop: stopMusic } = useMusicPreviewFeed({ enabled: songMusic.musicMode, video: songMusic.player.video, setPreviewTime: mech.setPreviewTime, resetKey: songMusic.summary?.id ?? null });
  endPreviewRef.current = () => {
    endTimePreview({
      previewTimeRef: mech.previewTimeRef, setPreviewTime: mech.setPreviewTime,
      stopMusic: songMusic.musicMode ? stopMusic : null, bumpStop: () => setTimePreviewStop(n => n + 1),
    });
  };
  // 換 cue、換歌、換看的 Cue 庫 cue：預覽結束
  useEffect(() => { endTimePreviewNow(); }, [selectedCueId, selectedSongId, libPreviewId, libId, endTimePreviewNow]);
  // 上方列放不下時一級一級收（1 次要按鈕只留圖示 / 2 全部只留圖示 / 3 收進「⋯」），CSS 在 ProjectEditor.css
  const topbarRef = useRef<HTMLElement>(null);
  useFitLevel(topbarRef, 3);
  const [setNowBusy, setSetNowBusy] = useState(false);
  const cueTitle = (c: Cue) => (c.libraryCue ? `Cue ${c.libraryCue.label}「${c.libraryCue.name}」` : `「${c.name}」`);
  const canSetNow = canSetCueTimeFromMusic(editorRole) && songMusic.musicMode;
  async function setCueToMusicNow() {
    const cue = selectedCue;
    if (!canSetNow || !projectId || !selectedSongId) return;
    // 快捷鍵 M 不像按鈕會灰掉，這裡自己擋：沒選 cue、提案的 cue、音樂還沒載好（讀到的時間是 0，會把 cue 的時間蓋成 0:00.0）
    const blocked = setNowBlocker({ cue, playerReady: songMusic.player.ready });
    if (blocked || !cue) { if (blocked) toast.info(blocked); return; }
    const t = cueTimeFromMusic(songMusic.player.video.currentTime);
    const before = cue.videoTimeSec;
    if (!cueTimeChanged(before, t)) { toast.info(`${cueTitle(cue)} 本來就設在 ${formatMusicClock(t)}`); return; }
    const pid = projectId, sid = selectedSongId, scope = historyScope(selectedCueId);
    setSetNowBusy(true);
    try {
      await api.updateCue(pid, sid, cue.id, { videoTimeSec: t });
      historyRef.current.push(scope, {
        undo: async () => { await api.updateCue(pid, sid, cue.id, { videoTimeSec: before }); await refreshCues(); },
        redo: async () => { await api.updateCue(pid, sid, cue.id, { videoTimeSec: t }); await refreshCues(); },
      });
      bumpHistory();
      await refreshCues();
      toast.success(setNowToast(cueTitle(cue), t) + '（可以按「復原」改回來）');
    } catch (e) { toast.error('cue 的時間沒有存到：' + msg(e)); }
    finally { setSetNowBusy(false); }
  }
  const setNowRef = useRef(setCueToMusicNow);
  setNowRef.current = setCueToMusicNow;
  useMusicHotkeys({
    enabled: songMusic.musicMode,
    onToggle: songMusic.player.togglePlay,
    onMark: canSetNow ? () => { void setNowRef.current(); } : null,
  });
  const musicCtx = useMemo<EditorMusicValue>(() => ({
    clock: musicClock,
    setNow: canSetNow ? { cueLabel: selectedCue && selectedCue.status === 'master' ? cueTitle(selectedCue) : null, busy: setNowBusy, run: () => { void setNowRef.current(); } } : null,
  }), [musicClock, canSetNow, selectedCue, setNowBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cue 庫：還沒存的改動 → 另存 / 更新 / 放棄 / 換 cue 時的提醒 ──
  const refreshLibCues = useCallback(async () => {
    if (!projectId || !libMode) { setLibCues([]); return; }
    try { setLibCues(await cueLib.listLibraryCues(projectId)); } catch (e) { console.warn('load library cues failed', e); }
    setLibLoading(false);
  }, [projectId, libMode]);
  useEffect(() => { void refreshLibCues(); }, [refreshLibCues, libId]);
  const currentLib = libCues.find(c => c.id === libId) ?? null;

  /** 寫進後台之後：畫面 / 機關 / 清單都重新讀一次，讀到才清草稿（不然畫面會先閃回舊的）；中間又改的不清 */
  async function afterLookWrite(saved: LookDraft | null | undefined) {
    const results = await Promise.allSettled([refreshCues(), refreshCueStates(), mechRef.current.refreshPositions(), refreshLibCues(), lookDraftRef.current?.loadBase()]);
    const ld = lookDraftRef.current;
    if (ld && ld.getDraft() === (saved ?? null)) ld.clear();
    if (results.some(r => r.status === 'rejected')) toast.warn('已經存好了，但畫面沒有更新，請重新整理');
  }
  function dismissSaveToast() {
    if (saveToastRef.current) toast.dismiss(saveToastRef.current);
    saveToastRef.current = null;
  }
  async function currentBaseLook() {
    const ld = lookDraftRef.current;
    const b = ld?.base ?? await ld?.loadBase() ?? null;
    return b && ld ? { rev: b.rev, look: { objects: b.objects, mechanisms: b.mechanisms } } : null;
  }

  /** 回 null = 存好了；回文字 = 要顯示在對話框裡的錯誤 */
  async function doSaveAs(label: string, name: string): Promise<string | null> {
    endTimePreviewNow();
    if (!projectId || !libId || (!libPreviewId && (!selectedSongId || !selectedCueId))) return '沒有選到 cue';
    const b = await currentBaseLook();
    if (!b) return '這個 cue 的畫面讀不到，請重新整理';
    const d = lookDraftRef.current?.getDraft();
    const look = d ? applyDraftToLook(b.look, d) : b.look;
    try {
      // 預覽中（這首歌沒有用到它）：只存進 Cue 庫，不動這首歌
      const place = libPreviewId || !selectedSongId || !selectedCueId ? undefined : { songId: selectedSongId, replaceCueId: selectedCueId };
      const r = await cueLib.createLibraryCue(projectId, { label, name, basedOnId: libId, look, ...(place ? { place } : {}) });
      toast.success(`已另存成 Cue ${r.libraryCue.label}「${r.libraryCue.name}」`);
      await afterLookWrite(d);
      return null;
    } catch (e) { return msg(e); }
  }

  /** true = 更新好了。被別人改過（409）→ 草稿留著 */
  async function doUpdate(): Promise<boolean> {
    endTimePreviewNow();
    if (!projectId || !libId || !sceneCueId) return false;
    const b = await currentBaseLook();
    const d = lookDraftRef.current?.getDraft();
    if (!b) { toast.error('這個 cue 的畫面讀不到，請重新整理'); return false; }
    if (!d) return false;
    const look = applyDraftToLook(b.look, d);
    const pid = projectId, lid = libId, scope = historyScope(sceneCueId);
    setDraftBusy(true);
    try {
      const r = await cueLib.putLibraryLook(pid, lid, look, b.rev);
      dismissSaveToast();
      toast.success(`已覆蓋 Cue ${currentLib?.label ?? ''}`);
      // 更新是一步：復原 = 把換掉之前的畫面寫回去（共用這個 cue 的每首歌都會跟著變）
      let rev = r.rev;
      const reload = async () => {
        await Promise.all([refreshCueStatesRef.current(), mechRef.current.refreshPositions(), refreshLibCues(), lookDraftRef.current?.loadBase()]);
      };
      historyRef.current.push(scope, {
        undo: async () => { rev = (await cueLib.putLibraryLook(pid, lid, r.previous, rev)).rev; await reload(); },
        redo: async () => { rev = (await cueLib.putLibraryLook(pid, lid, look, rev)).rev; await reload(); },
      });
      bumpHistory();
      await afterLookWrite(d);
      return true;
    } catch (e) {
      if ((e as cueLib.CueLibraryApiError).status === 409) toast.error('這個 cue 剛被別人改過，請重新整理後再改');
      else toast.error('更新沒有存到：' + msg(e));
      return false;
    } finally { setDraftBusy(false); }
  }

  /** 按「更新」：有別的 cue 也在用 → 先列出會影響的歌再問 */
  async function startUpdate(after?: () => void) {
    if (!projectId || !libId || lookSavingRef.current) return;
    lookSavingRef.current = true;
    setDraftBusy(true);
    saveToastRef.current = toast.push('info', `正在存 Cue ${currentLib?.label ?? ''}…`, 0);
    try {
      let usage: cueLib.LibraryCueUsage;
      try { usage = await cueLib.getLibraryCueUsage(projectId, libId); } catch (e) { toast.error('讀不到誰在用這個 cue：' + msg(e)); return; }
      if (usage.slotCount > 1) { setUpdateOpen({ usage, after }); return; }
      if (await doUpdate()) after?.();
    } finally {
      dismissSaveToast();
      lookSavingRef.current = false;
      setDraftBusy(false);
    }
  }

  /** 換 cue / 換歌 / 離開之前：有沒存的改動就先問（規格第 10 節） */
  async function guardLeave(action: () => void) {
    if (lookSavingRef.current) { toast.info('還在存這個 cue，存好再換'); return; }
    const ld = lookDraftRef.current;
    if (!libModeRef.current || !ld || !ld.hasChanges) { action(); return; }
    const lib = libCuesRef.current.find(c => c.id === libIdRef.current);
    const label = lib?.label ?? '';
    const shared = lib && lib.usage.songCount > 1 ? `會影響 ${lib.usage.songCount} 首歌：${usageSongNames(lib.usage).join('、')}。` : '';
    const ans = await chooseDialog({
      title: '這個 cue 動到了，要另存成新的 cue 嗎？',
      message: `Cue ${label}${lib ? `「${lib.name}」` : ''}的改動還沒存。${shared}`,
      choices: [
        { value: 'saveas', label: '另存成新的 cue', primary: true },
        { value: 'update', label: `覆蓋 Cue ${label}` },
        { value: 'discard', label: '放棄改動' },
      ],
    });
    if (ans === 'saveas') setSaveAsOpen({ after: action });
    else if (ans === 'update') void startUpdate(action);
    else if (ans === 'discard') { ld.clear(); action(); }
  }
  guardRef.current = (a) => { void guardLeave(a); };
  const selectCue = (id: string | null) => { if (id !== selectedCueId || libPreviewId) guardRef.current(() => { setLibPreviewId(null); setSelectedCueId(id); }); };
  // 換歌：歌單裡展開的那一首留在看得到的範圍（上面的歌收起來、列表會往上縮，不要讓選到的跑出畫面）
  useEffect(() => {
    if (!selectedSongId) return;
    const id = window.requestAnimationFrame(() => {
      const el = Array.from(document.querySelectorAll<HTMLElement>('.song-list [data-song-id]')).find(n => n.dataset.songId === selectedSongId);
      el?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedSongId]);
  const selectSong = (id: string | null) => { if (id !== selectedSongId) guardRef.current(() => { setLibPreviewId(null); setSelectedSongId(id); }); };

  // ── Cue 庫分頁 ──
  /** 點 Cue 庫的一個 cue：這首歌有 cue 指到它 → 選那個 cue；沒有 → 只在 3D 預覽（不改任何歌） */
  const selectLib = (lcId: string) => {
    if (lcId === libIdRef.current) return;
    guardRef.current(() => {
      const slot = selectedSongId ? masterCues.find(c => c.libraryCueId === lcId) : undefined;
      if (slot) { setLibPreviewId(null); setSelectedCueId(slot.id); } else setLibPreviewId(lcId);
    });
  };
  selectLibRef.current = selectLib;
  // 點「誰在用」的連結：跳去那首歌的那個 cue（歌換了要等 cue 清單讀到才選得到）
  const [jumpCueId, setJumpCueId] = useState<string | null>(null);
  useEffect(() => {
    if (!jumpCueId || cuesSongId !== selectedSongId) return;
    if (cues.some(c => c.id === jumpCueId)) setSelectedCueId(jumpCueId);
    setJumpCueId(null);
  }, [jumpCueId, cues, cuesSongId, selectedSongId]);
  const jumpToSlot = (songId: string, cueId: string) => {
    guardRef.current(() => {
      setLibPreviewId(null);
      setRightTab('cues');
      if (songId === selectedSongId) setSelectedCueId(cueId);
      else { setJumpCueId(cueId); setSelectedSongId(songId); }
    });
  };
  // ── 這首歌的 cue（Cue 庫專案）：加 / 換 / 改時間 / 拿掉 / 複製成新的 cue ──
  const [addSlotOpen, setAddSlotOpen] = useState<{ libraryCueId?: string } | null>(null);
  const [swapSlot, setSwapSlot] = useState<Cue | null>(null);
  const [copySlot, setCopySlot] = useState<Cue | null>(null);
  const [slotBusy, setSlotBusy] = useState(false);
  const DRAFT_BLOCK = '這個 cue 還有沒存的改動，請先另存、更新或放棄';
  async function afterSlotWrite() {
    await Promise.all([refreshCues(), refreshLibCues(), refreshCueStates(), mechRef.current.refreshPositions()]);
  }
  // ── 機關時間放開就存（Cue 庫專案）──
  // 換到另一個 Cue 庫的 cue（只改這首 / 復原）時，還沒存的改動（草稿）跟著過去，不丟掉
  const carryDraftRef = useRef<{ to: string; draft: LookDraft } | null>(null);
  useEffect(() => {
    const c = carryDraftRef.current;
    if (!c || c.to !== libId) return;
    carryDraftRef.current = null;
    lookDraftRef.current?.adopt({ ...c.draft, libId: c.to });
  }, [libId]);
  const carryDraft = (to: string, items: LibTimingSave['items']) => {
    const d = lookDraftRef.current?.getDraft();
    carryDraftRef.current = d && draftHasOps(d) ? { to, draft: syncDraftTimings(d, items) } : null;
  };
  async function saveLibTimings(req: LibTimingSave): Promise<LibTimingStep | null> {
    const pid = projectId, sid = selectedSongId;
    const slot = cues.find(c => c.id === req.cueId);
    if (!pid || !sid || !slot) return null;
    const oldLib = slot.libraryCueId;
    const fail = (e: unknown) => { toast.error('機關的時間沒有存到：' + msg(e)); return null; };
    // 別首歌也用這個 cue → 放開才問一次
    let scope: TimingScope = 'all';
    if (oldLib) {
      let usage: cueLib.LibraryCueUsage;
      try { usage = await cueLib.getLibraryCueUsage(pid, oldLib); } catch (e) { return fail(e); }
      if (usage.slotCount > 1) {
        const a = await new Promise<TimingScope | null>(resolve => setScopeAsk({ songCount: usage.songCount, resolve }));
        setScopeAsk(null);
        if (!a) return null;
        scope = a;
      }
    }
    const reloadPositions = async () => { await Promise.all([mechRef.current.refreshPositions(), refreshLibCues(), lookDraftRef.current?.loadBase()]); };
    if (scope === 'all' || !oldLib) {
      const write = async (list: LibTimingSave['items']) => {
        await setCuePositionsBulk(pid, sid, req.cueId, list);
        lookDraftRef.current?.syncTimings(list);
        await reloadPositions();
      };
      try { await write(req.items); } catch (e) { return fail(e); }
      return { undo: () => write(req.back), redo: () => write(req.items) };
    }
    // 只改這首：存著的畫面 + 新的時間 → 另存成新的 cue，這首歌的這個 cue 換過去；別首歌不動
    const place = { songId: sid, replaceCueId: req.cueId };
    const create = async () => {
      const base = await cueLib.getLibraryLook(pid, oldLib);
      const look = applyTimingItems({ objects: base.objects, mechanisms: base.mechanisms }, req.items);
      return (await cueLib.createLibraryCue(pid, { basedOnId: oldLib, look, place })).libraryCue;
    };
    let created: cueLib.LibraryCue;
    try { created = await create(); } catch (e) { return fail(e); }
    let cur = created.id;
    carryDraft(cur, req.items);
    toast.success(`已另存成 Cue ${created.label}「${created.name}」，只有這首歌換過去`);
    await afterSlotWrite(); await lookDraftRef.current?.loadBase();
    return {
      undo: async () => {
        carryDraft(oldLib, req.back);
        await cueLib.updateSongSlot(pid, sid, req.cueId, { libraryCueId: oldLib });
        try { await cueLib.deleteLibraryCue(pid, cur); } catch { /* 有別人用、刪不掉就留著 */ }
        await afterSlotWrite(); await lookDraftRef.current?.loadBase();
      },
      redo: async () => {
        try {
          carryDraft(cur, req.items);
          await cueLib.updateSongSlot(pid, sid, req.cueId, { libraryCueId: cur });
        } catch {
          // 新 cue 已經在復原時收掉了 → 重新另存一個
          const c = await create();
          cur = c.id;
          carryDraft(cur, req.items);
        }
        await afterSlotWrite(); await lookDraftRef.current?.loadBase();
      },
    };
  }
  libTimingSaveRef.current = saveLibTimings;

  async function addSlot(v: { libraryCueId: string; videoTimeSec: number | null; crossfadeSeconds?: number }): Promise<string | null> {
    if (!projectId || !selectedSongId) return '先選一首歌';
    try {
      const r = await cueLib.addSongSlot(projectId, selectedSongId, v);
      await afterSlotWrite();
      if (!lookDraftRef.current?.hasChanges) { setLibPreviewId(null); setSelectedCueId(r.slot.id); }
      toast.success('已加進這首歌');
      return null;
    } catch (e) { return msg(e); }
  }
  async function swapSlotTo(cue: Cue, v: { libraryCueId: string }): Promise<string | null> {
    if (!projectId || !selectedSongId) return '先選一首歌';
    if (cue.id === selectedCueId && lookDraftRef.current?.hasChanges) return DRAFT_BLOCK;
    try {
      await cueLib.updateSongSlot(projectId, selectedSongId, cue.id, { libraryCueId: v.libraryCueId });
      await afterSlotWrite();
      return null;
    } catch (e) { return msg(e); }
  }
  async function changeSlotTime(cue: Cue, sec: number | null): Promise<string | null> {
    if (!projectId || !selectedSongId) return '先選一首歌';
    try {
      await cueLib.updateSongSlot(projectId, selectedSongId, cue.id, { videoTimeSec: sec });
      await refreshCues();
      return null;
    } catch (e) { return msg(e); }
  }
  async function changeSlotFade(cue: Cue, sec: number | null): Promise<string | null> {
    if (!projectId || !selectedSongId) return '先選一首歌';
    try {
      await cueLib.updateSongSlot(projectId, selectedSongId, cue.id, { crossfadeSeconds: sec });
      await refreshCues();
      if (sec === null) toast.success('這個 cue 的轉場改回用 Cue 庫的');
      return null;
    } catch (e) { return msg(e); }
  }
  async function removeSlot(cue: Cue) {
    if (!projectId || !selectedSongId) return;
    if (cue.id === selectedCueId && lookDraft.hasChanges) { toast.info(DRAFT_BLOCK); return; }
    const lc = cue.libraryCue;
    const led = ledSwitchFollows(parseSwitches(selectedSong?.templateSwitches), cue.id);
    const when = cue.videoTimeSec !== null ? fmtSlotTime(cue.videoTimeSec) : '這個 cue';
    const ledText = led ? `${when} 有 LED 版型跟著這個 cue 換，拿掉之後那個換版型的點也會一起拿掉。` : '';
    if (!(await confirmDialog({
      title: lc ? `從這首歌拿掉 Cue ${lc.label}「${lc.name}」？` : '從這首歌拿掉這個 cue？',
      message: `只是從這首歌的清單拿掉，Cue 庫裡的 cue 還在，別的歌也不受影響。${ledText ? ' ' + ledText : ''}`,
      confirmLabel: '拿掉', destructive: true,
    }))) return;
    setSlotBusy(true);
    try {
      await cueLib.removeSongSlot(projectId, selectedSongId, cue.id);
      historyRef.current.clear(historyScope(cue.id));
      if (selectedCueId === cue.id) setSelectedCueId(null);
      await Promise.all([afterSlotWrite(), refreshSongs()]);
    } catch (e) { toast.error('沒有拿掉：' + msg(e)); }
    finally { setSlotBusy(false); }
  }
  async function resortSlots() {
    if (!projectId || !selectedSongId) return;
    setSlotBusy(true);
    try {
      await api.reorderCues(projectId, selectedSongId, sortedSlotIds(masterCues));
      await refreshCues();
    } catch (e) { toast.error('沒有排好：' + msg(e)); }
    finally { setSlotBusy(false); }
  }
  const showInLibrary = (lcId: string) => { setRightTab('library'); selectLib(lcId); };

  const [copyOf, setCopyOf] = useState<cueLib.LibraryCue | null>(null);
  const [libBusy, setLibBusy] = useState(false);
  async function relabelOrRename(lc: cueLib.LibraryCue, patch: { name?: string; label?: string; crossfadeSeconds?: number }): Promise<string | null> {
    if (!projectId) return '沒有專案';
    try {
      await cueLib.updateLibraryCue(projectId, lc.id, patch);
      await Promise.all([refreshLibCues(), refreshCues()]);
      return null;
    } catch (e) { return msg(e); }
  }
  async function deleteLib(lc: cueLib.LibraryCue) {
    if (!projectId) return;
    const blocked = async (usage: cueLib.LibraryCueUsage) => {
      await chooseDialog({
        title: `Cue ${lc.label}「${lc.name}」還有歌在用，不能刪`,
        message: `${deleteBlockedText(usage)}。`,
        choices: [{ value: 'ok', label: '知道了', primary: true }],
      });
    };
    if (lc.usage.slotCount > 0) { await blocked(lc.usage); return; }
    if (lc.id === libId && lookDraft.hasChanges) { toast.info('這個 cue 還有沒存的改動，請先另存、更新或放棄'); return; }
    if (!(await confirmDialog({ title: `刪除 Cue ${lc.label}「${lc.name}」？`, message: '這個沒辦法復原。', confirmLabel: '刪除', destructive: true }))) return;
    setLibBusy(true);
    try {
      await cueLib.deleteLibraryCue(projectId, lc.id);
      if (lc.id === libPreviewId) setLibPreviewId(null);
      toast.success(`已刪除 Cue ${lc.label}`);
      await refreshLibCues();
    } catch (e) {
      const err = e as cueLib.CueLibraryApiError;
      if (err.status === 409 && err.data?.usage) { await blocked(err.data.usage); await refreshLibCues(); }
      else toast.error('刪除失敗：' + msg(e));
    } finally { setLibBusy(false); }
  }
  async function resetAllLib(lc: cueLib.LibraryCue) {
    if (!projectId) return;
    if (lc.id === libId && lookDraft.hasChanges) { toast.info('這個 cue 還有沒存的改動，請先另存、更新或放棄'); return; }
    const shared = lc.usage.songCount > 1 ? `用到它的 ${lc.usage.songCount} 首歌都會一起變。` : lc.usage.songCount === 1 ? '用到它的那首歌會跟著變。' : '';
    if (!(await confirmDialog({ title: `把 Cue ${lc.label}「${lc.name}」全部回到預設？`, message: `所有物件和機關都會回到預設位置。${shared}`, confirmLabel: '全部回到預設', destructive: true }))) return;
    setLibBusy(true);
    try {
      const cur = await cueLib.getLibraryLook(projectId, lc.id);
      await cueLib.putLibraryLook(projectId, lc.id, { objects: [], mechanisms: [] }, cur.rev);
      toast.success(`Cue ${lc.label} 已全部回到預設`);
      await Promise.all([refreshLibCues(), refreshCueStates(), mechRef.current.refreshPositions(), lookDraftRef.current?.loadBase()]);
    } catch (e) {
      if ((e as cueLib.CueLibraryApiError).status === 409) toast.error('這個 cue 剛被別人改過，請重新整理後再改');
      else toast.error('沒有成功：' + msg(e));
    } finally { setLibBusy(false); }
  }
  // 關分頁 / 重新整理：有沒存的改動就讓瀏覽器問一下
  useEffect(() => {
    if (!libMode || !lookDraft.hasChanges) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [libMode, lookDraft.hasChanges]);

  // ── Cue state (per object override) actions ──
  // 這個 cue 目前的物件狀態：畫面上那份就是這個 cue 的 → 直接用；不是（還在讀）→ 去後台拿
  async function loadCueState(pid: string, songId: string, cueId: string, objId: string): Promise<CueState | null> {
    if (cueStatesForRef.current === cueId) return cueStatesRef.current.find(s => s.objectId === objId) ?? null;
    const list = await api.listCueStates(pid, songId, cueId);
    return list.find(s => s.objectId === objId) ?? null;
  }
  // 存成功後先套到畫面上（排隊的修改全部做完會再重新讀一次）；已經換到別的 cue 就不套
  function applyCueStateLocal(cueId: string, objId: string, patch: CueStatePatch) {
    if (cueStatesForRef.current !== cueId) return;
    commitCueStates(cueId, cueStatesRef.current.map(s => (s.objectId === objId ? applyCueStatePatch(s, patch) : s)));
  }
  function applyStageObjectLocal(objId: string, patch: Partial<StageObject>) {
    commitStageObjects(stageObjectsRef.current.map(o => (o.id === objId ? { ...o, ...patch } : o)));
  }

  function handleSetState(objId: string, input: StatePatchInput): Promise<void> {
    endTimePreviewNow();
    if (!projectId) return Promise.resolve();
    const pid = projectId;
    const songId = selectedSongId;
    const cueId = sceneCueId;
    const scope = historyScope(cueId);
    const resolvePatch = (cur: CueState['effective']) => (typeof input === 'function' ? input(cur) : input);

    // 沒選 cue → 改物件預設位置（影響所有沒自己設定的 cue）
    if (!cueId) {
      return runEdit(async () => {
        const obj = stageObjectsRef.current.find(o => o.id === objId);
        if (!obj) return;
        const patch = resolvePatch({ position: obj.defaultPosition, rotation: obj.defaultRotation, scale: obj.defaultScale, visible: defaultVisible(obj) });
        const after: Partial<StageObject> = {};
        const before: Partial<StageObject> = {};
        if (patch.position) { after.defaultPosition = patch.position; before.defaultPosition = obj.defaultPosition; }
        if (patch.rotation) { after.defaultRotation = patch.rotation; before.defaultRotation = obj.defaultRotation; }
        if (Object.keys(after).length === 0) return;  // 預設位置沒有「顯示 / 隱藏」
        await api.updateStageObject(pid, objId, after);
        applyStageObjectLocal(objId, after);
        historyRef.current.push(scope, {
          undo: async () => { await api.updateStageObject(pid, objId, before); applyStageObjectLocal(objId, before); },
          redo: async () => { await api.updateStageObject(pid, objId, after); applyStageObjectLocal(objId, after); },
        });
        bumpHistory();
      }, 'objects');
    }
    // Cue 庫專案：改的是「還沒存的改動」，不寫後台
    if (libMode && libId) {
      if (cueStatesForRef.current !== cueId) return Promise.resolve();
      const shown = applyDraftToStates(cueStatesRef.current, lookDraftRef.current?.getDraft());
      const cur = shown.find(x => x.objectId === objId);
      if (!cur) return Promise.resolve();
      const patch = resolvePatch(cur.effective);
      if (Object.keys(patch).length > 0) lookDraft.setObject(objId, patch);
      return Promise.resolve();
    }
    if (!songId || !cueId) return Promise.resolve();
    return runEdit(async () => {
      const cur = await loadCueState(pid, songId, cueId, objId);
      if (!cur) return;
      const patch = resolvePatch(cur.effective);
      const changed = Object.keys(patch) as CueStateField[];
      if (changed.length === 0) return;
      const before = cur.override;  // 改之前這個 cue 自己的設定（復原用）
      await api.setCueState(pid, songId, cueId, objId, patch);
      applyCueStateLocal(cueId, objId, patch);
      historyRef.current.push(scope, {
        undo: async () => {
          const plan = cueStateRestorePlan(before, changed);
          if (plan.reset) await api.resetCueState(pid, songId, cueId, objId);
          if (plan.put) await api.setCueState(pid, songId, cueId, objId, plan.put);
        },
        redo: async () => {
          await api.setCueState(pid, songId, cueId, objId, patch);
          applyCueStateLocal(cueId, objId, patch);
        },
      });
      bumpHistory();
    }, 'states');
  }
  /** 這個 cue 裡顯示 / 隱藏這個物件（排隊輪到時才看現在是顯示還是隱藏，連按兩下也對） */
  function handleToggleVisible(objId: string) {
    handleSetState(objId, cur => ({ visible: !cur.visible }))
      .catch(e => toast.error('顯示 / 隱藏沒有存到：' + msg(e)));
  }
  /** askFirst = false：多選一起重置時，外面已經問過一次了 */
  async function handleResetState(objId: string, askFirst = true) {
    if (!projectId || (!libPreviewId && (!selectedSongId || !selectedCueId))) return;
    if (askFirst && !(await confirmDialog({ title: '把這個物件重置回預設位置？', message: '可以按「復原」改回來。', confirmLabel: '重置' }))) return;
    endTimePreviewNow();
    if (libMode && libId) { lookDraft.resetObjectFields(objId); return; }
    if (!selectedSongId || !selectedCueId) return;
    const pid = projectId, songId = selectedSongId, cueId = selectedCueId;
    const scope = historyScope(cueId);
    try {
      await runEdit(async () => {
        const cur = await loadCueState(pid, songId, cueId, objId);
        const restore = overridePatch(cur?.override);
        await api.resetCueState(pid, songId, cueId, objId);
        if (Object.keys(restore).length === 0) return;  // 本來就沒自己的設定 → 沒東西可復原
        historyRef.current.push(scope, {
          undo: async () => { await api.setCueState(pid, songId, cueId, objId, restore); },
          redo: async () => { await api.resetCueState(pid, songId, cueId, objId); },
        });
        bumpHistory();
      }, 'states');
    } catch (e) {
      toast.error('重置失敗：' + msg(e));
    }
  }

  // ── Stage object actions ──
  async function handleSeedObjects() {
    if (!projectId) return;
    if (!(await confirmDialog({ title: '加入 10 個常用物件範例？', message: 'SKY、LED-1～4、樂手 LED、旋轉舞臺、升降台、走位。之後可以改名或刪掉。', confirmLabel: '加入範例' }))) return;
    try {
      const r = await api.seedDefaultStageObjects(projectId);
      toast.success(`已新增 ${r.inserted} 個範例物件`);
      await refreshStageObjects();
      await refreshCueStates();
    } catch (e) { toast.error('範例物件沒有加入：' + msg(e)); }
  }
  async function handleAddObject() {
    if (!projectId) return;
    const meshName = (await promptDialog({ title: '新增物件', label: 'C4D 物件名稱', hint: '要跟 .glb 檔裡的物件名稱一模一樣', confirmLabel: '下一步', maxLength: 200 }))?.trim();
    if (!meshName) return;
    const shown = await promptDialog({ title: '新增物件', label: '畫面上顯示的名稱（可以留空）', defaultValue: meshName, confirmLabel: '新增', required: false, maxLength: 100 });
    if (shown == null) return;
    const displayName = shown.trim() || meshName;
    try {
      await api.createStageObject(projectId, { meshName, displayName, category: 'other' });
      await refreshStageObjects();
      await refreshCueStates();
    } catch (e) { toast.error('物件沒有新增：' + msg(e)); }
  }
  async function handleRenameObject(obj: StageObject) {
    if (!projectId) return;
    const displayName = (await promptDialog({ title: '改物件名稱', label: '畫面上顯示的名稱', defaultValue: obj.displayName, confirmLabel: '改名', maxLength: 100 }))?.trim();
    if (!displayName || displayName === obj.displayName) return;
    try {
      await api.updateStageObject(projectId, obj.id, { displayName });
      await refreshStageObjects();
      await refreshCueStates();
    } catch (e) { toast.error('改名失敗：' + msg(e)); }
  }
  async function handleChangeCategory(obj: StageObject, category: StageObjectCategory) {
    if (!projectId) return;
    try {
      await api.updateStageObject(projectId, obj.id, { category });
      await refreshStageObjects();
      await refreshCueStates();
    } catch (e) { toast.error('分類沒有存到：' + msg(e)); }
  }
  async function handleUpdateMaterial(objId: string, patch: MaterialProps) {
    if (!projectId) return;
    const obj = stageObjects.find(o => o.id === objId);
    if (!obj) return;
    const merged = { ...(obj.materialProps || {}), ...patch };
    try {
      await api.updateStageObject(projectId, objId, { materialProps: merged });
      await refreshStageObjects();
    } catch (e) { toast.error('材質沒有存到：' + msg(e)); }
  }
  async function handleUpdateLed(objId: string, patch: LedProps) {
    if (!projectId) return;
    const obj = stageObjects.find(o => o.id === objId);
    if (!obj) return;
    const merged = { ...(obj.ledProps || {}), ...patch };
    try {
      await api.updateStageObject(projectId, objId, { ledProps: merged });
      await refreshStageObjects();
    } catch (e) { toast.error('LED 設定沒有存到：' + msg(e)); }
  }

  async function handleToggleLock(obj: StageObject) {
    if (!projectId) return;
    try {
      await api.updateStageObject(projectId, obj.id, { locked: !obj.locked });
      // 鎖時取消當前 selection（如果鎖的剛好是 selected）
      if (!obj.locked) setSelectedObjectIds(prev => prev.filter(x => x !== obj.id));
      await refreshStageObjects();
      await refreshCueStates();
    } catch (e) { toast.error((obj.locked ? '沒有解鎖：' : '沒有鎖住：') + msg(e)); }
  }
  /** 物件「預設隱藏」開關（只有製作）：改完物件清單和 3D 畫面（每個 cue 的顯示都可能跟著變）重新讀 */
  async function handleToggleDefaultHidden(obj: StageObject) {
    if (!projectId) return;
    const next = !isDefaultHidden(obj);
    try {
      await api.updateStageObject(projectId, obj.id, { defaultHidden: next });
      await refreshStageObjects();
      await refreshCueStates();
      toast.success(defaultHiddenToast(obj.displayName, next));
    } catch (e) { toast.error(`沒有存到「${DEFAULT_HIDDEN_LABEL}」：` + msg(e)); }
  }
  async function handleDeleteObject(obj: StageObject) {
    if (!projectId) return;
    if (!(await confirmDialog({ title: `刪除物件「${obj.displayName}」？`, message: '所有 cue 裡對它的調整也會一起刪掉，刪了就救不回來。', confirmLabel: '刪除', destructive: true }))) return;
    try {
      await api.deleteStageObject(projectId, obj.id);
      await refreshStageObjects();
      await refreshCueStates();
    } catch (e) { toast.error('刪除失敗：' + msg(e)); }
  }

  // 專案狀態：進行中 ⇄ 審查中（全站製作和這個專案的製作都能切；封存另外走，只有全站製作）
  const statusToggle = editorRole === 'admin' && projectMeta ? reviewToggle(projectMeta.status as ProjectStatusValue) : null;
  async function toggleProjectStatus() {
    if (!projectId || !projectMeta || !statusToggle) return;
    try {
      await api.updateProject(projectId, { status: statusToggle.next });
      await refreshProjectMeta();
      toast.success(statusToggle.doneMessage(projectMeta.name));
    } catch (e) { toast.error('狀態沒有改成：' + msg(e)); }
  }

  // 上方列：直接顯示的只有 復原 / 重做 / 對比 / 分享 / 導演頁 / 動畫師工作站；其他收進兩個下拉
  //   「製作」= 做 cue 的輔助（分鏡、搜留言、匯入 Cue、LED 版型、自動貼 UV、調整排版）
  //   「專案」= 專案本身（成員、Drive、最近活動、用其他身分看、審查中）
  // 手機（640px 以下，或實際量到放不下）整個收進一個「⋯」，裡面一樣分「製作」「專案」兩段，不會同時出現兩個入口。
  const ledTplGone = editorRole !== 'admin' || (ledTpls.loaded && !ledTpls.list);
  const isInReview = projectMeta?.status === 'in_review';
  const directActions: { key: string; label: string; menuLabel: string; title: string; icon: LucideIcon; onSelect: () => void; disabled?: boolean; secondary?: boolean }[] = [
    { key: 'diff', label: '對比', menuLabel: '兩個 cue 對比', title: '兩個 cue 並排對比', icon: Columns2, onSelect: () => setDiffOpen(true), disabled: !selectedSong || masterCues.length < 2, secondary: true },
    { key: 'share', label: '分享', menuLabel: '分享連結', title: '建立公開分享連結（外部的人不用登入就能看，只能看不能改）', icon: Link, onSelect: () => setShareOpen(true) },
    // 直接打開導演頁 / 動畫師工作站：用自己的身分，什麼都能做（不是預覽）；全站製作和這個專案的製作都有
    ...(!canViewAs ? [] : [
      { key: 'openDirector', label: '導演頁', menuLabel: '導演頁（直接操作）', title: '打開導演頁（新分頁）：用自己的身分審片、留言、畫記，可以決定通過 / 需修改', icon: Clapperboard, onSelect: () => { window.open(`/preview/${encodeURIComponent(projectId || '')}`, '_blank', 'noopener'); }, secondary: true },
      { key: 'openStudio', label: '動畫師工作站', menuLabel: '動畫師工作站（直接操作）', title: '打開動畫師工作站（新分頁）：用自己的身分替任何一首歌交影片、刪版本、留言、畫記', icon: Palette, onSelect: () => { window.open(`/studio/${encodeURIComponent(projectId || '')}`, '_blank', 'noopener'); }, secondary: true },
    ]),
  ];
  const makeItems: OverflowItem[] = [
    { key: 'storyboard', label: '分鏡', hint: '分鏡：這首歌所有 cue 的縮圖一覽', icon: LayoutGrid, onSelect: () => setStoryboardOpen(true), disabled: !selectedSong || masterCues.length === 0 },
    { key: 'search', label: '搜留言', hint: '搜尋專案內所有歌的留言', icon: Search, onSelect: () => setCommentSearchOpen(true) },
    { key: 'import', label: '匯入 Cue', hint: '從別首歌匯入 cue（巡迴複製、跨專案沿用）', icon: Import, onSelect: () => setImportCuesOpen(true), disabled: !selectedSong },
    ...(ledTplGone ? [] : [{ key: 'ledtpl', label: 'LED 版型', hint: 'LED 版型：只出幾塊螢幕時，排進一張比較小的畫布', icon: LayoutPanelLeft, active: ledTplOpen || undefined, onSelect: () => { setUvOpen(false); setLedTplOpen(true); } }]),
    ...(ledTplGone || !modelUrl ? [] : [{ key: 'ledUv', label: '自動貼 UV', hint: '自動貼 UV：選 LED 螢幕、填點距或總點數，系統展開 UV、排好畫布、存成新的模型版本', icon: Ruler, active: (uvOpen && uvEntry.mode === 'full') || undefined, onSelect: () => { setLedTplOpen(false); setUvEntry(e => ({ mode: 'full', n: e.n })); setUvOpen(true); } }]),
    ...(ledTplGone || !modelUrl || !uvApplied ? [] : [{ key: 'ledUvAdjust', label: '調整排版', hint: '調整排版：自動貼 UV 套用過了，直接到「排版」那一步，位置是上次套用時的樣子；拖完按「套用」就好，不用重走一遍', icon: LayoutPanelLeft, active: (uvOpen && uvEntry.mode === 'adjust') || undefined, onSelect: () => { setLedTplOpen(false); setUvEntry(e => ({ mode: 'adjust', n: e.n + 1 })); setUvOpen(true); } }]),
  ];
  const projectItems: OverflowItem[] = [
    // 成員：這個專案有誰（製作 / 導演 / 動畫師）、加人、改角色、移出、邀請連結；全站製作和這個專案的製作都能用
    ...(editorRole !== 'admin' ? [] : [{ key: 'members', label: '成員', hint: '成員：這個專案有誰、加人、改角色、移出專案、邀請連結（傳給對方，點開就加進這個專案）', icon: Users, onSelect: () => setMembersOpen(true) }]),
    // Drive 來源只有全站製作能設（專案的製作碰不到 Google 帳號）
    ...(!isGlobalAdmin ? [] : [{ key: 'drive', label: 'Drive', hint: 'Drive 來源設定', icon: Cloud, badge: projectMeta?.drive_folder_id ? <span className="ovf__tag">已設定</span> : undefined, onSelect: () => setDriveOpen(true) }]),
    { key: 'activity', label: '最近活動', hint: '最近活動', icon: HistoryIcon, onSelect: () => setActivityOpen(true) },
    // 用其他身分看：子項目（導演 / 動畫師 / 客戶畫面）和讀分享連結的行為都在 useViewAsMenu 裡，這裡只加小標題
    ...(!canViewAs ? [] : viewAsMenu.items.map((it, i) => (i === 0 ? { ...it, section: VIEW_AS_MENU_LABEL } : it))),
    ...(!statusToggle ? [] : [{
      key: 'projectStatus',
      label: isInReview ? '審查中（點一下改回進行中）' : statusToggle.menuLabel,
      hint: `專案狀態現在是「${isInReview ? '審查中' : '進行中'}」，按一下${statusToggle.menuLabel}（首頁可以用「審查中」篩出來）`,
      icon: Flag, active: isInReview || undefined, separatorBefore: true,
      onSelect: () => { void toggleProjectStatus(); },
    }]),
  ];
  const makeDot = !ledTplGone && (ledTplOpen || uvOpen);
  const projectDot = isInReview;

  return (
    <EditorMusicContext.Provider value={musicCtx}>
    <div className="editor">
      {/* Top */}
      <header className="editor-topbar" ref={topbarRef}>
        <button className="editor-back" onClick={() => guardRef.current(() => navigate(isGlobalAdmin ? '/admin' : '/studio'))} title="返回專案總覽" aria-label="返回專案總覽"><Icon icon={ArrowLeft} size="md" /></button>
        <div className="editor-breadcrumb">
          <span className="muted editor-breadcrumb__root">專案</span>
          <span className="sep editor-breadcrumb__root">/</span>
          {/* 顯示專案名稱；載入中顯示「…」，載不到才退回代號 */}
          <span className="strong editor-breadcrumb__project" title={projectMeta?.name || undefined}>{projectMeta ? (projectMeta.name || projectId) : (projectMetaFailed ? projectId : '…')}</span>
          {selectedSong && (<>
            <span className="sep editor-breadcrumb__sub">/</span>
            <span className="muted editor-breadcrumb__sub editor-breadcrumb__song" title={selectedSong.name}>{selectedSong.name}</span>
          </>)}
          {selectedCue && (<>
            <span className="sep editor-breadcrumb__sub">/</span>
            <span className="strong editor-breadcrumb__sub editor-breadcrumb__cue" title={selectedCue.name}>{selectedCue.name}</span>
          </>)}
        </div>
        <div className="editor-topbar__right">
          <SaveIndicator />
          <span className="muted small editor-topbar__count">{stageObjectsStatus === 'ready' ? stageObjects.length : '…'} 物件</span>
          <button
            className="editor-topbar__activity"
            onClick={() => runHistory('undo')}
            disabled={historyCounts.undo === 0}
            title={historyCounts.undo > 0
              ? `復原${selectedCueId ? '這個 cue ' : ''}的上一步（還有 ${historyCounts.undo} 步）— Cmd/Ctrl+Z`
              : `${selectedCueId ? '這個 cue ' : ''}沒有可以復原的修改`}
          >
            <Icon icon={Undo2} /><span className="editor-topbar__label"> 復原</span>{historyCounts.undo > 0 && <span className="muted small"> ({historyCounts.undo})</span>}
          </button>
          <button
            className="editor-topbar__activity"
            onClick={() => runHistory('redo')}
            disabled={historyCounts.redo === 0}
            title={historyCounts.redo > 0
              ? `重做剛剛復原的修改（還有 ${historyCounts.redo} 步）— Cmd/Ctrl+Shift+Z 或 Ctrl+Y`
              : '沒有可以重做的修改'}
          >
            <Icon icon={Redo2} /><span className="editor-topbar__label"> 重做</span>{historyCounts.redo > 0 && <span className="muted small"> ({historyCounts.redo})</span>}
          </button>
          {/* 寬的時候：對比 / 分享 / 導演頁 / 動畫師工作站 直接顯示 + 「製作」「專案」兩個下拉（不夠寬只留圖示）；手機（640px 以下）全部收進一個「⋯」 */}
          {directActions.map(a => (
            <button
              key={a.key}
              className={'editor-topbar__activity ovf-hide-narrow' + (a.secondary ? ' editor-topbar__activity--secondary' : '')}
              onClick={a.onSelect}
              title={a.title}
              aria-label={a.menuLabel}
              disabled={a.disabled}
            >
              <Icon icon={a.icon} /><span className="editor-topbar__label"> {a.label}</span>
            </button>
          ))}
          <OverflowMenu
            className="ovf-hide-narrow"
            label="製作：分鏡、搜留言、匯入 Cue、版型、自動貼 UV"
            trigger={{ icon: Wrench, text: <><span className="editor-topbar__label"> 製作</span><Icon icon={ChevronDown} size="xs" className="editor-topbar__caret" /></>, className: 'editor-topbar__activity editor-topbar__activity--secondary' }}
            items={makeItems}
            dot={makeDot}
          />
          <OverflowMenu
            className="ovf-hide-narrow"
            label="專案：成員、Drive、最近活動、用其他身分看"
            trigger={{ icon: FolderCog, text: <><span className="editor-topbar__label"> 專案</span><Icon icon={ChevronDown} size="xs" className="editor-topbar__caret" /></>, className: 'editor-topbar__activity editor-topbar__activity--secondary' }}
            items={projectItems}
            onOpen={canViewAs ? viewAsMenu.load : undefined}
            dot={projectDot}
          />
          <NotificationBell />
          <OverflowMenu
            className="ovf-only-narrow"
            items={[
              ...directActions.map(a => ({ key: a.key, label: a.menuLabel, icon: a.icon, disabled: a.disabled, onSelect: a.onSelect, separatorBefore: a.key === 'share' })),
              ...makeItems.map((it, i) => (i === 0 ? { ...it, section: '製作' } : it)),
              ...projectItems.map((it, i) => (i === 0 && !it.section ? { ...it, section: '專案' } : it)),
            ]}
            onOpen={canViewAs ? viewAsMenu.load : undefined}
            dot={makeDot || projectDot}
          />
        </div>
      </header>

      {/* 手機：編輯請用電腦或平板橫放（只有手機寬度才看得到） */}
      <PhoneEditNote />

      <div
        className="editor-body"
        style={{
          ...(songsPanel.track ? { '--songs-w': songsPanel.track } : {}),
          ...(sidePanel.track ? { '--side-w': sidePanel.track } : {}),
        } as React.CSSProperties}
      >
        {/* Left — songs */}
        <PanelEdge panel={songsPanel} label="歌單" column={1} />
        <PanelEdge panel={sidePanel} label="右邊面板" column={3} />
        <aside className={'editor-songs' + songsPanel.className}>
          <div className="editor-songs__header">
            <h3>歌曲</h3>
            <span className="editor-songs__header-right">
              <span className="muted">
                {statusFilter === 'all' ? `${songs.length} 首` : `${visibleSongs.length} / ${songs.length}`}
              </span>
              {editorRole === 'admin' && projectId && (
                <OverflowMenu
                  label="歌單的更多動作"
                  items={[{
                    key: 'music-batch',
                    label: '批次上傳音樂',
                    icon: Music,
                    hint: '一次拖進很多首音樂，依檔名配到歌曲（已有音樂的歌會跳過）',
                    blocked: projectMeta?.status === 'archived' ? '已封存的專案不能上傳音樂' : songs.length === 0 ? '還沒有歌曲' : null,
                    onSelect: () => setMusicBatchOpen(true),
                  }]}
                />
              )}
            </span>
          </div>

          {songs.length > 0 && (
            <StatusFilterChips
              counts={statusCounts}
              total={songs.length}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          )}

          {user && songs.length > 0 && (
            <div className="editor-songs__mine-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={mineOnly}
                  onChange={e => setMineOnly(e.target.checked)}
                />
                <span>只看我改過的{mineOnly ? `（${mineSongIds.size}）` : ''}</span>
              </label>
            </div>
          )}

          {songsLoading ? (
            <div className="editor-empty muted">載入中…</div>
          ) : songs.length === 0 ? (
            <div className="editor-empty">
              <Icon icon={Music} size="xl" className="editor-empty__icon" />
              <div>還沒有歌曲</div>
              <small className="muted">點下方按鈕新增第一首</small>
            </div>
          ) : visibleSongs.length === 0 ? (
            <div className="editor-empty muted">
              <Icon icon={Search} size="xl" className="editor-empty__icon" />
              <div>沒有「{STATUS_INFO[statusFilter as SongStatus].label}」狀態的歌</div>
              <button className="btn btn--ghost" style={{ marginTop: 8 }} onClick={() => setStatusFilter('all')}>顯示全部</button>
            </div>
          ) : (
            <ul className="song-list">
              {visibleSongs.map((s) => {
                const fullIdx = songs.findIndex(x => x.id === s.id);
                // 選到的那首展開（加影片、音樂、負責動畫師、LED 版型…）；沒選到的只佔一到兩行
                const isSel = selectedSongId === s.id;
                return (
                  <li
                    key={s.id}
                    data-song-id={s.id}
                    className={'song-item' + (isSel ? ' is-active is-open' : ' is-compact')}
                    onClick={() => selectSong(s.id)}
                  >
                    <div className="song-item__order">{String(fullIdx + 1).padStart(2, '0')}</div>
                    {/* 一列四行：代號 + 歌名（太長變「…」）+ 最右邊的「⋯」/ 狀態、cue 數 / 影片 / 負責動畫師。
                        滑鼠移上去只換底色，不會長出按鈕把歌名、選單擠掉（上移、下移、改名、刪除都在「⋯」裡） */}
                    <div className="song-item__main">
                      <div className="song-item__head">
                        <div className="song-item__name">
                          {projectId && (
                            <SongCodeField
                              projectId={projectId}
                              song={s}
                              songs={songs}
                              onSaved={(code) => setSongs(list => list.map(x => x.id === s.id ? { ...x, code } : x))}
                            />
                          )}
                          <span className="song-item__name-text" title={s.name}>{s.name}</span>
                        </div>
                        <div className="song-item__more" onClick={(e) => e.stopPropagation()}>
                          <OverflowMenu
                            label={`「${songTitle(s)}」的動作（上移、下移、改名、刪除）`}
                            items={songRowMenuItems(fullIdx, songs.length, {
                              onMove: (dir) => moveSong(s.id, dir),
                              onRename: () => handleRenameSong(s.id, s.name),
                              onDelete: () => handleDeleteSong(s.id, s.name),
                            })}
                          />
                        </div>
                      </div>
                      <div className="song-item__meta">
                        <div className="song-item__status" onClick={(e) => e.stopPropagation()}>
                          <StatusBadge
                            value={s.status}
                            blocked={(st) => statusBlockedReason(s, st)}
                            onChange={(next) => handleUpdateSongStatus(s.id, next)}
                          />
                        </div>
                        <span>{s.cueCount} 個 cue</span>
                        {s.proposalCount > 0 && <span className="proposal-badge">{s.proposalCount} 提案</span>}
                        {newVersionLabel(s) && <span className="song-item__newer" title={newerVersionTitle(s)}>有新版 {newVersionLabel(s)}</span>}
                        {projectId && <GateStageTag projectId={projectId} song={s} />}
                        {!isSel && <SongRowIcons song={s} jobs={videoJobsBySong.get(s.id)} />}
                      </div>
                      {/* 沒選到的收起來（不卸載：音樂上傳到一半換歌也不會被中斷） */}
                      <div className="song-item__fold" hidden={!isSel}>
                      <SongVideoMeta
                        song={s}
                        jobs={videoJobsBySong.get(s.id)}
                        onAdd={() => setAddVideoSongId(s.id)}
                        onOpenVersions={() => setVersionsSongId(s.id)}
                      />
                      {/* 後台還沒有音樂這一功能（舊版後台的歌單沒有 hasMusic）→ 先不顯示，免得按了上傳才發現不能用 */}
                      {projectId && s.hasMusic !== undefined && (
                        <SongMusicField
                          projectId={projectId}
                          songId={s.id}
                          music={s.music ?? null}
                          canEdit={canEditMusic(editorRole)}
                          hasVideo={!!s.latestVideoId}
                          onChange={(m) => {
                            setSongs(list => list.map(x => (x.id === s.id ? { ...x, hasMusic: !!m, music: m ? musicSummaryOf(m) : null } : x)));
                            if (s.id === selectedSongId) songMusic.apply(m);
                          }}
                        />
                      )}
                      <label className="song-item__assign" onClick={(e) => e.stopPropagation()}>
                        <span>負責動畫師</span>
                        <select
                          value={s.animatorUserId ?? ''}
                          onChange={(e) => handleAssignAnimator(s.id, e.target.value || null)}
                          disabled={animatorMembers.length === 0 && !s.animatorUserId}
                          title={animatorMembers.length === 0 ? '這個專案還沒有動畫師：先到上方「專案」→「成員」把動畫師加進這個專案' : '指派後，動畫師在工作站的「我的」就看得到這首歌'}
                        >
                          <option value="">{animatorMembers.length === 0 ? '專案裡還沒有動畫師' : '還沒指派'}</option>
                          {animatorMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                          {s.animatorUserId && !animatorMembers.some(m => m.id === s.animatorUserId) && (
                            <option value={s.animatorUserId}>
                              {(projectMembers.find(m => m.id === s.animatorUserId)?.name || userNames?.get(s.animatorUserId) || (isGlobalAdmin ? '已刪除的帳號' : '不在專案裡的人')) + '（還不是這個專案的動畫師，請到上方「專案」→「成員」加入或邀請）'}
                            </option>
                          )}
                        </select>
                      </label>
                      {customLedTemplates.length > 0 && (
                        <label className="song-item__assign" onClick={(e) => e.stopPropagation()}>
                          <span>LED 版型</span>
                          <select
                            value={s.templateId && customLedTemplates.some(t => t.id === s.templateId) ? s.templateId : ''}
                            onChange={(e) => handleSongTemplate(s.id, e.target.value || null)}
                            title="這首歌的影片做成哪個版型；選了以後，加影片會用版型的畫布大小檢查，3D 畫面（影片、AE 即時、本地檔案）也會照版型貼回。「全部」= 整張 LED 畫布"
                          >
                            <option value="">全部（整張畫布）</option>
                            {customLedTemplates.map(t => <option key={t.id} value={t.id}>{t.name}（{t.canvas.width}×{t.canvas.height}）</option>)}
                          </select>
                        </label>
                      )}
                      {customLedTemplates.length > 0 && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <SongTimelineSummary
                            switches={parseSwitches(s.templateSwitches)}
                            templates={customLedTemplates}
                            cueTimes={s.id === selectedSongId && cuesSongId === s.id ? cueTimesOf(cues) : undefined}
                          />
                          <button
                            type="button"
                            className="song-item__tl-btn"
                            onClick={() => setTimelineSongId(s.id)}
                            disabled={!s.templateId || !customLedTemplates.some(t => t.id === s.templateId)}
                            title={s.templateId && customLedTemplates.some(t => t.id === s.templateId)
                              ? '同一首歌的影片中途換版型：到了指定的 Cue 或時間，LED 改用另一個版型的排法（影片還是一個檔）'
                              : '先在上面選一個自己排的版型當開頭，才能設定中途換版型'}
                          >
                            <Icon icon={ArrowRightLeft} size="xs" /> {parseSwitches(s.templateSwitches).length > 0 ? '編輯中途換版型' : '中途換版型…'}
                          </button>
                        </div>
                      )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <button className="btn btn--ghost editor-songs__add" onClick={handleAddSong}>＋ 新增歌曲</button>
          {/* 刪掉的歌留 30 天，製作可以救回來；沒有刪掉的歌就不出現 */}
          {projectId && (
            <DeletedSongsEntry projectId={projectId} enabled={editorRole === 'admin'} reloadKey={trashKey} onRestored={handleSongRestored} />
          )}
        </aside>

        {/* Center — 3D viewport（永遠顯示；沒 cue 時編輯 = 改 default） */}
        <section className="editor-viewport">
          {stageObjectsStatus === 'loading' ? (
            <div className="viewport-stage">
              <div className="viewport-placeholder" role="status" aria-live="polite">
                <div className="editor-spinner" aria-hidden="true" />
                <div className="muted">載入舞台中…</div>
              </div>
            </div>
          ) : stageObjectsStatus === 'error' && stageObjects.length === 0 ? (
            <div className="viewport-stage">
              <div className="viewport-placeholder" role="alert">
                <div className="muted">舞台物件載入失敗</div>
                <small className="muted">可能是網路斷了，或伺服器暫時沒有回應</small>
                <button className="btn btn--ghost" onClick={() => { setStageObjectsStatus('loading'); refreshStageObjects(); }}>重新載入</button>
              </div>
            </div>
          ) : stageObjects.length === 0 ? (
            <div className="viewport-stage">
              <div className="viewport-placeholder">
                <Icon icon={Shapes} size="xxl" className="editor-empty__icon" />
                <div className="muted">這個專案還沒有物件</div>
                <small className="muted">上傳舞台的 3D 模型（.glb / .gltf），或先用範例物件試試看</small>
                <div className="viewport-placeholder__actions">
                  <button className="btn btn--primary" onClick={() => { setRightTab('objects'); setUploadOpen(true); }}><Icon icon={Upload} /> 上傳模型</button>
                  <button className="btn btn--ghost" onClick={() => { setRightTab('objects'); void handleSeedObjects(); }}><Icon icon={Sprout} /> 加入範例物件</button>
                </div>
              </div>
            </div>
          ) : (
            <>
            {/* LED 版型：AE 即時現在照哪個版型放到 LED 上（這首歌選的）；畫面大小跟版型對不上時提醒 */}
            {ledChip && (
              <div className="tpl-bar">
                <span className="tpl-bar__label">AE 即時</span>
                <LedTemplateChip chip={ledChip} onToggleFullView={() => setLedFullView(v => !v)} onPickSegment={setLedSegPick} />
              </div>
            )}
            <LedTemplateHint hint={ledHint} />
            <StageScene
              onNdiFrameSize={setAeFrameSize}
              states={stageScene.states}
              stageObjects={sceneStageObjects}
              selectedObjectIds={selectedObjectIds}
              onSelect={(id, mode) => {
                if (mode === 'toggle' && id) toggleSelected(id);
                else selectSingle(id);
                if (id) setRightTab(t => (t === 'mechanisms' ? t : 'state'));
              }}
              onTransform={(objId, position, rotation) =>
                handleSetState(objId, { position, rotation }).catch(e => { toast.error('位置沒有存到：' + msg(e)); })
              }
              onEditStart={endTimePreviewNow}
              cueName={selectedCue ? selectedCue.name : '（沒選 cue：改的是物件預設位置）'}
              cueKey={stageScene.cueKey}
              crossfadeSeconds={songSwitch.snap ? 0 : stageScene.crossfadeSeconds}
              modelUrl={modelUrl}
              enableNdi
              bookmarkScope={projectId}
              {...mech.sceneProps}
              mechanismTargets={stageScene.mechanismTargets}
              mechanismTimings={stageScene.mechanismTimings}
              mechanismMotion={songSwitch.snap ? 'instant' : mech.sceneProps.mechanismMotion}
              scaleCorrection={view.scaleCorrection}
              onScaleCorrectionChange={view.canEdit ? view.setScaleCorrection : undefined}
              stageProps={band.stageProps}
              onStagePropsChange={band.onStagePropsChange}
              stagePropsHistory={band.history}
              cameraBookmarks={camBm.bookmarks}
              onCameraBookmarksChange={camBm.onChange}
              stageShow={{ title: selectedSong ? songTitle(selectedSong) : undefined }}
              lookSettings={personalLook.effective}
              onLookSettingsChange={personalLook.set}
              lookCustomized={personalLook.customized}
              onLookReset={personalLook.reset}
              onLookSaveAsDefault={view.canEdit ? () => view.setLookSettings(personalLook.effective) : undefined}
              materialOverrides={view.materialOverrides}
              onMaterialOverrideChange={view.canEdit ? view.setMaterialOverride : undefined}
              homeView={view.homeView}
              onHomeViewChange={view.canEdit ? view.setHomeView : undefined}
              projectName={projectMeta?.name}
              ledCanvas={view.ledCanvas}
              workScale={view.workScale}
              onWorkScaleChange={editorRole === 'admin' ? changeWorkScale : undefined}
              workScaleTemplates={workScaleTemplates}
              ledOffPieces={view.ledOffPieces}
              onLedCanvasChange={view.canEdit ? view.setLedCanvas : undefined}
              ledUvPreview={uvOpen ? uvPreview : null}
              ledTemplate={ledChoice.plan}
              ledTimeline={ledChoice.timeline ?? null}
              ledTimelineSec={ledHoldSec}
              ledSongTemplate={ledSongTpl}
              ledPreviewCanvas={tplPreview ? tplPattern.canvas : null}
              ledPreviewVersion={tplPattern.version}
            />
            </>
          )}
          {/* 音樂播放列：沒有影片、有音樂的歌。播的時候機關時間軸的播放頭和 3D 的機關跟著音樂走；「設在現在這一秒」= 邊聽邊設選中的 cue 的時間 */}
          {songMusic.musicMode && songMusic.summary && (
            <MusicBar
              player={songMusic.player}
              label={songMusic.summary.filename}
              onStop={stopMusic}
              onReload={songMusic.reload}
              loadError={songMusic.error}
              setNow={musicCtx.setNow ? { cueLabel: musicCtx.setNow.cueLabel, busy: musicCtx.setNow.busy, onClick: musicCtx.setNow.run } : null}
            />
          )}
          {/* 機關時間軸：選了歌就一直排在 3D 畫面下面（右邊欄太窄），不管開哪個分頁；收起來變成一條細的時間尺 + cue 編號，不會消失。
              每個分頁都能直接拖（色塊、key、cue 編號、轉場條、歌曲長度），放開就存、Ctrl+Z 可以退。Cue 庫分頁看單一 cue 時改顯示那個 cue 自己的時間軸 */}
          {!libraryMechOn && selectedSongId && stageObjects.length > 0 && (
            <>
              {/* 這首歌的歌曲長度、起始位置（Cue 庫專案）：製作在這裡改，導演頁也看得到同一份；收起來時不佔高度 */}
              {mech.timelineOpen && !narrowLayout && libMode && mech.songStart && selectedSong && (
                <SongTimingBar
                  projectId={projectId || ''}
                  song={selectedSong}
                  length={songLengthNow}
                  startLabel={startLabel(mech.songStart)}
                  startMissing={mech.songStart.requestedMode === 'library_cue' && mech.songStart.mode !== 'library_cue'}
                  firstCueAtZero={masterCues.length > 0 && masterCues[0].videoTimeSec === 0}
                  canEdit={editorRole === 'admin'}
                  cueOps={timingOps}
                />
              )}
              <MechanismTimeline
                mech={mech} cues={cues} selectedCueId={selectedCueId} onSelectCue={selectCue}
                onClose={() => mech.setTimelineOpen(false)} onOpen={narrowLayout ? null : () => mech.setTimelineOpen(true)} collapsed={!mech.timelineOpen || narrowLayout}
                edit={timelineEdit} readOnly={!timelineEdit} readOnlyHint={null} showWhenEmpty stopSignal={timePreviewStop}
                startLabel={mech.songStart ? startLabel(mech.songStart) : null} lengthSec={songLengthNow.seconds} heightKey="editor"
              />
            </>
          )}
          {/* Cue 庫的機關時間軸：只有這一個 cue（從開始轉場到最後一個機關停下），不用先選歌 */}
          {libraryMechOn && libShown && mech.libraryCues && mech.mechanisms.length > 0 && stageObjects.length > 0 && (
            <MechanismTimeline
              mech={mech} cues={mech.libraryCues as unknown as Cue[]} selectedCueId={mech.libraryCues[1]?.id ?? null}
              onSelectCue={() => {}} onClose={() => mech.setTimelineOpen(false)} onOpen={narrowLayout ? null : () => mech.setTimelineOpen(true)} collapsed={!mech.timelineOpen || narrowLayout}
              single={{ label: libShown.label, name: libShown.name }} stopSignal={timePreviewStop}
              heightKey="editor"
            />
          )}
        </section>

        {/* Right — Cue panel */}
        <aside className={'editor-cues' + sidePanel.className}>
          <div className="editor-cues__tabs">
            {([
              ['cues',      `Cue (${masterCues.length})`],
              ['state',     '物件狀態'],
              ['mechanisms', mech.mechanisms.length > 0 ? `機關 (${mech.mechanisms.length})` : '機關'],
              // 提案：現在沒有「建立 / 接受提案」的流程，只有舊資料才會有 → 有提案才出現這個分頁（只能看、刪）
              ...(proposalCues.length > 0 || rightTab === 'proposals' ? [['proposals', `提案 (${proposalCues.length})`] as const] : []),
              ['objects',   stageObjectsStatus === 'ready' ? `物件 (${stageObjects.length})` : '物件'],
              ...(libMode ? [['library', libCues.length > 0 ? `Cue 庫 (${libCues.length})` : 'Cue 庫'] as const] : []),
            ] as const).map(([key, label]) => (
              <button
                key={key}
                className={'tab' + (rightTab === key ? ' is-active' : '')}
                onClick={() => setRightTab(key)}
              >{label}</button>
            ))}
          </div>

          {libMode && libId && currentLib && (
            <LookDraftBar
              label={currentLib.label}
              name={currentLib.name}
              songCount={currentLib.usage.songCount}
              summary={lookDraft.summary}
              hasChanges={lookDraft.hasChanges}
              busy={draftBusy}
              onSaveAs={() => setSaveAsOpen({})}
              onUpdate={() => { void startUpdate(); }}
              onDiscard={() => lookDraft.clear()}
              onApplyTemplate={() => openTemplatePicker('apply')}
            />
          )}
          <div className="editor-cues__body">
            <MechanismHintProvider value={mech}>
            {rightTab === 'objects' ? (
              // 物件（模型）是整個專案的，還沒有歌也能上傳模型、加物件
              <ObjectsManager
                objects={stageObjects}
                status={stageObjectsStatus}
                onSeed={handleSeedObjects}
                onAdd={handleAddObject}
                onUpload={() => setUploadOpen(true)}
                onShowVersions={() => setVersionsOpen(true)}
                onPickFromLibrary={() => setPickerOpen(true)}
                hasModel={!!modelUrl}
                onRename={handleRenameObject}
                onChangeCategory={handleChangeCategory}
                onToggleLock={handleToggleLock}
                onToggleDefaultHidden={handleToggleDefaultHidden}
                onDelete={handleDeleteObject}
              />
            ) : rightTab === 'mechanisms' && (selectedSongId || !songsLoading) ? (
              // 機關的設定也是整個專案的；沒選歌時只能設定機關和預設位置
              <MechanismTab
                mech={mech}
                projectId={projectId || ''}
                projectName={projectMeta?.name}
                cues={selectedSongId ? cues : []}
                selectedCue={selectedSongId ? selectedCue : null}
                onSelectCue={selectCue}
                stageObjects={stageObjects}
                selectedObjectIds={selectedObjectIds}
                scaleCorrection={view.scaleCorrection}
                onStatesChanged={() => { void refreshCueStates(); void lookDraftRef.current?.loadBase(); }}
              />
            ) : rightTab === 'library' && libMode ? (
              <CueLibraryTab
                cues={libCues}
                loading={libLoading}
                shownId={libId}
                previewing={!!libPreviewId}
                onSelect={selectLib}
                onCopy={setCopyOf}
                onRename={(lc, name) => relabelOrRename(lc, { name })}
                onRelabel={(lc, label) => relabelOrRename(lc, { label })}
                onSetFade={(lc, sec) => relabelOrRename(lc, { crossfadeSeconds: sec })}
                onDelete={(lc) => { void deleteLib(lc); }}
                onResetAll={(lc) => { void resetAllLib(lc); }}
                onJump={jumpToSlot}
                songInfo={(id) => songs.find(s => s.id === id) ?? null}
                onAddToSong={selectedSongId ? (lc) => setAddSlotOpen({ libraryCueId: lc.id }) : undefined}
                onSaveAs={() => { if (libId) setSaveAsOpen({}); else toast.info('先點一個 cue，再從它另存'); }}
                busy={libBusy || draftBusy}
                subTab={libSub}
                onSubTab={setLibSub}
                mechanisms={libShown && mech.libraryCues ? (
                  <MechanismTab
                    mech={mech}
                    projectId={projectId || ''}
                    projectName={projectMeta?.name}
                    cues={mech.libraryCues as unknown as Cue[]}
                    selectedCue={(mech.libraryCues[1] as unknown as Cue) ?? null}
                    onSelectCue={() => {}}
                    stageObjects={stageObjects}
                    selectedObjectIds={selectedObjectIds}
                    scaleCorrection={view.scaleCorrection}
                    onStatesChanged={() => { void refreshCueStates(); void lookDraftRef.current?.loadBase(); }}
                    library={{ label: libShown.label, name: libShown.name }}
                  />
                ) : null}
              />
            ) : songsLoading && !selectedSongId ? (
              <div className="editor-empty muted">載入中…</div>
            ) : !selectedSongId ? (
              songs.length === 0 ? (
                <div className="editor-empty">
                  <Icon icon={Music} size="xl" className="editor-empty__icon" />
                  <div>還沒有歌</div>
                  <small className="muted">cue 是跟著歌走的：先在左邊按「新增歌曲」加第一首歌。模型可以先到「物件」分頁上傳。</small>
                  <button className="btn btn--ghost" style={{ marginTop: 12 }} onClick={() => setRightTab('objects')}>
                    <Icon icon={Upload} /> 到「物件」上傳模型
                  </button>
                </div>
              ) : (
                <div className="editor-empty muted">先在左側選一首歌</div>
              )
            ) : rightTab === 'cues' && libMode ? (
              <SongCueSlots
                cues={masterCues}
                loading={cuesLoading}
                selectedId={selectedCueId}
                libCues={libCues}
                libLoading={libLoading}
                onSelect={selectCue}
                onAdd={() => setAddSlotOpen({})}
                onNewFromTemplate={() => openTemplatePicker('new')}
                onChangeTime={changeSlotTime}
                onChangeFade={changeSlotFade}
                onSwap={(c) => { if (c.id === selectedCueId && lookDraft.hasChanges) toast.info(DRAFT_BLOCK); else setSwapSlot(c); }}
                onCopyNew={(c) => { if (c.id === selectedCueId && lookDraft.hasChanges) toast.info(DRAFT_BLOCK); else setCopySlot(c); }}
                onRemove={(c) => { void removeSlot(c); }}
                onSaveTemplate={(c) => { void handleSaveCueAsTemplate(c.id, c.libraryCue?.name ?? c.name); }}
                onShowInLibrary={showInLibrary}
                onMove={moveCue}
                onDragMove={dragMoveCue}
                onStep={stepCue}
                onResortNow={() => { void resortSlots(); }}
                onResetAllObjects={handleResetAllCueObjects}
                busy={slotBusy || draftBusy}
              />
            ) : rightTab === 'cues' ? (
              <CueList
                cues={masterCues}
                loading={cuesLoading}
                selectedId={selectedCueId}
                onSelect={selectCue}
                onAddBlank={handleAddBlankCue}
                onClone={handleCloneCue}
                onSnapshot={handleSnapshotCue}
                onFromTemplate={() => setTemplatePickerOpen(true)}
                onDuplicateQuick={handleDuplicateCueQuick}
                onReset={handleResetCue}
                onRename={handleRenameCue}
                onDelete={handleDeleteCue}
                onMove={moveCue}
                onDragMove={dragMoveCue}
                onStep={stepCue}
                onSaveTemplate={handleSaveCueAsTemplate}
                onResetAllObjects={handleResetAllCueObjects}
              />
            ) : rightTab === 'state' ? (
              <ObjectStateEditor
                cue={selectedCue}
                states={cueStates}
                loading={statesLoading || stageObjectsStatus === 'loading'}
                error={statesError || (stageObjectsStatus === 'error' && stageObjects.length === 0)}
                onRetry={() => { refreshStageObjects(); refreshCueStates(); }}
                stageObjects={stageObjects}
                selectedObjectIds={selectedObjectIds}
                onSelectSingle={selectSingle}
                onToggleSelected={toggleSelected}
                scaleCorrection={view.scaleCorrection}
                unitsReady={view.loaded}
                onSet={handleSetState}
                onToggleVisible={handleToggleVisible}
                onReset={handleResetState}
                onUpdateCueMeta={handleUpdateCueMeta}
                onUpdateMaterial={handleUpdateMaterial}
                onUpdateLed={handleUpdateLed}
                onJumpToObjects={() => setRightTab('objects')}
              />
            ) : rightTab === 'proposals' ? (
              <>
                <div className="editor-proposals-note">
                  這些是以前留下的「提案」cue。現在還沒有「接受提案」的功能（提案沒有記下要取代哪一個 cue，套上去可能蓋錯），
                  只能點進去看擺位、或刪掉。要用的話，請在 Cue 分頁「沿用目前的 cue」複製一份再改。
                </div>
                <CueList
                  cues={proposalCues}
                  loading={cuesLoading}
                  selectedId={selectedCueId}
                  onSelect={selectCue}
                  onDelete={handleDeleteCue}
                  emptyText="這首歌沒有提案"
                />
              </>
            ) : null}
            </MechanismHintProvider>
          </div>
        </aside>
      </div>

      <UploadModelDialog
        open={uploadOpen}
        projectId={projectId || ''}
        onClose={() => setUploadOpen(false)}
        onImported={() => {
          refreshStageObjects();
          refreshCueStates();
          refreshModel();
        }}
      />

      {timelineSong && projectId && (
        <LedTimelineDialog
          open
          projectId={projectId}
          song={{ id: timelineSong.id, name: timelineSong.name, templateId: timelineSong.templateId ?? null, switches: parseSwitches(timelineSong.templateSwitches) }}
          templates={customLedTemplates}
          onClose={() => setTimelineSongId(null)}
          onSaved={handleTimelineSaved}
        />
      )}
      <LedTemplatesDialog
        open={ledTplOpen && editorRole === 'admin'}
        projectId={projectId || ''}
        projectName={projectMeta?.name}
        onClose={() => setLedTplOpen(false)}
        onPreview={setTplPreview}
        list={ledTpls.rawList}
        workScale={view.workScale}
        ledObjects={ledObjectInfos}
        getSpans={() => mech.sceneQueryRef.current?.ledSpans?.() ?? null}
        getParts={() => mech.sceneQueryRef.current?.ledPartSpans?.() ?? null}
        onChanged={ledTpls.reload}
        ledCanvas={view.ledCanvas}
        onSetCanvas={view.canEdit ? view.setLedCanvas : undefined}
      />

      <LedUvPanel
        open={uvOpen && editorRole === 'admin'}
        projectId={projectId || ''}
        projectName={projectMeta?.name}
        ledObjects={uvObjects}
        list={ledTpls.rawList}
        ledCanvas={view.ledCanvas}
        scaleCorrection={view.scaleCorrection}
        onClose={() => { setUvOpen(false); setUvPreview(null); }}
        onPreview={setUvPreview}
        entry={uvEntry}
        onApplied={(r) => {
          setUvPreview(null);
          // 新模型、新畫布（後台已經一起換好）、新的「舊版面」版型
          view.setLedCanvas(r.ledCanvas);
          if (r.workScaleReset) { view.clearWorkScaleLocal(); toast.info('新的畫布縮不成原本的製作尺寸，已經切回原尺寸'); }
          view.setLedOffPieces(r.ledOffPieces ?? []);   // 不放進畫布的 LED：3D 上變黑
          refreshModel();
          void ledTpls.reload();
          // 「不放進畫布」的物件，後台把類型改成「其他」了：物件清單和 3D 畫面跟著重讀
          if (r.excludedObjects && r.excludedObjects.length > 0) { void refreshStageObjects(); void refreshCueStates(); }
        }}
      />

      <ModelVersionsDialog
        open={versionsOpen}
        projectId={projectId || ''}
        onClose={() => setVersionsOpen(false)}
        onChanged={(info) => {
          // 換回舊版時，後台把畫布也換回來了（沒有這個版本的紀錄 = 畫布不動）
          if (info?.restoredCanvas) view.setLedCanvas(info.ledCanvas);
          if (info?.workScaleReset) { view.clearWorkScaleLocal(); toast.info('換回的畫布縮不成原本的製作尺寸，已經切回原尺寸'); }
          if (info?.ledOffPieces) view.setLedOffPieces(info.ledOffPieces);   // 黑色的 LED 跟著版本走
          // 自動貼 UV 時「不放進畫布」改成「其他」的物件，換版本時類型跟著改回來 / 再改掉
          if (info?.objectTypesChanged) { void refreshStageObjects(); void refreshCueStates(); }
          refreshModel();
          void ledTpls.reload();
        }}
      />

      <AssetPickerDialog
        open={pickerOpen}
        projectId={projectId || ''}
        currentKey={modelUrl ? modelUrl.replace(api.apiBase() + '/r2/', '') : null}
        onClose={() => setPickerOpen(false)}
        onPicked={() => {
          refreshModel();
          refreshStageObjects();
          refreshCueStates();
        }}
      />

      <ActivityDrawer
        open={activityOpen}
        projectId={projectId || ''}
        onClose={() => setActivityOpen(false)}
      />

      <DriveSettingsDialog
        open={driveOpen}
        onClose={() => setDriveOpen(false)}
        projectId={projectId || ''}
        songs={songs}
        initialFolderId={projectMeta?.drive_folder_id || ''}
        initialAccountId={projectMeta?.drive_oauth_token_id || ''}
        onChanged={() => { refreshProjectMeta(); }}
      />

      {versionsSong && projectId && (
        <SongVideosDialog
          open
          onClose={() => setVersionsSongId(null)}
          projectId={projectId}
          song={versionsSong}
          jobs={videoJobsBySong.get(versionsSong.id) ?? []}
          onAdd={() => setAddVideoSongId(versionsSong.id)}
          covered={!!addVideoSong}
          onChanged={reloadSongsQuietly}
        />
      )}

      {musicBatchOpen && projectId && editorRole === 'admin' && projectMeta?.status !== 'archived' && (
        <MusicBatchDialog
          projectId={projectId}
          songs={songs}
          onUploaded={(songId, m) => {
            setSongs(list => list.map(x => (x.id === songId ? { ...x, hasMusic: true, music: musicSummaryOf(m) } : x)));
          }}
          onClose={() => { setMusicBatchOpen(false); reloadSongsQuietly(); }}
        />
      )}

      {addVideoSong && projectId && (
        <AddVideoDialog
          open
          onClose={() => setAddVideoSongId(null)}
          projectId={projectId}
          song={addVideoSong}
          jobs={videoJobsBySong.get(addVideoSong.id) ?? []}
          canUpload
          ledCanvas={view.workCanvas}
          workScale={view.workScale}
        />
      )}

      <ShareLinksDialog
        open={shareOpen}
        projectId={projectId || ''}
        projectName={projectMeta?.name}
        songs={songs}
        onClose={() => setShareOpen(false)}
        onProjectChanged={refreshProjectMeta}
      />

      <ProjectMembersDialog
        open={membersOpen}
        projectId={projectId || ''}
        projectName={projectMeta?.name}
        onChanged={() => { void refreshMembers(); }}
        onClose={() => { setMembersOpen(false); void refreshMembers(); }}
      />

      <CommentSearchDialog
        open={commentSearchOpen}
        projectId={projectId || ''}
        songs={songs}
        onClose={() => setCommentSearchOpen(false)}
        onJump={(songId) => { selectSong(songId); setCommentSearchOpen(false); }}
      />

      {decisionSongId && (() => {
        const ds = songs.find(x => x.id === decisionSongId);
        return (
          <DecisionDialog
            songName={ds?.name ?? ''}
            versionLabel={ds?.latestVideoVersion ? `V${ds.latestVideoVersion}` : ''}
            onCancel={() => setDecisionSongId(null)}
            onSubmit={async (reason) => {
              const ok = await handleUpdateSongStatus(decisionSongId, 'needs_changes', reason);
              if (ok) { setDecisionSongId(null); toast.success(`已送出「需修改」${ds ? `，${decisionNotifyHint(ds)}` : ''}`); }
              return ok;
            }}
          />
        );
      })()}

      <CueDiffDialog
        open={diffOpen}
        projectId={projectId || ''}
        songId={selectedSongId || ''}
        cues={masterCues}
        onClose={() => setDiffOpen(false)}
      />

      {saveAsOpen && currentLib && (
        <SaveAsCueDialog
          defaultLabel={suggestLabel(libCues.map(c => c.label))}
          defaultName={saveAsName ?? currentLib.name}
          existingLabels={libCues.map(c => c.label)}
          onSave={async (label, name) => {
            const err = await doSaveAs(label, name);
            if (!err) { const after = saveAsOpen.after; setSaveAsOpen(null); setSaveAsName(null); after?.(); }
            return err;
          }}
          onCancel={() => { setSaveAsOpen(null); setSaveAsName(null); }}
        />
      )}
      {addSlotOpen && (
        <AddSongCueDialog
          mode="add"
          libCues={libCues}
          slots={masterCues}
          initialLibraryCueId={addSlotOpen.libraryCueId}
          onSubmit={async (v) => { const err = await addSlot(v); if (!err) setAddSlotOpen(null); return err; }}
          onCancel={() => setAddSlotOpen(null)}
        />
      )}
      {swapSlot && (
        <AddSongCueDialog
          mode="swap"
          libCues={libCues}
          slots={masterCues}
          currentLibraryCueId={swapSlot.libraryCueId}
          onSubmit={async (v) => { const err = await swapSlotTo(swapSlot, v); if (!err) setSwapSlot(null); return err; }}
          onCancel={() => setSwapSlot(null)}
        />
      )}
      {copySlot && copySlot.libraryCueId && (
        <SaveAsCueDialog
          defaultLabel={suggestLabel(libCues.map(c => c.label))}
          defaultName={copySlot.libraryCue?.name ?? copySlot.name}
          existingLabels={libCues.map(c => c.label)}
          onSave={async (label, name) => {
            if (!projectId || !selectedSongId) return '先選一首歌';
            try {
              const r = await cueLib.createLibraryCue(projectId, {
                label, name, basedOnId: copySlot.libraryCueId!,
                place: { songId: selectedSongId, replaceCueId: copySlot.id },
              });
              toast.success(`這首歌改用新的 Cue ${r.libraryCue.label}「${r.libraryCue.name}」，原本的 cue 沒動`);
              await afterSlotWrite();
              setCopySlot(null);
              return null;
            } catch (e) { return msg(e); }
          }}
          onCancel={() => setCopySlot(null)}
        />
      )}
      {copyOf && (
        <SaveAsCueDialog
          defaultLabel={suggestLabel(libCues.map(c => c.label))}
          defaultName={copyOf.name}
          existingLabels={libCues.map(c => c.label)}
          onSave={async (label, name) => {
            if (!projectId) return '沒有專案';
            try {
              const r = await cueLib.createLibraryCue(projectId, { label, name, basedOnId: copyOf.id });
              toast.success(`已複製成 Cue ${r.libraryCue.label}「${r.libraryCue.name}」`);
              await refreshLibCues();
              setCopyOf(null);
              return null;
            } catch (e) { return msg(e); }
          }}
          onCancel={() => setCopyOf(null)}
        />
      )}
      {scopeAsk && <TimingScopeMenu songCount={scopeAsk.songCount} onPick={scopeAsk.resolve} />}
      {updateOpen && currentLib && (
        <UpdateCueDialog
          label={currentLib.label}
          name={currentLib.name}
          usage={updateOpen.usage}
          busy={draftBusy}
          onConfirm={async () => {
            if (await doUpdate()) { const after = updateOpen.after; setUpdateOpen(null); after?.(); }
          }}
          onSaveAs={() => { const after = updateOpen.after; setUpdateOpen(null); setSaveAsOpen({ after }); }}
          onCancel={() => setUpdateOpen(null)}
        />
      )}

      <ImportCuesDialog
        open={importCuesOpen}
        projectId={projectId || ''}
        songId={selectedSongId || ''}
        songName={selectedSong?.name || ''}
        onClose={() => setImportCuesOpen(false)}
        onImported={refreshCues}
      />

      {storyboardOpen && selectedSong && projectId && (
        <div className="dlg-overlay" onClick={(e) => { if (e.target === e.currentTarget) setStoryboardOpen(false); }}>
          <div className="dlg" style={{ maxWidth: 1100, width: '95vw' }}>
            <header className="dlg__header">
              <h2>{selectedSong.name} — 分鏡</h2>
              <button className="dlg__close" onClick={() => setStoryboardOpen(false)} title="關閉" aria-label="關閉"><Icon icon={X} size="md" /></button>
            </header>
            <div className="dlg__body">
              <CueStoryboard
                projectId={projectId}
                songId={selectedSong.id}
                cues={masterCues}
                stageObjects={stageObjects}
                modelUrl={modelUrl}
                selectedCueId={selectedCueId}
                onSelectCue={selectCue}
                onReordered={refreshCues}
                projectName={projectMeta?.name}
                songName={selectedSong.name}
                scaleCorrection={view.scaleCorrection}
                mechanisms={mech.sceneProps.mechanisms}
                mechanismTargetsFor={(cueId) => valuesOf(mech.resolved.get(cueId))}
                lookSettings={personalLook.effective}
                materialOverrides={view.materialOverrides}
                homeView={view.homeView}
              />
            </div>
          </div>
        </div>
      )}

      <CueTemplatePickerDialog
        open={templatePickerOpen}
        projectId={projectId || ''}
        onClose={() => setTemplatePickerOpen(false)}
        onPick={libMode ? handleApplyTemplateToDraft : handleApplyTemplate}
        title={libMode ? (templatePickerMode === 'new' ? '從常用擺位建立新 cue' : '套用常用擺位') : '從常用擺位建立 cue'}
        hint={libMode ? (templatePickerMode === 'new'
          ? '會先套進這個 cue 還沒存的改動，接著請你取編號和名稱另存成新的 cue。'
          : '會套進這個 cue 還沒存的改動，不會新增 cue；之後可以另存、更新或放棄。') : undefined}
      />
    </div>
    </EditorMusicContext.Provider>
  );
}

function CueTemplatePickerDialog({
  open, projectId, onClose, onPick, title, hint,
}: {
  open: boolean;
  title: string;
  hint?: string;
  projectId: string;
  onClose: () => void;
  onPick: (templateId: string) => Promise<void>;
}) {
  const [templates, setTemplates] = useState<api.CueTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.listCueTemplates(projectId)
      .then(setTemplates)
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, projectId]);
  if (!open) return null;

  async function handleDelete(t: api.CueTemplate) {
    if (!(await confirmDialog({ title: `刪除常用擺位「${t.name}」？`, message: '已經用它建好的 cue 不受影響。', confirmLabel: '刪除', destructive: true }))) return;
    try { await api.deleteCueTemplate(t.id); setTemplates(prev => prev.filter(x => x.id !== t.id)); }
    catch (e) { toast.error('刪除失敗：' + (e instanceof Error ? e.message : String(e))); }
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <header className="dialog__header">
          <h2>{title}</h2>
          <button className="dialog__close" onClick={onClose} title="關閉" aria-label="關閉"><Icon icon={X} size="md" /></button>
        </header>
        <div className="dialog__body">
          {hint && <p className="muted small">{hint}</p>}
          {loading ? (
            <div className="editor-empty muted">載入中…</div>
          ) : templates.length === 0 ? (
            <div className="editor-empty muted">
              <Icon icon={Bookmark} size="xl" className="editor-empty__icon" />
              <div>還沒有任何常用擺位</div>
              <small>在 cue 旁邊按「存成常用擺位」（書籤圖示），就能把那個 cue 的物件位置存起來</small>
            </div>
          ) : (
            <ul className="versions-list">
              {templates.map(t => (
                <li key={t.id} className="version-row">
                  <div className="version-row__num"><Icon icon={Bookmark} size="md" /></div>
                  <div className="version-row__main">
                    <div className="version-row__title">
                      {t.name}
                      {t.global && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--accent)' }}>所有專案</span>}
                    </div>
                    {t.description && <div className="muted small">{t.description}</div>}
                    {t.authorName && <div className="muted small">{t.authorName} 建立</div>}
                  </div>
                  <div className="version-row__actions">
                    <button className="btn btn--primary btn--sm" onClick={() => onPick(t.id)}>套用</button>
                    <button className="btn btn--ghost btn--sm" onClick={() => handleDelete(t)} title="刪除這個常用擺位" aria-label="刪除這個常用擺位"><Icon icon={Trash2} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────

function CueList({
  cues, loading, selectedId, onSelect, onAddBlank, onClone, onSnapshot, onFromTemplate, onDuplicateQuick,
  onReset, onRename, onDelete, onMove, onDragMove, onStep, onSaveTemplate, onResetAllObjects, emptyText,
}: {
  cues: Cue[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAddBlank?: () => void;
  onClone?: (sourceCueId?: string) => void;
  onSnapshot?: () => void;
  onFromTemplate?: () => void;
  onDuplicateQuick?: (cueId: string) => void;
  onReset?: (cueId: string, name: string) => void;
  onRename?: (cueId: string, name: string) => void;
  onDelete: (id: string, name: string) => void;
  onMove?: (cueId: string, direction: -1 | 1) => void;
  /** 拖拉改順序（有給才會有把手） */
  onDragMove?: (fromId: string, toId: string) => void;
  onStep?: (cueId: string, direction: -1 | 1) => void;
  onSaveTemplate?: (cueId: string, name: string) => void;
  /** 這首歌所有 cue 的物件一次回到預設位置（Cue 分頁的「⋯」；提案那一頁沒有） */
  onResetAllObjects?: () => void;
  emptyText?: string;
}) {
  const drag = useCueRowDrag({
    canDrop: (a, b) => canDragOnto(cues, a, b),
    onDrop: (a, b) => onDragMove?.(a, b),
    onStep: (id, d) => onStep?.(id, d),
    disabled: !onDragMove,
  });
  if (loading) return <div className="editor-empty muted">載入中…</div>;
  return (
    <>
      {cues.length === 0 ? (
        <div className="editor-empty">
          <Icon icon={Clapperboard} size="xl" className="editor-empty__icon" />
          <div className="muted">{emptyText || '還沒有 cue'}</div>
        </div>
      ) : (
        <ul className="cue-list">
          {cues.map((c, i) => (
            <li
              key={c.id}
              data-cue-row={c.id}
              className={'cue-item' + (selectedId === c.id ? ' is-active' : '') + (c.status === 'proposal' ? ' is-proposal' : '') + drag.rowClass(c.id)}
              onClick={() => onSelect(c.id)}
            >
              {onDragMove && cues.length > 1 && <CueDragHandle props={drag.handleProps(c.id)} label={`「${c.name}」`} />}
              <div className="cue-item__num">{i + 1}</div>
              <div className="cue-item__main">
                <div className="cue-item__name">{c.name}</div>
                <div className="cue-item__meta muted small">
                  {crossfadeLabel(c.crossfadeSeconds)}
                  {c.status === 'proposal' && <span className="proposal-tag">提案</span>}
                </div>
              </div>
              {/* 上移 / 下移、複製、改名、刪除…都收在最右邊的「⋯」：點一列的中間只會選這個 cue
                  （以前滑鼠移上去，按鈕會蓋住右半邊，點中間可能按到「上移」把 cue 換了順序） */}
              <div className="cue-item__more" onClick={(e) => e.stopPropagation()}>
                <OverflowMenu
                  label={`「${c.name}」的動作（上移、下移、複製、改名、刪除）`}
                  items={cueRowMenuItems(c, i, cues.length, { onMove, onDuplicateQuick, onSaveTemplate, onReset, onRename, onDelete })}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      {onDragMove && cues.length > 1 && <p className="cue-list__hint muted small">{dragHintText(cues)}</p>}
      {onAddBlank && (
        <div className="editor-cues__footer">
          <CueAddSplitButton
            onAddBlank={onAddBlank}
            onClone={onClone}
            onSnapshot={onSnapshot}
            onFromTemplate={onFromTemplate}
            hasSelectedCue={!!selectedId}
          />
          {/* 整首歌的動作收在「⋯」：現在只有一個。每一列的「⋯」是那一個 cue 的動作 */}
          {onResetAllObjects && (
            <OverflowMenu
              label="這首歌所有 cue 的動作"
              items={[{ key: 'resetAll', label: RESET_ALL_MENU_LABEL, icon: RotateCcw, disabled: cues.length === 0, onSelect: onResetAllObjects }]}
            />
          )}
        </div>
      )}
    </>
  );
}

/** 歌單每一列「⋯」裡的動作：上移 / 下移（第一首、最後一首按不了）、改名、刪除 */
function songRowMenuItems(
  index: number, count: number,
  h: { onMove: (direction: -1 | 1) => void; onRename: () => void; onDelete: () => void },
): OverflowItem[] {
  return [
    { key: 'up', label: '上移', icon: ArrowUp, disabled: index <= 0, onSelect: () => h.onMove(-1) },
    { key: 'down', label: '下移', icon: ArrowDown, disabled: index >= count - 1, onSelect: () => h.onMove(1) },
    { key: 'rename', label: '改名', icon: Pencil, separatorBefore: true, onSelect: h.onRename },
    { key: 'delete', label: '刪除', icon: Trash2, danger: true, separatorBefore: true, onSelect: h.onDelete },
  ];
}

/** cue 列「⋯」裡的動作（有給 handler 的才出現） */
function cueRowMenuItems(
  c: Cue, i: number, count: number,
  h: {
    onMove?: (cueId: string, direction: -1 | 1) => void;
    onDuplicateQuick?: (cueId: string) => void;
    onSaveTemplate?: (cueId: string, name: string) => void;
    onReset?: (cueId: string, name: string) => void;
    onRename?: (cueId: string, name: string) => void;
    onDelete: (id: string, name: string) => void;
  },
): OverflowItem[] {
  const items: OverflowItem[] = [];
  const { onMove, onDuplicateQuick, onSaveTemplate, onReset, onRename, onDelete } = h;
  if (onMove) {
    items.push({ key: 'up', label: '上移', icon: ArrowUp, disabled: i === 0, onSelect: () => onMove(c.id, -1) });
    items.push({ key: 'down', label: '下移', icon: ArrowDown, disabled: i === count - 1, onSelect: () => onMove(c.id, 1) });
  }
  const sep = items.length > 0;
  if (onDuplicateQuick) items.push({ key: 'dup', label: '複製這個 cue', icon: Copy, separatorBefore: sep, onSelect: () => onDuplicateQuick(c.id) });
  if (onSaveTemplate) items.push({ key: 'tpl', label: '存成常用擺位', icon: BookmarkPlus, separatorBefore: sep && !onDuplicateQuick, onSelect: () => onSaveTemplate(c.id, c.name) });
  if (onRename) items.push({ key: 'rename', label: '改名', icon: Pencil, onSelect: () => onRename(c.id, c.name) });
  if (onReset) items.push({ key: 'reset', label: '重置（清掉這個 cue 的所有調整）', icon: RotateCcw, onSelect: () => onReset(c.id, c.name) });
  items.push({ key: 'delete', label: '刪除', icon: Trash2, danger: true, separatorBefore: true, onSelect: () => onDelete(c.id, c.name) });
  return items;
}

function CueAddSplitButton({
  onAddBlank, onClone, onSnapshot, onFromTemplate, hasSelectedCue,
}: {
  onAddBlank: () => void;
  onClone?: (id?: string) => void;
  onSnapshot?: () => void;
  onFromTemplate?: () => void;
  hasSelectedCue: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="cue-split editor-cues__add" ref={ref}>
      <button className="cue-split__main btn btn--primary" onClick={onAddBlank} title="新增空白 cue（全部用預設位置）">
        <Icon icon={Plus} /> 新增 cue
      </button>
      <button className="cue-split__caret btn btn--primary" onClick={() => setOpen(o => !o)} aria-label="更多新增方式" title="更多新增方式">
        <Icon icon={ChevronDown} />
      </button>
      {open && (
        <div className="cue-split__menu">
          <button onClick={() => { setOpen(false); onAddBlank(); }}>
            <span><Icon icon={Plus} /> 空白 cue</span>
            <small>全部用預設位置</small>
          </button>
          <button
            onClick={() => { setOpen(false); onClone && onClone(); }}
            disabled={!onClone || !hasSelectedCue}
            title={!hasSelectedCue ? '先選一個 cue' : ''}
          >
            <span><Icon icon={Copy} /> 沿用目前的 cue</span>
            <small>{hasSelectedCue ? '複製目前 cue 的所有調整' : '要先選一個 cue'}</small>
          </button>
          <button onClick={() => { setOpen(false); onSnapshot && onSnapshot(); }} disabled={!onSnapshot}>
            <span><Icon icon={Camera} /> 從目前 3D 狀態建立</span>
            <small>記下 3D 畫面現在的物件位置</small>
          </button>
          <button onClick={() => { setOpen(false); onFromTemplate && onFromTemplate(); }} disabled={!onFromTemplate}>
            <span><Icon icon={Bookmark} /> 從常用擺位建立</span>
            <small>選一個存好的常用擺位套用</small>
          </button>
        </div>
      )}
    </div>
  );
}

function ObjectStateEditor({
  cue, states, loading, error, onRetry, stageObjects, selectedObjectIds, onSelectSingle, onToggleSelected,
  scaleCorrection, unitsReady, onSet, onToggleVisible, onReset, onUpdateCueMeta, onUpdateMaterial, onUpdateLed, onJumpToObjects,
}: {
  cue: Cue | null;
  states: CueState[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  stageObjects: StageObject[];
  selectedObjectIds: string[];
  onSelectSingle: (id: string | null) => void;
  onToggleSelected: (id: string) => void;
  /** 測距校準倍率：位置欄位用公尺顯示 */
  scaleCorrection: number;
  /** 專案設定（校準倍率）讀完了沒；還沒讀完先不給改位置，免得用錯倍率換算 */
  unitsReady: boolean;
  onSet: (objId: string, patch: StatePatchInput) => Promise<void>;
  onToggleVisible: (objId: string) => void;
  onReset: (objId: string, askFirst?: boolean) => Promise<void>;
  onUpdateCueMeta: (patch: Partial<Pick<Cue, 'name' | 'crossfadeSeconds' | 'videoTimeSec'>>) => Promise<void>;
  onUpdateMaterial: (objId: string, patch: MaterialProps) => Promise<void>;
  onUpdateLed: (objId: string, patch: LedProps) => Promise<void>;
  onJumpToObjects: () => void;
}) {
  if (!cue) return <div className="editor-empty muted">先選一個 cue</div>;
  if (loading) return <div className="editor-empty muted">載入物件狀態中…</div>;
  if (error) {
    return (
      <div className="editor-empty" role="alert">
        <div className="muted">物件狀態載入失敗</div>
        <small className="muted">可能是網路斷了，或伺服器暫時沒有回應</small>
        <button className="btn btn--ghost" style={{ marginTop: 8 }} onClick={onRetry}>重新載入</button>
      </div>
    );
  }
  if (stageObjects.length === 0) {
    return (
      <div className="editor-empty">
        <Icon icon={Shapes} size="xl" className="editor-empty__icon" />
        <div>這個專案還沒有物件</div>
        <small className="muted">先到「物件」分頁新增，或一鍵加入範例</small>
        <button className="btn btn--primary" style={{ marginTop: 12 }} onClick={onJumpToObjects}>
          管理物件 <Icon icon={ArrowRight} />
        </button>
      </div>
    );
  }

  const selectedStates = states.filter(s => selectedObjectIds.includes(s.objectId));
  const selectedSo = selectedStates.length === 1
    ? stageObjects.find(o => o.id === selectedStates[0].objectId)
    : undefined;

  return (
    <div className="state-editor">
      <CueMetaEditor cue={cue} onUpdate={onUpdateCueMeta} />

      {/* 固定的 attribute panel — C4D / Maya 風格 */}
      <div className="attribute-panel">
        <div className="attribute-panel__selector">
          <span className="attribute-panel__selector-label">
            物件 {selectedObjectIds.length > 0 && <span className="muted small">已選 {selectedObjectIds.length}</span>}
          </span>
          <div className="attribute-panel__selector-actions">
            <button
              type="button"
              className="link-btn"
              onClick={() => onSelectSingle(null)}
              disabled={selectedObjectIds.length === 0}
            >清空</button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                const allIds = states.filter(s => !s.locked).map(s => s.objectId);
                // 全選未鎖定的
                if (selectedObjectIds.length === allIds.length) onSelectSingle(null);
                else allIds.forEach(id => { if (!selectedObjectIds.includes(id)) onToggleSelected(id); });
              }}
            >全選</button>
          </div>
        </div>
        <ul className="attribute-panel__objects">
          {states.map(s => {
            const cat = CATEGORY_INFO[s.category];
            const checked = selectedObjectIds.includes(s.objectId);
            const visible = s.effective.visible;
            return (
              <li
                key={s.objectId}
                className={'attribute-panel__obj' + (checked ? ' is-selected' : '') + (s.locked ? ' is-locked' : '') + (visible ? '' : ' is-hidden-in-cue')}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={s.locked}
                    onChange={() => onToggleSelected(s.objectId)}
                  />
                  <span className="attribute-panel__obj-cat" title={cat.label}><Icon icon={cat.icon} /></span>
                  <span className="attribute-panel__obj-name">{s.displayName}</span>
                  {s.locked && <span className="lock-tag" title="已鎖定"><Icon icon={Lock} size="xs" /></span>}
                  {isDefaultHidden(s) && <span className="default-hidden-tag" title={visibilityNote(s) ?? ''} data-testid="state-default-hidden">{DEFAULT_HIDDEN_LABEL}</span>}
                  {s.override && <span className="override-dot" title="此 cue 已自訂" />}
                </label>
                {/* 這個 cue 裡顯示 / 隱藏（前面的 cue 不受影響；後面沒另外設定的 cue 會跟著，跟位置一樣） */}
                <button
                  type="button"
                  className="vis-toggle"
                  onClick={() => onToggleVisible(s.objectId)}
                  disabled={s.locked}
                  aria-pressed={!visible}
                  aria-label={(visible ? '在這個 cue 隱藏「' : '在這個 cue 顯示「') + s.displayName + '」'}
                  title={s.locked ? '已鎖定，不能改' : visible ? '在這個 cue 隱藏（後面沒另外設定的 cue 也會跟著隱藏）' : (visibilityNote(s) ? visibilityNote(s) + ' — 點一下在這個 cue 顯示（後面沒另外設定的 cue 會跟著顯示）' : '這個 cue 裡隱藏中 — 點一下顯示')}
                >
                  <Icon icon={visible ? Eye : EyeOff} size="md" />
                </button>
              </li>
            );
          })}
        </ul>

        {selectedStates.length === 0 ? (
          <div className="attribute-panel__hint">
            <Icon icon={MousePointerClick} size="xl" className="editor-empty__icon" />
            <div>從上方清單勾選一個或多個物件</div>
            <small className="muted">在 3D 畫面點物件 = 單選；右側勾選 = 加選</small>
          </div>
        ) : selectedStates.length === 1 ? (
          <ObjectAttributePanel
            key={selectedStates[0].objectId}
            state={selectedStates[0]}
            stageObject={selectedSo}
            scaleCorrection={scaleCorrection}
            unitsReady={unitsReady}
            onSet={onSet}
            onReset={onReset}
            onUpdateMaterial={onUpdateMaterial}
            onUpdateLed={onUpdateLed}
          />
        ) : (
          <ObjectAttributePanelMulti
            selectedStates={selectedStates}
            scaleCorrection={scaleCorrection}
            unitsReady={unitsReady}
            onSet={onSet}
            onReset={onReset}
            onUpdateMaterial={onUpdateMaterial}
          />
        )}
      </div>
    </div>
  );
}

/** 顯示 / 隱藏的眼睛圖示（線條 SVG，跟著文字顏色） */
const POS_AXES = ['x', 'y', 'z'] as const;
const ROT_AXES = [
  { key: 'pitch', label: 'P', title: 'Pitch（繞 X 軸轉）' },
  { key: 'yaw', label: 'Y', title: 'Yaw（繞 Y 軸轉）' },
  { key: 'roll', label: 'R', title: 'Roll（繞 Z 軸轉）' },
] as const;
const NUMBER_INVALID_MSG = '要填數字，已還原';

/**
 * 一格數字欄位：打字不會馬上存，按 Enter 或點別的地方才存；Esc 取消；↑↓ 微調（Shift 一次 10 倍）。
 * 不是數字 → 不存、還原（onInvalid）；跟原本一樣 → 不存。
 */
function NumberField({
  axisLabel, axisTitle, unit, value, placeholder, step, disabled, disabledTitle, onCommit, onInvalid,
}: {
  axisLabel: string;
  axisTitle?: string;
  unit: string;
  /** 目前的值（已經換好顯示單位）；null = 多選時「不改」，欄位留空 */
  value: number | null;
  placeholder?: string;
  step: number;
  disabled?: boolean;
  disabledTitle?: string;
  onCommit: (n: number) => Promise<unknown> | void;
  onInvalid?: () => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // 剛存出去、畫面還沒拿到新值的這一下，先顯示打的數字（不會閃回舊值）
  const [pending, setPending] = useState<number | null>(null);
  const cancelRef = useRef(false);
  const shown = pending !== null ? formatFieldNumber(pending) : value === null ? '' : formatFieldNumber(value);

  function commit() {
    if (cancelRef.current) { cancelRef.current = false; setDraft(null); return; }
    if (draft === null) return;
    setDraft(null);
    if (value === null && draft.trim() === '') return;  // 多選：沒填 → 不動
    const n = parseNumberInput(draft);
    if (n === null) { onInvalid?.(); return; }
    if (value !== null && sameAsShown(n, value)) return;
    const r = onCommit(n);
    if (r && typeof (r as Promise<unknown>).then === 'function') {
      setPending(n);
      (r as Promise<unknown>).then(() => setPending(null), () => setPending(null));
    }
  }

  return (
    <label className={'num-field' + (disabled ? ' is-disabled' : '')} title={disabled ? disabledTitle : axisTitle}>
      <span className="num-field__axis">{axisLabel}</span>
      <input
        type="text"
        inputMode="decimal"
        value={draft ?? shown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={(axisTitle || axisLabel) + '（' + unit + '）'}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          else if (e.key === 'Escape') { cancelRef.current = true; e.currentTarget.blur(); }
          else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const delta = (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
            setDraft(stepNumberInput(draft ?? shown, value ?? 0, delta));
          }
        }}
      />
      <span className="num-field__unit" aria-hidden="true">{unit}</span>
    </label>
  );
}

/** 多選 panel — 填的欄位才套到全部，沒填就不動；按 Enter 或點別的地方就套用 */
export function ObjectAttributePanelMulti({
  selectedStates, scaleCorrection, unitsReady, onSet, onReset, onUpdateMaterial,
}: {
  selectedStates: CueState[];
  scaleCorrection: number;
  unitsReady: boolean;
  onSet: (objId: string, patch: StatePatchInput) => Promise<void>;
  onReset: (objId: string, askFirst?: boolean) => Promise<void>;
  onUpdateMaterial: (objId: string, patch: MaterialProps) => Promise<void>;
}) {
  const [color, setColor] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // 選到的有一個裝在機關上 → 位置 / 角度整組都不能改（只能用機關的滑桿）；材質顏色、重置照舊
  const { mounts, loading: mountsLoading } = useMechanismMounts();
  const lockNote = stageLockMessage(mountedNames(selectedStates.map(s => s.objectId), mounts), selectedStates.length);
  const poseLocked = lockNote !== null || mountsLoading;
  const poseLockedTitle = lockNote ?? '載入機關資料中…';

  // 切換選中物件時清空輸入（避免上一次的殘留）
  const idsKey = selectedStates.map(s => s.objectId).join(',');
  useEffect(() => { setColor(''); setFieldError(null); }, [idsKey]);

  // 每個物件照它自己現在的值，只換掉打的那一軸（排隊一個一個存）
  async function applyToAll(make: (cur: CueState['effective']) => CueStatePatch) {
    setFieldError(null);
    const results = await Promise.allSettled(selectedStates.map(s => onSet(s.objectId, make)));
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed > 0) setFieldError(`有 ${failed} 個物件沒有存到，請再試一次`);
  }

  async function applyColor() {
    const c = color.trim();
    if (!c) return;
    if (!/^#[0-9a-f]{6}$/i.test(c)) { setFieldError('顏色要填像 #ff8800 這樣，已還原'); setColor(''); return; }
    setFieldError(null);
    setBusy(true);
    try {
      for (const s of selectedStates) await onUpdateMaterial(s.objectId, { color: c });
      setColor('');
    } finally {
      setBusy(false);
    }
  }

  async function resetAll() {
    if (!(await confirmDialog({ title: `把 ${selectedStates.length} 個物件重置回預設位置？`, message: '可以按「復原」改回來。', confirmLabel: '重置' }))) return;
    setBusy(true);
    try {
      for (const s of selectedStates) {
        if (s.override) await onReset(s.objectId, false);
      }
    } finally {
      setBusy(false);
    }
  }

  const invalid = () => setFieldError(NUMBER_INVALID_MSG);

  return (
    <div className="attribute-panel__body is-multi">
      <div className="attribute-panel__title">
        <span style={{ fontWeight: 700 }}>已選 {selectedStates.length} 個物件</span>
      </div>
      <div className="muted small" style={{ marginBottom: 8 }}>
        填的欄位才會套到全部選中的物件，沒填就不動；按 Enter 或點別的地方就套用。
      </div>

      <div className="form-row">
        <label>位置（套到全部）</label>
        <div className="vec3">
          {POS_AXES.map(axis => (
            <NumberField
              key={axis}
              axisLabel={axis.toUpperCase()}
              unit="m"
              step={0.1}
              value={null}
              placeholder="—"
              disabled={!unitsReady || poseLocked}
              disabledTitle={poseLocked ? poseLockedTitle : '載入專案設定中…'}
              onInvalid={invalid}
              onCommit={(meters) => {
                const v = metersToScene(meters, scaleCorrection);
                return applyToAll(cur => ({ position: { ...cur.position, [axis]: v } }));
              }}
            />
          ))}
        </div>
      </div>
      <div className="form-row">
        <label>角度（套到全部）</label>
        <div className="vec3">
          {ROT_AXES.map(axis => (
            <NumberField
              key={axis.key}
              axisLabel={axis.label}
              axisTitle={axis.title}
              unit="°"
              step={1}
              value={null}
              placeholder="—"
              disabled={poseLocked}
              disabledTitle={poseLockedTitle}
              onInvalid={invalid}
              onCommit={(deg) => applyToAll(cur => ({ rotation: { ...cur.rotation, [axis.key]: deg } }))}
            />
          ))}
        </div>
      </div>
      {lockNote && <MechanismLockNote text={lockNote} />}
      <div className="form-row">
        <label>材質顏色（套到全部）</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="color"
            value={/^#[0-9a-f]{6}$/i.test(color) ? color : '#888888'}
            onChange={(e) => setColor(e.target.value)}
            onBlur={applyColor}
            disabled={busy}
          />
          <input
            type="text"
            value={color}
            placeholder="— 不改 —"
            onChange={(e) => setColor(e.target.value)}
            onBlur={applyColor}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            style={{ flex: 1, fontFamily: 'var(--font-mono)' }}
            disabled={busy}
          />
          {/* 按下去不要先讓欄位失焦（不然會先把顏色套上去） */}
          {color && <button type="button" className="link-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => setColor('')}>清除</button>}
        </div>
      </div>
      {fieldError && <div className="field-error small" role="alert">{fieldError}</div>}

      <div className="object-row__actions">
        <button className="btn btn--ghost" onClick={resetAll} disabled={busy}>
          <Icon icon={RotateCcw} /> 全部重置成預設
        </button>
      </div>
    </div>
  );
}

function CueMetaEditor({
  cue, onUpdate,
}: {
  cue: Cue;
  onUpdate: (patch: Partial<Pick<Cue, 'name' | 'crossfadeSeconds' | 'videoTimeSec'>>) => Promise<void>;
}) {
  const savedVideoTime = cue.videoTimeSec != null ? String(cue.videoTimeSec) : '';
  const [name, setName] = useState(cue.name);
  const [crossfade, setCrossfade] = useState(String(cue.crossfadeSeconds));
  const [videoTime, setVideoTime] = useState<string>(savedVideoTime);
  const [error, setError] = useState<string | null>(null);
  // 音樂是這首歌的時間（沒影片、有音樂）：製作可以邊聽邊按，把這個 cue 設在音樂現在的時間
  const { setNow } = useContext(EditorMusicContext);

  // 各欄位分開同步：存好一個欄位時，不要把另一個正在打的欄位洗掉
  useEffect(() => { setName(cue.name); }, [cue.id, cue.name]);
  useEffect(() => { setCrossfade(String(cue.crossfadeSeconds)); }, [cue.id, cue.crossfadeSeconds]);
  useEffect(() => { setVideoTime(cue.videoTimeSec != null ? String(cue.videoTimeSec) : ''); }, [cue.id, cue.videoTimeSec]);
  useEffect(() => { setError(null); }, [cue.id]);

  const parsedVideoTime = parseVideoTimeInput(videoTime);
  const enterToBlur = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.currentTarget.blur(); };

  async function saveName() {
    const v = name.trim().slice(0, 100);
    if (!v) { setName(cue.name); setError('名稱不能空白，已還原'); return; }
    if (v !== name) setName(v);
    if (v === cue.name) return;
    setError(null);
    await onUpdate({ name: v });
  }
  async function saveCrossfade() {
    const n = parseCrossfadeInput(crossfade);
    if (n === null) {
      setCrossfade(String(cue.crossfadeSeconds));
      setError('轉場秒數要填 0 或正的數字，已還原');
      return;
    }
    setCrossfade(String(n));
    if (n === cue.crossfadeSeconds) return;
    setError(null);
    await onUpdate({ crossfadeSeconds: n });
  }
  async function saveVideoTime() {
    const r = parseVideoTimeInput(videoTime);
    if (!r.ok) {
      setVideoTime(savedVideoTime);
      setError('影片時間要填像 15.5 或 1:23 這樣，已還原');
      return;
    }
    if (r.value === cue.videoTimeSec) return;
    setError(null);
    await onUpdate({ videoTimeSec: r.value });
  }

  return (
    <div className="cue-meta-editor">
      {/* Cue 庫專案：名稱是 Cue 庫那個 cue 的，到 Cue 庫分頁改 */}
      {!cue.libraryCueId && (
        <div className="cue-meta-editor__row">
          <label>當前 cue 名稱</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onKeyDown={enterToBlur}
            maxLength={100}
          />
        </div>
      )}
      <div className="cue-meta-editor__row">
        <label>轉場（秒）</label>
        <input
          type="text"
          inputMode="decimal"
          value={crossfade}
          onChange={(e) => setCrossfade(e.target.value)}
          onBlur={saveCrossfade}
          onKeyDown={enterToBlur}
        />
        <small className="muted">換到這個 cue 時舞台滑過去的秒數；0 = 直接切換（不滑）</small>
      </div>
      <div className="cue-meta-editor__row">
        <label>對應影片時間</label>
        <input
          type="text"
          value={videoTime}
          onChange={(e) => setVideoTime(e.target.value)}
          onBlur={saveVideoTime}
          onKeyDown={enterToBlur}
          placeholder="例：15.5 或 1:23"
        />
        {setNow && cue.status === 'master' && (
          <button type="button" className="btn btn--sm btn--ghost cue-meta-editor__setnow" onClick={setNow.run} disabled={setNow.busy} title={MUSIC_TEXT.setNowHint}>
            <Icon icon={Clock} /> {MUSIC_TEXT.setNow}
          </button>
        )}
        <small className="muted">
          {!parsedVideoTime.ok
            ? '格式：秒數（15.5）或 分:秒（1:23）'
            : parsedVideoTime.value != null
              ? `也就是 ${formatVideoTime(parsedVideoTime.value)}（導演那邊影片播到這一秒，會自動切到這個 cue）`
              : '空白 = 沒對應影片時間；填了之後播影片會自動切 cue'}
        </small>
      </div>
      {error && <div className="field-error small" role="alert">{error}</div>}
      <small className="muted">按 Enter 或點別的地方就會存</small>
    </div>
  );
}

export function ObjectAttributePanel({
  state, stageObject, scaleCorrection, unitsReady, onSet, onReset, onUpdateMaterial, onUpdateLed,
}: {
  state: CueState;
  stageObject?: StageObject;
  scaleCorrection: number;
  unitsReady: boolean;
  onSet: (objId: string, patch: StatePatchInput) => Promise<void>;
  onReset: (objId: string, askFirst?: boolean) => Promise<void>;
  onUpdateMaterial: (objId: string, patch: MaterialProps) => Promise<void>;
  onUpdateLed: (objId: string, patch: LedProps) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // 裝在機關上的物件：位置 / 角度不能直接改（只能用機關的滑桿，那邊卡得住上下限）；顯示 / 隱藏、重置、材質照舊
  const { mounts, loading: mountsLoading } = useMechanismMounts();
  const lockNote = stageLockMessage(mountedNames([state.objectId], mounts), 1);
  const poseLocked = lockNote !== null || mountsLoading;
  const poseLockedTitle = lockNote ?? '載入機關資料中…';
  const cat = CATEGORY_INFO[state.category];
  const hasOverride = !!state.override;
  const eff = state.effective;
  const meters = (v: number) => formatFieldNumber(sceneToMeters(v, scaleCorrection));

  // 只換掉打的那一軸，其他軸用排隊輪到時的最新值（連續改 X、Y 不會互相蓋掉）
  function save(make: (cur: CueState['effective']) => CueStatePatch) {
    setFieldError(null);
    return onSet(state.objectId, make).catch(e => setFieldError('沒有存到：' + msg(e)));
  }

  async function reset() {
    setBusy(true);
    try { await onReset(state.objectId); } finally { setBusy(false); }
  }

  const invalid = () => setFieldError(NUMBER_INVALID_MSG);

  return (
    <div className={'attribute-panel__body' + (hasOverride ? ' has-override' : '') + (state.locked ? ' is-locked' : '')}>
      <div className="attribute-panel__title">
        <span className="object-row__cat" title={cat.label}><Icon icon={cat.icon} /></span>
        <span className="object-row__name">{state.displayName}</span>
        {state.locked && <span className="lock-tag" title="已鎖定"><Icon icon={Lock} size="xs" /></span>}
        {hasOverride && <span className="override-dot" title="此 cue 把它放在自訂位置（跟其他 cue 獨立）" />}
      </div>

      {state.locked ? (
        <div className="muted small attribute-panel__locked">
          已鎖定，無法編輯。請到「物件」分頁按鎖頭圖示解鎖。
        </div>
      ) : (
        <>
          <div className="form-row">
            <label>位置</label>
            <div className="vec3">
              {POS_AXES.map(axis => (
                <NumberField
                  key={axis}
                  axisLabel={axis.toUpperCase()}
                  unit="m"
                  step={0.1}
                  value={sceneToMeters(eff.position[axis], scaleCorrection)}
                  disabled={!unitsReady || poseLocked}
                  disabledTitle={poseLocked ? poseLockedTitle : '載入專案設定中…'}
                  onInvalid={invalid}
                  onCommit={(m) => {
                    const v = metersToScene(m, scaleCorrection);
                    return save(cur => ({ position: { ...cur.position, [axis]: v } }));
                  }}
                />
              ))}
            </div>
          </div>
          <div className="form-row">
            <label>角度</label>
            <div className="vec3">
              {ROT_AXES.map(axis => (
                <NumberField
                  key={axis.key}
                  axisLabel={axis.label}
                  axisTitle={axis.title}
                  unit="°"
                  step={1}
                  value={eff.rotation[axis.key]}
                  disabled={poseLocked}
                  disabledTitle={poseLockedTitle}
                  onInvalid={invalid}
                  onCommit={(deg) => save(cur => ({ rotation: { ...cur.rotation, [axis.key]: deg } }))}
                />
              ))}
            </div>
          </div>
          {lockNote
            ? <MechanismLockNote text={lockNote} />
            : <div className="muted small">改完按 Enter 或點別的地方就會存；Esc 取消；↑↓ 微調（按住 Shift 一次 10 倍）</div>}
          {fieldError && <div className="field-error small" role="alert">{fieldError}</div>}
          <MechanismObjectHint objectId={state.objectId} />
          {hasOverride && (
            <div className="object-row__actions">
              <button className="btn btn--ghost" onClick={reset} disabled={busy} title="重置成預設位置">
                <Icon icon={RotateCcw} /> 重置
              </button>
            </div>
          )}
          {hasOverride && (
            <div className="muted small object-row__hint">
              這個 cue 有自己的設定（前面的 cue 不受影響；後面沒另外設定的 cue 會跟著）。原本的預設位置 ({meters(state.default.position.x)}, {meters(state.default.position.y)}, {meters(state.default.position.z)}) m
            </div>
          )}

          {stageObject && (
            <MaterialSection
              obj={stageObject}
              onUpdate={(patch) => onUpdateMaterial(state.objectId, patch)}
            />
          )}
          {stageObject && state.category === 'led_panel' && (
            <LedSection
              obj={stageObject}
              onUpdate={(patch) => onUpdateLed(state.objectId, patch)}
            />
          )}
        </>
      )}
    </div>
  );
}

function MaterialSection({ obj, onUpdate }: { obj: StageObject; onUpdate: (p: MaterialProps) => Promise<void> }) {
  const m = obj.materialProps || {};
  const [color, setColor] = useState(m.color || '#888888');
  const [roughness, setRoughness] = useState(m.roughness ?? 0.6);
  const [metalness, setMetalness] = useState(m.metalness ?? 0.1);
  const [opacity, setOpacity] = useState(m.opacity ?? 1.0);

  useEffect(() => {
    setColor(m.color || '#888888');
    setRoughness(m.roughness ?? 0.6);
    setMetalness(m.metalness ?? 0.1);
    setOpacity(m.opacity ?? 1.0);
  }, [obj.id, m.color, m.roughness, m.metalness, m.opacity]);

  function debouncedSave(patch: MaterialProps) {
    onUpdate(patch);
  }

  return (
    <div className="material-section">
      <div className="section-title"><Icon icon={Palette} /> 材質</div>
      <div className="form-row">
        <label>顏色</label>
        <div className="color-row">
          <input type="color" value={color}
            onChange={(e) => setColor(e.target.value)}
            onBlur={() => debouncedSave({ color })}
          />
          <span className="mono small muted">{color}</span>
        </div>
      </div>
      <div className="form-row">
        <label>粗糙度 Roughness: {roughness.toFixed(2)}</label>
        <input type="range" min={0} max={1} step={0.01}
          value={roughness}
          onChange={(e) => setRoughness(parseFloat(e.target.value))}
          onMouseUp={() => debouncedSave({ roughness })}
          onTouchEnd={() => debouncedSave({ roughness })}
        />
      </div>
      <div className="form-row">
        <label>金屬度 Metalness: {metalness.toFixed(2)}</label>
        <input type="range" min={0} max={1} step={0.01}
          value={metalness}
          onChange={(e) => setMetalness(parseFloat(e.target.value))}
          onMouseUp={() => debouncedSave({ metalness })}
          onTouchEnd={() => debouncedSave({ metalness })}
        />
      </div>
      <div className="form-row">
        <label>不透明度: {opacity.toFixed(2)}</label>
        <input type="range" min={0} max={1} step={0.01}
          value={opacity}
          onChange={(e) => setOpacity(parseFloat(e.target.value))}
          onMouseUp={() => debouncedSave({ opacity })}
          onTouchEnd={() => debouncedSave({ opacity })}
        />
      </div>
    </div>
  );
}

function LedSection({ obj, onUpdate }: { obj: StageObject; onUpdate: (p: LedProps) => Promise<void> }) {
  const led = obj.ledProps || {};
  const [brightness, setBrightness] = useState(led.brightness ?? 1.0);
  const [saturation, setSaturation] = useState(led.saturation ?? 1.0);
  const [hue, setHue] = useState(led.hue ?? 0);
  const [castStrength, setCastStrength] = useState(led.castLightStrength ?? 1.0);
  const [tint, setTint] = useState(led.tint || '#ffffff');
  const [imageUrl, setImageUrl] = useState(led.imageUrl || '');

  useEffect(() => {
    setBrightness(led.brightness ?? 1.0);
    setSaturation(led.saturation ?? 1.0);
    setHue(led.hue ?? 0);
    setCastStrength(led.castLightStrength ?? 1.0);
    setTint(led.tint || '#ffffff');
    setImageUrl(led.imageUrl || '');
  }, [obj.id, led.brightness, led.saturation, led.hue, led.castLightStrength, led.tint, led.imageUrl]);

  function save(patch: LedProps) { onUpdate(patch); }

  return (
    <div className="material-section material-section--led">
      <div className="section-title"><Icon icon={Lightbulb} /> LED 屬性</div>
      <div className="form-row">
        <label>亮度: {brightness.toFixed(2)} ×</label>
        <input type="range" min={0} max={3} step={0.05}
          value={brightness}
          onChange={(e) => setBrightness(parseFloat(e.target.value))}
          onMouseUp={() => save({ brightness })} onTouchEnd={() => save({ brightness })} />
      </div>
      <div className="form-row">
        <label>飽和度: {saturation.toFixed(2)} ×</label>
        <input type="range" min={0} max={2} step={0.05}
          value={saturation}
          onChange={(e) => setSaturation(parseFloat(e.target.value))}
          onMouseUp={() => save({ saturation })} onTouchEnd={() => save({ saturation })} />
      </div>
      <div className="form-row">
        <label>色相偏移: {hue}°</label>
        <input type="range" min={-180} max={180} step={1}
          value={hue}
          onChange={(e) => setHue(parseFloat(e.target.value))}
          onMouseUp={() => save({ hue })} onTouchEnd={() => save({ hue })} />
      </div>
      <div className="form-row">
        <label>色調 (Tint)</label>
        <div className="color-row">
          <input type="color" value={tint}
            onChange={(e) => setTint(e.target.value)}
            onBlur={() => save({ tint })} />
          <span className="mono small muted">{tint}</span>
        </div>
        <div className="color-swatches">
          {[
            { name: '白', val: '#ffffff' },
            { name: '紅', val: '#ff3838' },
            { name: '橘', val: '#ffaa44' },
            { name: '黃', val: '#ffd84a' },
            { name: '綠', val: '#10c78a' },
            { name: '青', val: '#22ccdd' },
            { name: '藍', val: '#5294ff' },
            { name: '紫', val: '#c264ff' },
            { name: '粉', val: '#ff64aa' },
          ].map(c => (
            <button
              key={c.val}
              type="button"
              className={'color-swatch' + (tint.toLowerCase() === c.val ? ' is-active' : '')}
              style={{ background: c.val }}
              onClick={() => { setTint(c.val); save({ tint: c.val }); }}
              title={`${c.name} ${c.val}`}
            />
          ))}
        </div>
      </div>
      <div className="form-row">
        <label>投光強度 (對周圍): {castStrength.toFixed(2)}</label>
        <input type="range" min={0} max={3} step={0.05}
          value={castStrength}
          onChange={(e) => setCastStrength(parseFloat(e.target.value))}
          onMouseUp={() => save({ castLightStrength: castStrength })}
          onTouchEnd={() => save({ castLightStrength: castStrength })} />
      </div>
      <div className="form-row">
        <label>貼圖 URL（測試用，CORS 友善的直連網址）</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={imageUrl}
            placeholder="https://picsum.photos/512  或留空"
            onChange={(e) => setImageUrl(e.target.value)}
            onBlur={() => save({ imageUrl: imageUrl.trim() })}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            style={{ flex: 1 }}
          />
          {imageUrl && (
            <button type="button" className="link-btn" onClick={() => { setImageUrl(''); save({ imageUrl: '' }); }}>清除</button>
          )}
        </div>
        <small className="muted">建議用可以直接打開的圖片網址（例如 imgur 的圖片連結）。3D 畫面切到「現場」模式才看得到。</small>
      </div>
      <div className="muted small">提示：要看到 LED 投光效果，把 3D 畫面上方的模式切到「現場」。</div>
    </div>
  );
}

function ObjectsManager({
  objects, status, onSeed, onAdd, onUpload, onShowVersions, onPickFromLibrary, hasModel,
  onRename, onChangeCategory, onToggleLock, onToggleDefaultHidden, onDelete,
}: {
  objects: StageObject[];
  status: LoadStatus;
  onSeed: () => void;
  onAdd: () => void;
  onUpload: () => void;
  onShowVersions: () => void;
  onPickFromLibrary: () => void;
  hasModel: boolean;
  onRename: (obj: StageObject) => void;
  onChangeCategory: (obj: StageObject, cat: StageObjectCategory) => void;
  onToggleLock: (obj: StageObject) => void;
  /** 預設隱藏開關：沒有 cue 設定過顯示的歌，這個物件預設隱藏 */
  onToggleDefaultHidden: (obj: StageObject) => void;
  onDelete: (obj: StageObject) => void;
}) {
  return (
    <div className="objects-manager">
      <div className="objects-manager__bar">
        <button className="btn btn--primary" onClick={onUpload} title="從 .glb / .gltf 匯入"><Icon icon={Upload} /> 上傳模型</button>
        <button className="btn btn--ghost" onClick={onPickFromLibrary} title="從模型庫選一個模型（不用重複上傳）">
          <Icon icon={Library} /> 模型庫
        </button>
        <button
          className="btn btn--ghost"
          onClick={onShowVersions}
          disabled={!hasModel}
          title={hasModel ? '看以前上傳過的模型，可切回舊版' : '還沒上傳過模型'}
        >
          <Icon icon={HistoryIcon} /> 歷史版本
        </button>
        <button className="btn btn--ghost" onClick={onSeed} title="一鍵加入 10 個常用範例物件"><Icon icon={Sprout} /> 範例</button>
        <button className="btn btn--ghost" onClick={onAdd} title="手動新增單一物件"><Icon icon={Plus} /> 手動</button>
      </div>

      {status === 'loading' ? (
        <div className="editor-empty muted">載入中…</div>
      ) : status === 'error' && objects.length === 0 ? (
        <div className="editor-empty muted" role="alert">物件載入失敗，請重新整理頁面再試一次</div>
      ) : objects.length === 0 ? (
        <div className="editor-empty">
          <Icon icon={Shapes} size="xl" className="editor-empty__icon" />
          <div className="muted">還沒有物件</div>
          <small className="muted">點上方「範例」一鍵加入 10 個常用物件</small>
        </div>
      ) : (
        <ul className="object-mgr-list">
          {objects.map(o => {
            const hiddenText = defaultHiddenToggleText(o);
            return (
            <li key={o.id} className={'object-mgr-row' + (o.locked ? ' is-locked' : '')}>
              <span className="object-row__cat" title={CATEGORY_INFO[o.category].label}><Icon icon={CATEGORY_INFO[o.category].icon} /></span>
              <div className="object-mgr-main">
                <div className="object-mgr-name">
                  {o.displayName}
                  {o.locked && <span className="lock-tag" title="已鎖定"><Icon icon={Lock} size="xs" /></span>}
                </div>
                <div className="muted small object-mgr-mesh">{o.meshName}</div>
                {isDefaultHidden(o) && (
                  <div className="object-mgr-flags">
                    <span className="default-hidden-tag" title={hiddenText.title} data-testid="default-hidden-badge"><Icon icon={EyeOff} size="xs" /> {DEFAULT_HIDDEN_LABEL}</span>
                  </div>
                )}
              </div>
              <select
                className="object-mgr-cat"
                value={o.category}
                onChange={(e) => onChangeCategory(o, e.target.value as StageObjectCategory)}
                title="分類"
                disabled={o.locked}
              >
                {(Object.keys(CATEGORY_INFO) as StageObjectCategory[]).map(c => (
                  <option key={c} value={c}>{CATEGORY_INFO[c].label}</option>
                ))}
              </select>
              <div className="object-mgr-actions">
                <button
                  onClick={() => onToggleDefaultHidden(o)}
                  title={o.locked ? '已鎖定，不能改' : hiddenText.title}
                  aria-label={hiddenText.label}
                  aria-pressed={isDefaultHidden(o)}
                  className={isDefaultHidden(o) ? 'is-default-hidden-btn' : ''}
                  disabled={o.locked}
                  data-testid="default-hidden-toggle"
                >
                  <Icon icon={isDefaultHidden(o) ? EyeOff : Eye} />
                </button>
                <button
                  onClick={() => onToggleLock(o)}
                  title={o.locked ? '解鎖（可編輯）' : '鎖定（3D 不可選、屬性不可改）'}
                  aria-label={o.locked ? '解鎖' : '鎖定'}
                  className={o.locked ? 'is-locked-btn' : ''}
                >
                  <Icon icon={o.locked ? Lock : LockOpen} />
                </button>
                <button onClick={() => onRename(o)} title="改名" aria-label="改名" disabled={o.locked}><Icon icon={Pencil} /></button>
                <button onClick={() => onDelete(o)} title="刪除" aria-label="刪除"><Icon icon={Trash2} /></button>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// 後台回的錯誤是英文（例：Not found）或沒有內容時，換成白話（lib/songReview.ts friendlyError）
function msg(e: unknown) { return friendlyError(e); }
function sameMembers(a: readonly ProjectMember[], b: readonly ProjectMember[]): boolean {
  return a.length === b.length && a.every((m, i) => m.id === b[i].id && m.role === b[i].role && m.name === b[i].name);
}
function formatVideoTime(s: number): string {
  if (!isFinite(s) || s < 0) return '00:00';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1);
  return `${String(m).padStart(2, '0')}:${sec.padStart(4, '0')}`;
}

/**
 * 沒選到的歌，第二行最後面的兩個小圖示：有沒有影片、有沒有音樂（滑上去有文字；沒有的淡一點）。
 * 影片正在交 / 沒交成功時換成警告色，文字寫進度。展開（選到）的歌看完整的影片和音樂那兩行。
 */
function SongRowIcons({ song, jobs }: { song: Song; jobs: VideoJob[] | undefined }) {
  const active = (jobs ?? []).filter(isJobActive);
  const failed = (jobs ?? []).find(j => j.phase === 'failed');
  const videoText = active.length > 0 ? `影片：${jobStatusText(active[0])}`
    : failed ? '影片沒交成功（選這首歌再看影片版本）'
    : song.latestVideoVersion ? `有影片（最新 V${song.latestVideoVersion}）` : '還沒有影片';
  const videoState = active.length > 0 ? ' is-busy' : failed ? ' is-error' : song.latestVideoVersion ? ' is-on' : '';
  const hasMusic = song.hasMusic === true;
  return (
    <span className="song-item__icons">
      <span className={'song-item__ico' + videoState} title={videoText} role="img" aria-label={videoText}><Icon icon={Film} size="sm" /></span>
      {song.hasMusic !== undefined && (
        <span className={'song-item__ico' + (hasMusic ? ' is-on' : '')} title={hasMusic ? '有音樂' : '還沒有音樂'} role="img" aria-label={hasMusic ? '有音樂' : '還沒有音樂'}><Icon icon={Music} size="sm" /></span>
      )}
    </span>
  );
}

/**
 * 歌單上每首歌的影片（第三行）：最新是哪一版、正在交的進度、＋加影片。
 * 「影片 V3」/ 交影片的進度是按鈕 → 打開「影片版本」視窗（每一版、刪除、到導演頁播放）；還沒有影片 → 只寫「還沒有影片」
 */
function SongVideoMeta({ song, jobs, onAdd, onOpenVersions }: {
  song: Song;
  jobs: VideoJob[] | undefined;
  onAdd: () => void;
  onOpenVersions: () => void;
}) {
  const active = (jobs ?? []).filter(isJobActive);
  const failed = (jobs ?? []).find(j => j.phase === 'failed');
  const label = songTitle(song);
  return (
    <div className="song-item__video" onClick={(e) => e.stopPropagation()}>
      {active.length > 0 ? (
        <button
          type="button"
          className="song-item__video-job"
          onClick={onOpenVersions}
          aria-haspopup="dialog"
          title={[...active.map(j => `${j.name}：${jobStatusText(j)}`), '點一下看這首歌的影片版本'].join('\n')}
        >{jobStatusText(active[0])}</button>
      ) : failed ? (
        <button
          type="button"
          className="song-item__video-job is-error"
          onClick={onOpenVersions}
          aria-haspopup="dialog"
          title={[failed.error, '點一下看這首歌的影片版本（可以重試或取消）'].filter(Boolean).join('\n')}
        >影片沒交成功</button>
      ) : song.latestVideoVersion ? (
        <button
          type="button"
          className="song-item__video-label"
          onClick={onOpenVersions}
          aria-haspopup="dialog"
          title={`看「${label}」的所有影片版本（誰交的、說明，可以刪掉不要的版本）`}
        >影片 V{song.latestVideoVersion}</button>
      ) : (
        <span className="song-item__video-none">還沒有影片</span>
      )}
      <button
        type="button"
        className="song-item__video-add"
        onClick={onAdd}
        title={`幫「${label}」加影片（上傳檔案或貼連結，好了變成下一個版本）`}
      >
        <Icon icon={Plus} size="xs" /> 加影片
      </button>
    </div>
  );
}

function StatusBadge({ value, onChange, blocked }: { value: SongStatus; onChange: (next: SongStatus) => void; blocked?: (to: SongStatus) => string | null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const info = STATUS_INFO[value];
  return (
    <div className="status-badge-wrap" ref={ref}>
      <button
        className={`status-badge status-badge--${info.mod}`}
        onClick={() => setOpen(o => !o)}
        title="點擊切換狀態"
      >
        <span className="status-dot" />
        <span className="status-label">{info.label}</span>
      </button>
      {open && (
        <div className="status-menu" role="menu">
          {STATUS_ORDER.map(s => (
            <button
              key={s}
              role="menuitem"
              className={`status-menu__item${s === value ? ' is-current' : ''}`}
              disabled={!!blocked?.(s)}
              title={blocked?.(s) ?? undefined}
              onClick={() => { setOpen(false); if (s !== value) onChange(s); }}
            >
              <span className={`status-dot status-dot--${STATUS_INFO[s].mod}`} />
              <span>{STATUS_INFO[s].label}</span>
              {s === value && <span className="status-menu__check"><Icon icon={Check} /></span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusFilterChips({
  counts, total, value, onChange,
}: {
  counts: Record<SongStatus, number>;
  total: number;
  value: StatusFilter;
  onChange: (next: StatusFilter) => void;
}) {
  return (
    <div className="status-filter-chips" role="tablist" aria-label="依狀態篩選歌曲">
      <button
        role="tab"
        aria-selected={value === 'all'}
        className={'status-chip' + (value === 'all' ? ' is-active' : '')}
        onClick={() => onChange('all')}
      >
        全部 <span className="status-chip__count">{total}</span>
      </button>
      {STATUS_ORDER.map(s => (
        <button
          key={s}
          role="tab"
          aria-selected={value === s}
          className={`status-chip status-chip--${STATUS_INFO[s].mod}` + (value === s ? ' is-active' : '')}
          onClick={() => onChange(s)}
          title={STATUS_INFO[s].label}
        >
          <span className={`status-dot status-dot--${STATUS_INFO[s].mod}`} />
          <span className="status-chip__label">{STATUS_INFO[s].label}</span>
          <span className="status-chip__count">{counts[s]}</span>
        </button>
      ))}
    </div>
  );
}
